import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Customer, FrameworkContract, InvoiceConfig, Quote, QuoteConfig, QuoteItem, QuoteRichBlock, QuoteSection, QuoteTotalsMode, ServiceItem, IntervalKind, IntervalPhase, TextTemplate, TextTemplateCategory, TenderDocumentKind, UserProfile, DEFAULT_QUOTE_SECTIONS } from '../../types';
import { supabase } from '../../services/supabaseClient';
import { updateQuote, rejectQuote, fetchCustomerPrices, acceptQuote, acceptQuoteAsFrameworkContract, linkQuoteToFrameworkContract, acceptFailureState, createQuoteTemplate, fetchTextTemplates, fetchTenderDocuments, QuoteConflictError, QuoteNotSavedError } from '../../services/crmService';
import { AcceptFrameworkValues, frameworkContractLabel, frameworkContractToQuoteItems, isFrameworkAcceptedQuote, isUnitPriceQuote } from '../../services/frameworkContractLogic';
import { backupRestoreMode, flushUntilSettled, prepareQuoteSave, quoteAfterAcceptFailure, rebaseInFlightEdits, rebaseSnapshotOnto, restoreQuoteDraftBackup, resultBelongsToEditor, settleSaveBase } from '../../services/quoteEditorSave';
import { BackupSlot, QuoteDraftBackup, backupBaseIsStale, buildQuoteDraftBackup, clearQuoteDraftBackup, clearQuoteDraftBackupIfCovered, readNewestQuoteDraftBackup, writeQuoteDraftBackup } from '../../services/quoteDraftBackup';
import { patchProfileSettings } from '../../services/cloudService';
import { getQuoteEditorBusy, setQuoteEditorBusy } from '../../services/editorBusy';
import { TextTemplatePicker } from './TextTemplatePicker';
import { QuoteRichTextEditor } from './QuoteRichTextEditor';
import { effectiveQuoteRich, quoteRichFromPlain, quoteTextFromRich } from '../../services/quoteRichText';
import { quoteTotalsMode, QUOTE_TOTALS_MODE_OPTIONS, applyCustomerPricing, calculateQuoteTotals, calculateQuoteTotalsDetailed, computeFollowUpDate, computePosNumbers, moveQuoteItem, canMoveQuoteItem, sortQuoteItemsByLvPosition, canSortQuoteItemsByLvPosition, canTransition, isPortalSubmission, isQuoteExpired, isQuoteItemExcluded, lineTotal, nextDayIso, resolveUnitPrice, INTERVAL_LABELS, QUOTE_EXCLUDED_LABEL, QUOTE_UNITS, phaseInspectionCount, totalInspectionCount, intervalSummary, syncPhaseQuantities } from '../../services/quoteLogic';
import {
  countExcludedQuoteItems,
  excludedQuoteItemKeys,
  pruneQuoteItemSelection,
  quoteSelectionStats,
  sectionSelectionState,
  setQuoteItemsExcluded,
  splitExcludedDuplicates,
  toggleQuoteItemSelection,
  toggleQuoteSectionSelection,
} from '../../services/quoteItemSelection';
import { generateQuotePDF, applyOriginalLv, lvOriginalMode } from '../../services/pdfService';
import { LvPricingError, LvUnmatchedItem, normalizeLvPos } from '../../services/pdf/lvPriceMatch';
import { buildCompanyForPdf } from '../../services/pdf/companyForPdf';
import { rechtFehlerOder, rechtFehltHinweis } from '../../services/rechte/rechtFehler';
import { projektAnlegenVerweigert } from '../../services/rechte/ansichtRechte';
import { erlaubteProjektTypen } from '../../services/projektAnlageRechte';
import { useRechte } from '../../hooks/useRechte';
import { annahmeGesperrtDurch, kundenBearbeitungImAngebot, vertriebRechte } from './vertriebRechte';
import { Button } from '../Button';
import { QuoteSendModal } from './QuoteSendModal';
import { TenderImportModal, TenderLvRow, TenderRequirements } from './TenderImportModal';
import type { TenderLvApplyMeta } from './TenderImportModal';
import { quoteItemLvKey, tenderLvRowKey } from '../../services/tenderLvImport';
import { AcceptQuoteDialog } from './AcceptQuoteDialog';
import { QuoteOrderPanel } from './QuoteOrderPanel';
import { QuoteTrancheDialog, TrancheDialogResult } from './QuoteTrancheDialog';
import { applyTrancheToProject, recordQuoteTranche } from '../../services/quoteOrderService';
import { isQuoteOrdered } from '../../services/quoteOrderTranches';
import { AcceptQuoteValues } from '../../services/quoteAcceptDefaults';
import { acceptQuoteAsSupplement, createSupplementQuote } from '../../services/quoteSupplementService';
import { isSupplementQuote, supplementLabel } from '../../services/quoteSupplementLogic';
import type { SupplementFormValues } from '../../services/supplementAcceptForm';
import { ProjectPositionPicker } from './ProjectPositionPicker';
import { verifyAddress } from './verifyAddress';
import { CustomerQuickEditModal } from './CustomerQuickEditModal';
import { documentCustomerState, resolveDocumentCustomer } from '../../services/customerQuickEdit';
import {
    applyCustomerOverride,
    canScopeToDocument,
    customerOverrideApplies,
    customerOverrideSummary,
    hasCustomerOverride,
} from '../../services/customerOverride';
import { istNativeApp } from '../../services/nativePlatform';
import { ausgabeFehlerMelden, dateiAusgeben } from '../../services/dateiAusgabe';
import { blobFuerUrl } from '../../services/nativeShims';
import { NativePdfSeiten } from '../NativePdfSeiten';
import { BestandteileEditor } from '../bestandteile/BestandteileEditor';
import { StundenKontingentFeld } from '../bestandteile/StundenKontingentFeld';
import { bestandteilePatch, positionsSperreFuerBestandteile, pruefPosition } from '../bestandteile/bestandteileEditorLogic';
import { formatStunden, hatBestandteile, normalisiereBestandteile } from '../../services/bestandteile/bestandteilLogic';
import { gueltigesKontingent, kontingentFuerKopie } from '../../services/bestandteile/kontingentKopie';
import { isAdminRole } from '../../services/projectLockLogic';
import {
  QUOTE_STATUS_META, quoteCompleteness, quoteReadinessWarnings, quoteSetupSummary, quoteValidityInfo,
} from '../../services/quoteEditorHeader';
import {
  ArrowLeft, Save, Loader2, Check, Plus, Trash2, Lock,
  Mail, ChevronDown, Search, Package, XCircle, CheckCircle2, ListPlus, FolderOpen,
  MapPin, ArrowUp, ArrowDown, AlertTriangle, Bookmark, Layers, Building2, Eye, X, Link2,
  Maximize2, Upload, QrCode, Pencil, Sparkles, Strikethrough, RotateCcw, MoreHorizontal,
  ChevronUp, ArrowDownAZ, ExternalLink, PictureInPicture2, RefreshCw, ListChecks, FilePlus,
} from 'lucide-react';
import QRCode from 'qrcode';
import type { ListProject } from '../../services/listProject';

/**
 * Was das Angebot beim Sprung ins Projekt reicht: die gerade angelegte
 * Projektzeile oder nur `{ id }` (Angebot mit Projektbezug). Zugesagt ist
 * allein die Kennung; App.handleOpenProject laedt den Rest selbst nach.
 */
export type OpenProjectTarget = Pick<ListProject, 'id'> & Partial<ListProject>;

interface Props {
  quote: Quote;
  customer: Customer;
  onChange: (q: Quote) => void;
  /**
   * Der Kundenstamm wurde aus dem Angebot heraus geaendert.
   *
   * Eigener Kanal neben onChange: das Angebot selbst aendert sich dabei nicht,
   * und ueber onChange gemeldet setzte die Liste `editing` neu — der Editor
   * zoege daraufhin Metadaten aus der Prop nach, obwohl gar nichts am Angebot
   * passiert ist.
   */
  onCustomerChange?: (customer: Customer) => void;
  onBack: () => void;
  // Firmen-/Angebots-Voreinstellungen fuer PDF + Outlook-Versand (Phase 4).
  company?: { name?: string; address?: string; logo?: string; footerText?: string; senderLine?: string };
  quoteConfig?: QuoteConfig;
  // Rechnungs-Stammdaten fuer die Spalten-Fusszeile — kommt von App.tsx
  // (resolveInvoiceConfig), NICHT aus settings: das ist ein handgebautes
  // Teilobjekt ohne invoiceConfig.
  invoiceConfig?: InvoiceConfig;
  // Phase 5 (Auftragsannahme): Koordinator-Profil + App-Settings fuer den
  // Projekt-Report, und Sprung ins angelegte Projekt.
  userProfile?: any;
  settings?: any;
  onOpenProject?: (project: OpenProjectTarget) => void;
  /**
   * Loeschen anstossen. Der Editor fragt nicht selbst nach — Rueckfrage und
   * Aufruf liegen in der Liste, damit es genau EINEN Loeschweg gibt. Fehlt die
   * Prop, steht der Eintrag nicht im Menue.
   */
  onDelete?: () => void;
  /**
   * Aus dem Editor wurde ein NEUES Angebot angelegt (Nachtrag erstellen).
   * Die Liste nimmt es auf und oeffnet es. Fehlt die Prop, steht die Aktion
   * nicht zur Verfuegung.
   */
  onCreatedQuote?: (q: Quote) => void;
}

// min-w-0: Ein <input> traegt als Grid-/Flex-Kind eine EIGENE Mindestbreite
// (Browser-Intrinsik, bei type=date rund 150 px). Ohne diese Klasse schrumpft
// es nicht in seine Spur, sondern ragt darueber hinaus — neben der
// angedockten Vorschau lief der Ueberhang unter das rechte Panel.
const inputClass =
  'w-full min-w-0 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed';
const labelClass = 'text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1';

const eur = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const fmtEur = (n: number) => eur.format(Number.isFinite(n) ? n : 0);

// ISO/Date-String -> YYYY-MM-DD fuer <input type="date">
const toDateInput = (v?: string): string => (v ? v.slice(0, 10) : '');

// Katalog-Row -> ServiceItem (Muster aus ProjectSetupWizard.refreshCatalog)
const mapCatalogRow = (d: any): ServiceItem => {
  // Bestandteil-Vorlage einer Paketposition (service_catalog.bestandteile,
  // SQL Nr. 165) — applyCustomerPricing kopiert sie ins Angebot. Fehlt die
  // Spalte, bleibt das Feld weg. Ebenso das Stundenkontingent
  // (service_catalog.stunden_kontingent, Nr. 171) — nur mit der Vorlage (S1).
  const vorlage = normalisiereBestandteile(d.bestandteile);
  return {
    id: d.id,
    lvPosition: d.lv_position,
    title: d.title,
    unit: d.unit,
    unitPrice: d.unit_price,
    category: d.category,
    isDefault: d.is_default,
    assignment: d.assignment || 'none',
    quantityContracted: 1,
    quantityUsed: 0,
    status: 'open',
    ...(vorlage ? { bestandteile: vorlage } : {}),
    ...kontingentFuerKopie({ bestandteile: vorlage }, d.stunden_kontingent),
  };
};

/**
 * Warum der letzte Speicherversuch scheiterte. `conflict` und `missing` sperren
 * den Autosave, bis der Nutzer im Banner entschieden hat; `error` (Netz,
 * Zeitueberschreitung) wird beim naechsten Anlass erneut versucht.
 */
type SaveProblem =
  | { kind: 'error'; message: string; backedUp: boolean }
  | { kind: 'conflict'; server: Quote }
  | { kind: 'missing'; backedUp: boolean };

/** Wie lange zwischen zwei lokalen Sicherungen mindestens vergeht (ms). */
const BACKUP_INTERVAL_MS = 1000;

/** Android-App: so viele Seiten rendert die Seitenvorschau (pdf.js); das ganze Angebot über „Als PDF öffnen". */
const NATIV_VORSCHAU_SEITEN = 3;

const isLockedQuote = (q: Quote): boolean =>
  isQuoteExpired(q, new Date().toISOString().slice(0, 10)) || q.status !== 'draft';

/**
 * Sitzung des Editors fuer die lokale Sicherung. `seq` zaehlt die
 * Sicherungen; ein erfolgreiches Speichern entfernt nur, was es abgedeckt hat
 * (quoteDraftBackup.isBackupCoveredBySave). `suspended`: solange eine aeltere
 * Sicherung zur Wiederherstellung angeboten wird, wird sie nicht
 * ueberschrieben — die laufende Sitzung sichert dann in den Platz 'pending'.
 */
interface BackupSession { sessionId: string; seq: number; suspended: boolean }

/**
 * Sicherung schreiben, solange der Entwurf vom Serverstand abweicht.
 * null = nichts geschrieben (nichts offen ODER Speicher nicht verfuegbar).
 */
const persistBackup = (draft: Quote, base: Quote, session: BackupSession, slot?: BackupSlot): QuoteDraftBackup | null => {
  if (draft === base) return null;
  session.seq += 1;
  const backup = buildQuoteDraftBackup(draft, base, new Date(), { sessionId: session.sessionId, seq: session.seq });
  return writeQuoteDraftBackup(backup, undefined, slot ?? (session.suspended ? 'pending' : 'main')) ? backup : null;
};

/**
 * Beim Oeffnen: gibt es eine lokale Sicherung, die gegenueber dem jetzigen
 * Serverstand noch etwas aendern wuerde? Nur dann anbieten. Weggeworfen wird
 * eine Sicherung hier NUR, wenn sie nichts mehr bewirkt (ihre Aenderungen
 * stehen schon auf dem Server) — nie, weil der Server neuer ist: das beweist
 * nicht, dass die lokalen Aenderungen gespeichert wurden.
 */
const pendingBackupFor = (q: Quote): QuoteDraftBackup | null => {
  // Die juengere der beiden Sicherungen (main / pending).
  const backup = readNewestQuoteDraftBackup(q.id);
  if (!backup) return null;
  // VOLLER Vergleich, unabhaengig von der Sperre: eine Preisaenderung in
  // einem inzwischen gesperrten Angebot ist nicht erledigt, nur blockiert.
  if (backupRestoreMode(q, backup, false) !== 'none') return backup;
  clearQuoteDraftBackup(q.id);
  return null;
};

/**
 * Ergebnis eines Speichervorgangs; `base` = neuer Serverstand bei Erfolg,
 * `sent` = der Entwurf, den dieser Vorgang abgeschickt hat.
 */
interface SaveResult { ok: boolean; base: Quote | null; sent: Quote }

const fmtBackupTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export const QuoteEditor: React.FC<Props> = ({ quote, customer, onChange, onCustomerChange, onBack, company, quoteConfig, invoiceConfig, userProfile, settings, onOpenProject, onDelete, onCreatedQuote }) => {
  const [draft, setDraft] = useState<Quote>(quote);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // Draft-Stand, dessen Autosave fehlschlug — verhindert Wiederholungsschleifen.
  const autosaveFailedForRef = useRef<Quote | null>(null);
  /**
   * Speicher-Kern. `draftRef` = was der Nutzer zuletzt sah, `baseRef` = was
   * zuletzt nachweislich auf dem Server stand. Beide als Refs, weil Zurueck,
   * Unmount und pagehide den AKTUELLEN Stand brauchen, nicht den eines alten
   * Renders.
   */
  const draftRef = useRef<Quote>(quote);
  const baseRef = useRef<Quote>(quote);
  const inFlightRef = useRef<Promise<SaveResult> | null>(null);
  // Eingefrorene Speichervorgaenge (Wechsel/Unmount), die noch warten oder
  // laufen — solange sperrt der Editor den Deploy-Reload weiter.
  const snapshotPendingRef = useRef(0);
  const mountedRef = useRef(true);
  const problemRef = useRef<SaveProblem | null>(null);
  const lastBackupAtRef = useRef(0);
  const [backupSessionId] = useState(() => crypto.randomUUID());
  const [initialOffer] = useState<QuoteDraftBackup | null>(() => pendingBackupFor(quote));
  const backupRef = useRef<BackupSession>({ sessionId: backupSessionId, seq: 0, suspended: initialOffer !== null });
  const [saveProblem, setSaveProblemState] = useState<SaveProblem | null>(null);
  // Aktion (Zurueck, Senden, Annehmen, Ablehnen) abgebrochen, weil der letzte
  // Stand nicht gespeichert werden konnte.
  const [guardMessage, setGuardMessage] = useState('');
  const [leaving, setLeaving] = useState(false);
  // Statuswechsel laeuft (Speichern + Statusaktion): Maske gesperrt, damit
  // keine Eingabe zwischen letztem Speichern und Statuswechsel verloren geht.
  const [statusBusy, setStatusBusy] = useState(false);
  // Das Wiederherstellungsangebot liegt NUR im Speicher (localStorage schrieb
  // nicht) — geht mit dem Schliessen der Seite verloren.
  const [offerMemoryOnly, setOfferMemoryOnly] = useState(false);
  // 'missing': lokale Sicherung beim Verlassen gescheitert — zweiter Klick
  // im Banner verlaesst trotzdem.
  const [leaveUnsaved, setLeaveUnsaved] = useState(false);
  const [restoreOffer, setRestoreOffer] = useState<QuoteDraftBackup | null>(initialOffer);
  const [rejecting, setRejecting] = useState(false);
  // Ueberlaufmenue der Kopfleiste. Die Leiste trug bis zu sechs gleich starke
  // Textknoepfe und brach in der Zwei-Spalten-Ansicht um; alles, was nicht der
  // naechste Schritt ist, liegt jetzt hier.
  const [moreOpen, setMoreOpen] = useState(false);
  /**
   * Aufgeklappte Positionszeile (hoechstens eine).
   *
   * Eine Zeile trug 15 Bedienelemente; bei 20 Positionen sind das 300 auf
   * einem Bildschirm, und die sechs haeufig gebrauchten (Bezeichnung, Einheit,
   * Menge, Preis, Rabatt, Gesamt) gingen zwischen NEP-Haken, Bemerkung,
   * Sortierpfeilen und Abschnittswahl unter. Die zweite Haelfte liegt jetzt
   * hinter dem Umschalter — sie ist selten und nie zeitkritisch.
   */
  const [openItemId, setOpenItemId] = useState<string | null>(null);
  // PAKETPOSITION: Position, deren „Bestandteile (intern)" im Detailbereich
  // aufgeklappt sind. Festlegen darf nur ein Admin (E6).
  const [bestandteileItemId, setBestandteileItemId] = useState<string | null>(null);
  const darfBestandteileFestlegen = isAdminRole(userProfile?.role);
  /**
   * Kopf- und Projektdaten eingeklappt.
   *
   * Beide Karten zusammen tragen achtzehn Felder, die nach dem Anlegen kaum
   * noch angefasst werden — sie standen aber ueber den Positionen und schoben
   * die eigentliche Arbeit unter den Falz. Bei einem noch leeren Angebot
   * bleiben sie offen: dort faengt die Arbeit tatsaechlich hier an.
   */
  const [setupOpen, setSetupOpen] = useState(() => (quote.items || []).length === 0);
  const [showSend, setShowSend] = useState(false);
  // Kundenstamm-Schnellbearbeitung. Rein lokal — Oeffnen und Schliessen fassen
  // den Angebots-Draft nicht an.
  const [customerEditOpen, setCustomerEditOpen] = useState(false);
  // Offener Ausschreibungs-Import ('lv' | 'requirements' | null).
  const [tenderKind, setTenderKind] = useState<TenderDocumentKind | null>(null);
  // LV-Zeilen aus der Ausschreibung, deren Vorgaben (LV-Nummer, Menge) der
  // Nutzer nach Rueckfrage zum Bearbeiten entsperrt hat (transient, nicht
  // persistiert — nach Reload gilt die Auftraggeber-Vorgabe wieder).
  const [lvRowUnlocked, setLvRowUnlocked] = useState<Set<string>>(new Set());
  // EINE Rueckfrage fuer beide Felder: die LV-Nummer ist der Schluessel fuer
  // den Preisstempel im Original-LV und den Duplikat-Check beim Re-Import,
  // die Menge wird dort gegen das Original geprueft.
  const unlockLvRow = (itemId: string) => {
    if (window.confirm('LV-Nummer und Menge stammen aus der Ausschreibung (Auftraggeber-Vorgabe). Wirklich zum Bearbeiten entsperren?\n\nHinweis: Über die LV-Nummer werden die Preise ins Original-LV gestempelt — eine geänderte Nummer findet dort keine Zeile mehr.')) {
      setLvRowUnlocked((prev) => new Set(prev).add(itemId));
    }
  };
  // Multi-select of the position table (transient): drives the bulk strike
  // action only, it is never persisted.
  const [selectedItemIds, setSelectedItemIds] = useState<ReadonlySet<string>>(new Set<string>());
  // Liegt zu diesem Angebot ein hochgeladenes Original-LV (tender_documents,
  // kind 'lv')? Steuert nur, ob die "Original anhaengen"-Option waehlbar ist.
  // null = noch nicht bekannt bzw. Pruefung fehlgeschlagen -> Option freigeben,
  // statt den Nutzer wegen einer fehlgeschlagenen Nebenabfrage auszusperren.
  const [hasLvDoc, setHasLvDoc] = useState<boolean | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [acceptError, setAcceptError] = useState('');
  // Ergebnis der Auftragsannahme in dieser Sitzung: haelt projectRow, damit
  // "Projekt oeffnen" direkt nach der Annahme mit der vollen Zeile arbeitet.
  // Annahme als Rahmenvertrag: kein Projekt, nur Auftrags- und Vertragsnummer.
  // Annahme als Nachtrag: bestehendes Projekt; `applied` = steht im Projekt-LV.
  const [acceptResult, setAcceptResult] = useState<
    | { kind: 'project'; orderNumber: string; projectNumber: string; projectRow: any }
    | { kind: 'framework'; orderNumber: string; contractNumber: string }
    | { kind: 'supplement'; orderNumber: string; projectId: string; applied: boolean }
    | null
  >(null);
  // Nachtragsangebot: Dialog „Projektposition übernehmen" (Zielabschnitt) und
  // „Nachtrag erstellen" aus einem beauftragten Angebot.
  const [positionPicker, setPositionPicker] = useState<{ sectionId?: string } | null>(null);
  const [creatingSupplement, setCreatingSupplement] = useState(false);
  // Teilbeauftragung: Dialog fuer Rest beauftragen / Rest entfaellt / Erneut
  // eintragen, plus Ergebnis- bzw. Fehlerhinweis.
  const [trancheDialog, setTrancheDialog] = useState<{ mode: 'order' | 'decline' | 'retry'; trancheId?: string } | null>(null);
  const [trancheBusy, setTrancheBusy] = useState(false);
  const [trancheError, setTrancheError] = useState('');
  const [trancheNotice, setTrancheNotice] = useState<{ tone: 'ok' | 'warn'; text: string } | null>(null);

  // Rahmenvertrag: hat der Kunde hinterlegte Rahmenvertraege, wird ausgewaehlt
  // statt frei getippt. 'Manuell eingeben…' blendet das alte Freitextfeld ein.
  const frameworkContracts = customer.frameworkContracts || [];
  const frameworkLabels = frameworkContracts.map(frameworkContractLabel);
  const [frameworkManual, setFrameworkManual] = useState(false);

  const [catalog, setCatalog] = useState<ServiceItem[]>([]);
  const [prices, setPrices] = useState<import('../../types').CustomerPrice[]>([]);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalogQuery, setCatalogQuery] = useState('');
  // Zielsektion des Katalog-Dropdowns (grouped rendering: pro Sektion einblendbar).
  const [catalogTargetSection, setCatalogTargetSection] = useState<string | null>(null);
  const catalogWrapRef = useRef<HTMLDivElement>(null);

  // Baustellen-Adress-Verifikation (Nominatim, non-blocking).
  const [siteChecking, setSiteChecking] = useState(false);
  const [siteCheck, setSiteCheck] = useState<{ ok: boolean; text: string } | null>(null);

  // Vorlage speichern.
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Textbausteine fuer die vier Textfelder (einmalig geladen, clientseitig
  // nach Kategorie gruppiert).
  const [textTemplates, setTextTemplates] = useState<TextTemplate[]>([]);

  // Live-PDF-Vorschau (dauerhaft sichtbar, kein Umschalter).
  const [previewUri, setPreviewUri] = useState<string | null>(null);
  // Aktuelle Objekt-URL der Vorschau — zum Freigeben beim Ersetzen/Unmount.
  const previewUrlRef = useRef<string | null>(null);
  useEffect(
    () => () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    },
    [],
  );
  const [previewError, setPreviewError] = useState<string | null>(null);
  /**
   * Vorschau im Vollbild. Die Spaltenvorschau ist zum Mitlesen beim Tippen
   * gedacht; ein mehrseitiges Angebot am Stueck durchzusehen braucht die ganze
   * Flaeche.
   */
  const [previewFullscreen, setPreviewFullscreen] = useState(false);
  /**
   * Vorschau in einem EIGENEN Fenster (zweiter Monitor). Solange sie abgedockt
   * ist, faellt die zweite Spalte weg und die Maske bekommt die ganze Breite —
   * das Positionsraster braucht rund 780 px, neben der Vorschau blieb davon in
   * einem 1600-px-Fenster zu wenig uebrig.
   *
   * Das Popup ist same-origin (about:blank vom Opener), deshalb darf es die
   * Blob-URL der Vorschau anzeigen und die Maske darf sein iframe umschalten.
   * Neu erzeugt wird dafuer nichts: es ist dasselbe previewUri wie in Spalte
   * und Vollbild.
   */
  const [previewDetached, setPreviewDetached] = useState(false);
  const previewWindowRef = useRef<Window | null>(null);
  // Popup-Blocker: ohne Rueckmeldung sieht es aus, als tue der Knopf nichts.
  const [detachBlocked, setDetachBlocked] = useState(false);

  // ─── Android-App: Seitenvorschau auf Anforderung ──────────────────────────
  // Die WebView hat keinen PDF-Betrachter; NativePdfSeiten rendert die Seiten
  // per pdf.js. Das kostet auf dem Tablet spuerbar, die Vorschau oben entsteht
  // aber bei jeder Tipp-Pause neu. Die App zeigt deshalb einen FESTEN Stand:
  // den ersten fertigen, danach nur auf „Vorschau aktualisieren" — hoechstens
  // NATIV_VORSCHAU_SEITEN Seiten, das ganze Angebot ueber „Als PDF öffnen".
  //
  // Eigene Objekt-URL statt previewUri: der Vorschau-Effekt gibt die alte URL
  // 10 s nach dem Ersetzen frei. Haengt die Seitenvorschau spaeter neu ein
  // (Vollbild, nach einer Fehlermeldung), liefe sie sonst ins Leere.
  // Im Browser bleibt das alles ungenutzt (nativStand bleibt null).
  const nativ = istNativeApp();
  const [nativStand, setNativStand] = useState<{ quelle: string; url: string } | null>(null);
  const nativStandUrlRef = useRef<string | null>(null);
  /** Laufnummer der Uebernahme: eine aeltere darf eine neuere nie ueberschreiben. */
  const nativStandLaufRef = useRef(0);
  /** Der erste Stand liess sich nicht uebernehmen — statt ewigem Spinner ein Hinweis. */
  const [nativStandFehlt, setNativStandFehlt] = useState(false);
  const [pdfAusgabeLaeuft, setPdfAusgabeLaeuft] = useState(false);

  const nativStandUebernehmen = useCallback((quelle: string) => {
    const lauf = ++nativStandLaufRef.current;
    blobFuerUrl(quelle)
      .then((pdf) => {
        if (lauf !== nativStandLaufRef.current) return;
        const url = URL.createObjectURL(pdf);
        const alt = nativStandUrlRef.current;
        nativStandUrlRef.current = url;
        setNativStand({ quelle, url });
        setNativStandFehlt(false);
        // Die alte Seitenvorschau hat ihre Bytes beim Einhaengen gelesen.
        if (alt) URL.revokeObjectURL(alt);
      })
      .catch((e: unknown) => {
        console.error('[QuoteEditor] Seitenvorschau: Stand nicht übernehmbar', e);
        if (lauf === nativStandLaufRef.current) setNativStandFehlt(true);
      });
  }, []);

  // Den ersten fertigen Stand von selbst zeigen; danach nur auf Anforderung.
  useEffect(() => {
    if (nativ && previewUri && !nativStand) nativStandUebernehmen(previewUri);
  }, [nativ, previewUri, nativStand, nativStandUebernehmen]);

  // Beim Verlassen: eigene Objekt-URL frei, laufende Uebernahme entwerten.
  useEffect(() => {
    const laufRef = nativStandLaufRef;
    const urlRef = nativStandUrlRef;
    return () => {
      laufRef.current += 1;
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    };
  }, []);

  /** Neuer Vorschau-Stand liegt vor, die Seitenvorschau zeigt noch den alten. */
  const nativVeraltet = !!nativStand && !!previewUri && nativStand.quelle !== previewUri;

  /**
   * Nur App: das AKTUELLE Angebot als Datei in den Teilen-Dialog — dort
   * oeffnet es eine PDF-App. Gesperrt, solange der Dialog aufgeht: ein zweites
   * Tippen liefe sonst ins „Teilen läuft schon".
   */
  const alsPdfOeffnen = () => {
    if (!previewUri || pdfAusgabeLaeuft) return;
    const dateiname = `${draft.quoteNumber || 'Angebot'}.pdf`;
    setPdfAusgabeLaeuft(true);
    void blobFuerUrl(previewUri)
      .then((pdf) => dateiAusgeben(pdf, dateiname))
      .catch(ausgabeFehlerMelden)
      .finally(() => setPdfAusgabeLaeuft(false));
  };

  /**
   * App-Zweig der Vorschau (Spalte und Vollbild): Leiste + feste
   * Seitenvorschau. Der Aufrufer stellt einen Flex-Spalten-Rahmen mit Hoehe.
   * Die Spalte rendert nicht mit, solange das Vollbild offen ist — sonst
   * liefe pdf.js zweimal fuer denselben Stand.
   */
  const nativVorschau = (imVollbild: boolean) => (
    <>
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-3 py-2">
        <span className={`min-w-0 flex-1 text-[11px] ${nativVeraltet ? 'font-semibold text-amber-700' : 'text-slate-500'}`}>
          {nativVeraltet
            ? 'Die Vorschau zeigt einen älteren Stand.'
            : `Seitenvorschau, höchstens ${NATIV_VORSCHAU_SEITEN} Seiten`}
        </span>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => { if (previewUri) nativStandUebernehmen(previewUri); }}
          disabled={!nativVeraltet}
          icon={<RefreshCw className="h-3.5 w-3.5" />}
        >
          Vorschau aktualisieren
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={alsPdfOeffnen}
          disabled={!previewUri || pdfAusgabeLaeuft}
          icon={pdfAusgabeLaeuft ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5" />}
        >
          Als PDF öffnen
        </Button>
      </div>
      <div className="relative min-h-0 flex-1">
        {!nativStand && nativStandFehlt ? (
          <div className="absolute inset-0 flex items-center justify-center px-6 text-center text-xs text-slate-500">
            Seitenvorschau nicht verfügbar. Über „Als PDF öffnen" lässt sich das Angebot in einer PDF-App ansehen.
          </div>
        ) : !nativStand ? (
          <div className="absolute inset-0 flex items-center justify-center text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : imVollbild || !previewFullscreen ? (
          <NativePdfSeiten
            key={nativStand.url}
            pdfUrl={nativStand.url}
            maxSeiten={NATIV_VORSCHAU_SEITEN}
            onTeilen={alsPdfOeffnen}
            teilenLaeuft={pdfAusgabeLaeuft}
            teilenText="Als PDF öffnen"
          />
        ) : null}
      </div>
    </>
  );

  const detachPreview = () => {
    // Android-App (docs/android-app-vertrag.md E14): die WebView kennt weder
    // zweite Fenster noch einen PDF-Betrachter. Das Angebot geht deshalb als
    // Datei in den Teilen-Dialog — dort öffnet es eine PDF-App. Kein Abdocken,
    // die Spalte bleibt stehen. Browser: unverändert (unten).
    if (istNativeApp()) {
      alsPdfOeffnen();
      return;
    }
    const w = window.open('', 'osia-quote-preview', 'width=980,height=1200');
    if (!w) { setDetachBlocked(true); return; }
    setDetachBlocked(false);
    // Eigene Huelle statt window.open(previewUri): so laesst sich das PDF bei
    // jeder Neuberechnung im SELBEN Fenster austauschen, ohne dass ein neues
    // aufspringt.
    w.document.open();
    w.document.write(
      '<!doctype html><html lang="de"><head><meta charset="utf-8">'
      + '<title>Angebotsvorschau</title>'
      + '<style>html,body{margin:0;height:100%;background:#334155}'
      + 'iframe{border:0;display:block;width:100%;height:100%}</style>'
      + '</head><body><iframe id="pv" title="Angebotsvorschau"></iframe></body></html>',
    );
    w.document.close();
    previewWindowRef.current = w;
    setPreviewDetached(true);
    setPreviewFullscreen(false);
    w.focus();
  };

  const attachPreview = () => {
    previewWindowRef.current?.close();
    previewWindowRef.current = null;
    setPreviewDetached(false);
  };

  // Abgedockte Vorschau aktuell halten und das Schliessen von Hand bemerken.
  useEffect(() => {
    if (!previewDetached) return;
    const w = previewWindowRef.current;
    if (!w || w.closed) { setPreviewDetached(false); return; }
    try {
      w.document.title = `Angebotsvorschau ${draft.quoteNumber || ''}`.trim();
      const frame = w.document.getElementById('pv') as HTMLIFrameElement | null;
      // Die alte Objekt-URL wird erst nach 10 s freigegeben (siehe Vorschau-
      // Effekt), das Umschalten hier kommt also nie ins Leere.
      if (frame && previewUri && frame.src !== previewUri) frame.src = previewUri;
    } catch {
      // Fenster wurde zwischen Pruefung und Zugriff geschlossen.
      setPreviewDetached(false);
      return;
    }
    // Schliesst der Nutzer das Fenster ueber die Fensterleiste, erfaehrt die
    // Maske davon nur durch Nachsehen — sonst bliebe die Spalte fuer immer weg.
    const timer = window.setInterval(() => {
      if (previewWindowRef.current?.closed) {
        previewWindowRef.current = null;
        setPreviewDetached(false);
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [previewDetached, previewUri, draft.quoteNumber]);

  // Beim Verlassen des Editors nimmt das Vorschaufenster niemand mehr mit.
  useEffect(() => () => { previewWindowRef.current?.close(); }, []);

  // Escape schliesst das Vollbild. Ein Klick daneben tut es NICHT: die Flaeche
  // ist das iframe selbst, und ein versehentlicher Klick beim Lesen wuerde die
  // Ansicht sonst wegreissen.
  useEffect(() => {
    if (!previewFullscreen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPreviewFullscreen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [previewFullscreen]);
  // Bepreisen-Fehler mit Reparaturdaten (LvPricingError): unzuordenbare
  // Positionen + im Original gefundene Nummern-Kandidaten. Steuert das
  // "Direkt beheben"-Panel unter der Vorschau-Fehlermeldung.
  const [lvFix, setLvFix] = useState<{ items: LvUnmatchedItem[]; candidates: string[] } | null>(null);
  // Gewaehlte Original-Nummer je unzuordenbarer Position (itemId -> Nummer).
  const [lvFixSel, setLvFixSel] = useState<Record<string, string>>({});
  // Vergroesserte Bearbeitung eines Textfelds (Modal); key/kind statt Closure,
  // damit der Modal-Inhalt immer den aktuellen Draft liest.
  // kind 'item': key ist die Positions-ID, bearbeitet wird deren Bezeichnung.
  const [expand, setExpand] = useState<{ label: string; key: string; kind: 'quote' | 'detail' | 'item' } | null>(null);

  // ─── Unterschrift (persoenlich, profiles.settings.signature) ───────────────
  const [signature, setSignature] = useState<string | undefined>((userProfile as any)?.settings?.signature);
  const [sigSaving, setSigSaving] = useState(false);

  const saveSignature = async (dataUrl?: string) => {
    if (!userProfile?.id) { alert('Kein Profil geladen.'); return; }
    setSigSaving(true);
    try {
      // Nur die Unterschrift patchen, NICHT den ganzen settings-Stand aus der
      // Prop zurueckschreiben: die Prop ist ein Abzug vom Anmeldezeitpunkt und
      // loeschte alles, was der Admin seither gepflegt hat (Briefkopf,
      // Rechnungsstammdaten, zentrale E-Mail-Signatur).
      await patchProfileSettings(userProfile.id, { signature: dataUrl });
      setSignature(dataUrl);
    } catch (e) {
      alert('Unterschrift konnte nicht gespeichert werden: ' + rechtFehlerOder(e));
    } finally {
      setSigSaving(false);
    }
  };

  const handleSignatureFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        // Auf max. 600px Breite verkleinern; PNG behaelt Transparenz.
        const scale = Math.min(1, 600 / img.width);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d')?.drawImage(img, 0, 0, canvas.width, canvas.height);
        void saveSignature(canvas.toDataURL('image/png'));
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // ─── Unterschrift per Smartphone (QR) ──────────────────────────────────────
  // Desktop legt signature_request mit Einmal-Token an, zeigt QR (#/sign/<token>),
  // pollt bis das Handy via RPC unterschrieben hat, uebernimmt ins Profil.
  const [qr, setQr] = useState<{ token: string; qrDataUrl: string; url: string } | null>(null);

  const startQrSignature = async () => {
    if (!userProfile?.id) { alert('Kein Profil geladen.'); return; }
    try {
      const token = crypto.randomUUID();
      const { error } = await supabase.from('signature_requests').insert({ token, user_id: userProfile.id });
      if (error) throw error;
      const url = `${window.location.origin}${window.location.pathname}#/sign/${token}?q=${encodeURIComponent(draft.quoteNumber || '')}`;
      const qrDataUrl = await QRCode.toDataURL(url, { width: 240, margin: 1 });
      setQr({ token, qrDataUrl, url });
    } catch (e) {
      alert('QR-Anfrage fehlgeschlagen (crm_quotes_v5.sql schon ausgeführt?): ' + rechtFehlerOder(e));
    }
  };

  const cancelQrSignature = async () => {
    if (qr) void supabase.from('signature_requests').delete().eq('token', qr.token);
    setQr(null);
  };

  useEffect(() => {
    if (!qr) return;
    const iv = setInterval(async () => {
      const { data } = await supabase.from('signature_requests').select('signature').eq('token', qr.token).maybeSingle();
      if (data?.signature) {
        clearInterval(iv);
        await saveSignature(data.signature);
        void supabase.from('signature_requests').delete().eq('token', qr.token);
        setQr(null);
      }
    }, 2500);
    // Token laeuft serverseitig nach 10 min ab — Modal dann schliessen.
    const to = setTimeout(() => { clearInterval(iv); setQr(null); }, 10 * 60_000);
    return () => { clearInterval(iv); clearTimeout(to); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qr]);

  // Kleiner Vergroessern-Button neben Textfeld-Labels (oeffnet das Expand-Modal).
  const expandBtn = (label: string, key: string, kind: 'quote' | 'detail') => (
    <button type="button" onClick={() => setExpand({ label, key, kind })} className="p-1 text-slate-300 hover:text-blue-600 hover:bg-blue-50 rounded transition-all" title={`${label} vergrößern`}>
      <Maximize2 className="w-3.5 h-3.5" />
    </button>
  );

  // Effektiver Status: ein versendetes, abgelaufenes Angebot gilt als 'expired'.
  // Alle Aktionen/Hinweise haengen daran – sonst liesse sich ein abgelaufenes
  // Angebot noch ablehnen, obwohl canTransition('expired','rejected') false ist.
  const effStatus = isQuoteExpired(draft, new Date().toISOString().slice(0, 10))
    ? 'expired'
    : draft.status;
  const locked = effStatus !== 'draft';

  /**
   * Das Angebot hat eine eingefrorene Anschrift.
   *
   * BEIDE Bedingungen zaehlen. Der Snapshot entsteht ausschliesslich beim
   * Versand (crmService.markQuoteSent) — ein Angebot, das aus dem Entwurf
   * heraus direkt auf "angenommen" oder "abgelehnt" gesetzt wurde
   * (ALLOWED_TRANSITIONS.draft in quoteLogic.ts), ist gesperrt und hat trotzdem
   * keinen; ebenso alles, was vor Einfuehrung von customer_snapshot verschickt
   * wurde. Ohne die Snapshot-Bedingung saehe der Dialog dort eine
   * Festschreibung, die es nicht gibt, und die Vorschau zeichnete gleich
   * darauf die neue Anschrift.
   *
   * Der Status muss trotzdem dazu: "Bearbeiten" setzt ein versendetes Angebot
   * zurueck auf Entwurf und laesst den alten Snapshot stehen — allein daran
   * gemessen bliebe es fuer immer bei der Anschrift von damals.
   */
  const customerFrozen = locked && Boolean(draft.customerSnapshot);

  /**
   * Restlaufzeit und Fertigstellungsgrad. Beides stand dem Nutzer bisher
   * nirgends zur Verfuegung: ein Angebot, das in drei Tagen ablaeuft, sah aus
   * wie jedes andere, und eines mit dreissig unbepreisten LV-Positionen sah
   * aus wie ein fertiges.
   */
  const validity = quoteValidityInfo(draft, effStatus, new Date().toISOString().slice(0, 10));
  const completeness = quoteCompleteness(draft.items || []);
  /**
   * Ungespeicherte Aenderung. Dieselbe Bedingung wie der Autosave-Effekt —
   * im gesperrten Angebot laeuft der naemlich nicht, und der Nutzer muss
   * sehen, dass er selbst speichern muss.
   */
  const dirty = draft !== quote;
  const setupSummary = quoteSetupSummary({
    taxRate: draft.taxRate,
    validUntil: draft.validUntil,
    street: draft.details?.siteStreet,
    zip: draft.details?.siteZip,
    city: draft.details?.siteCity,
    inspections: totalInspectionCount(draft.details?.intervalPhases ?? []),
    // intervalSummary liefert Datumsspannen ("01.03.–30.06. wöchentlich, …"),
    // also gut 70 Zeichen je Phase. Die Zeile ist `truncate` — deshalb steht
    // die Begehungszahl davor: sie ist die eine Zahl, die man eingeklappt
    // wirklich braucht.
    intervalText: intervalSummary(draft.details?.intervalPhases ?? []),
  });
  const readiness = quoteReadinessWarnings(completeness);

  /** offen / festgeschrieben / gesperrt-ohne-Festschreibung. */
  const customerEditState = documentCustomerState(!locked, Boolean(draft.customerSnapshot));

  /**
   * Abweichende Kundenangaben NUR fuer dieses Angebot.
   *
   * Der Bearbeiten-Dialog schreibt wahlweise in den Kundenstamm oder hierher
   * (details.customerOverride). Ab dem Versand steckt die Abweichung im
   * customerSnapshot und spielt keine eigene Rolle mehr.
   */
  const customerOverride = draft.details?.customerOverride;

  /**
   * Dieselbe Abweichung, aber nur solange sie fuer dieses Angebot ueberhaupt
   * noch gilt (customerOverride: customerOverrideApplies).
   *
   * Frueher stand hier `!customerFrozen`, und damit ueberlagerte ein aus dem
   * Entwurf heraus direkt angenommenes Angebot — gesperrt, nie versendet, ohne
   * Snapshot — weiter mit einer Abweichung, die der Bearbeiten-Dialog gar nicht
   * mehr anzeigte und die sich ueber die Oberflaeche nicht entfernen liess.
   */
  const effectiveOverride = customerOverrideApplies(customerEditState) ? customerOverride : undefined;
  const overrideActive = hasCustomerOverride(effectiveOverride);

  /** Der lebende Kunde, ueberlagert mit der Abweichung dieses Angebots. */
  const customerWithOverride = applyCustomerOverride(customer, effectiveOverride);

  /**
   * Der Kunde, mit dem gerendert wird: beim versendeten Angebot der Snapshot,
   * sonst der lebende Kunde MIT der Abweichung dieses Angebots. Ohne das zeigte
   * die Vorschau eines versendeten Angebots eine andere Anschrift als das PDF,
   * das beim Kunden liegt.
   */
  const customerForPdf =
    resolveDocumentCustomer(customerWithOverride, draft.customerSnapshot, customerFrozen) ??
    customerWithOverride;

  /**
   * Die gedruckten Kundenfelder als Zeichenkette — Ausloeser fuer den Neubau
   * der Vorschau.
   *
   * Bewusst NICHT die Objekt-Identitaet: die Angebotsliste baut fuer einen
   * nicht geladenen Kunden bei jedem Rendern ein neues Platzhalter-Objekt, und
   * ein Effekt daran wuerde sich endlos selbst neu anstossen.
   */
  const customerPrintKey = [
    customerForPdf.name, customerForPdf.name2, customerForPdf.name3,
    customerForPdf.street, customerForPdf.zip, customerForPdf.city,
    customerForPdf.address, customerForPdf.customerNumber,
  ].join('|');

  /**
   * Wer Kundendaten aus dem Angebot heraus aendern darf (Rollensystem). Den
   * Kundenstamm schreibt nur „Kunden verwalten“ — dieselbe Grenze wie die RLS
   * auf `customers` (Nr. 152); die Abweichung nur auf diesem Angebot schreibt
   * das Angebot und braucht „Angebote“ (vertriebRechte.ts). Admins duerfen
   * beides wie bisher.
   */
  const rechte = useRechte(userProfile?.role);
  const vertrieb = useMemo(() => vertriebRechte(rechte, userProfile?.role ?? null), [rechte, userProfile?.role]);
  const kundenBearbeitung = kundenBearbeitungImAngebot(vertrieb, Boolean(customer.id), canScopeToDocument(customerEditState));
  const canEditCustomer = kundenBearbeitung.offen;
  /**
   * Die Annahme legt ein Projekt des im Dialog gewaehlten Typs an — darf
   * jemand keinen Typ anlegen, bleibt der Knopf gesperrt, ausser das Angebot
   * laesst sich als Rahmenvertrag (ohne Projekt) annehmen (vertriebRechte.ts).
   * Der Dialog bietet nur die erlaubten Typen an.
   */
  const annahmeSperre = annahmeGesperrtDurch(rechte, isUnitPriceQuote(draft));
  const annahmeProjektTypen = useMemo(() => erlaubteProjektTypen(rechte.hat), [rechte]);
  /**
   * NACHTRAGSWEG: die Annahme in ein BESTEHENDES Projekt legt keins an und
   * braucht deshalb kein Anlage-Recht (wie „Rest beauftragen"). Der Dialog
   * bietet ihn immer an — darum sperrt `annahmeSperre` nur noch den Weg
   * „Projekt anlegen" IM Dialog (keine erlaubten Typen → Hinweis, Bestätigen
   * aus; handleAcceptConfirmed prüft das Recht vor dem Schreiben erneut),
   * nicht mehr den Knopf. Am Knopf steht der Hinweis weiter.
   */
  const annahmeKnopfHinweis = annahmeSperre
    ? `${rechtFehltHinweis(annahmeSperre)} — nur als Nachtrag zu einem bestehenden Projekt möglich`
    : undefined;
  // Nachtragsangebot (details.nachtragZu): Banner, „Projektposition übernehmen".
  const supplementRef = isSupplementQuote(draft) ? draft.details?.nachtragZu : undefined;

  // Draft nur beim Wechsel auf ein ANDERES Angebot komplett zuruecksetzen.
  // Prop-Updates desselben Angebots (Echo des eigenen Saves, Statuswechsel
  // durch Versand/Annahme) duerfen laufende Eingaben nicht verwerfen — es
  // werden nur Status-/Metadaten in den Draft uebernommen.
  const lastQuoteIdRef = useRef(quote.id);
  useEffect(() => {
    if (quote.id !== lastQuoteIdRef.current && draftRef.current !== baseRef.current) {
      // Wechsel auf ein anderes Angebot mit offenem Stand: GENAU diesen Stand
      // (Entwurf + Basis von A) einfrieren, lokal sichern und abschicken —
      // saveSnapshot liest keine Refs, die gleich auf B umgestellt werden.
      const frozenDraft = draftRef.current;
      const frozenBase = baseRef.current;
      persistBackup(frozenDraft, frozenBase, backupRef.current);
      const p = problemRef.current;
      if (!p || p.kind === 'error') void saveSnapshotRef.current(frozenDraft, frozenBase);
    }
    baseRef.current = quote;
    if (quote.id !== lastQuoteIdRef.current) {
      lastQuoteIdRef.current = quote.id;
      draftRef.current = quote;
      setDraft(quote);
      autosaveFailedForRef.current = null;
      problemRef.current = null;
      setSaveProblemState(null);
      setGuardMessage('');
      const offer = pendingBackupFor(quote);
      backupRef.current.suspended = offer !== null;
      setRestoreOffer(offer);
      setOfferMemoryOnly(false);
      setLeaveUnsaved(false);
      setAcceptResult(null);
      setTrancheDialog(null);
      setTrancheNotice(null);
      setTrancheError('');
      setPositionPicker(null);
      // Ansichtszustand gehoert zum Angebot, nicht zum Editor: sonst landet
      // man im naechsten Angebot mit dem Klappzustand des vorigen — beim
      // neuen, leeren Angebot ausgerechnet zugeklappt.
      setSetupOpen((quote.items || []).length === 0);
      setOpenItemId(null);
      setMoreOpen(false);
    } else {
      setDraft((cur) => {
        if (cur === quote) return cur;
        const metaKeys: (keyof Quote)[] = [
          'status', 'sentAt', 'sentTo', 'pdfUrl', 'orderNumber', 'projectId',
          'acceptedAt', 'rejectedAt', 'followUpDoneAt', 'followUpNotifiedAt', 'updatedAt',
          // Auftragsteile schreibt nur der Server-Weg (Annahme, Rest-Dialog) —
          // der Editor zeigt sie nur an.
          'orderTranches',
        ];
        const changed = metaKeys.filter((k) => quote[k] !== cur[k]);
        if (changed.length === 0) return cur;
        const merged: Quote = { ...cur };
        changed.forEach((k) => { (merged as any)[k] = quote[k]; });
        return merged;
      });
    }
    // Manuell-Modus, wenn ein frameworkRef gesetzt ist, der zu keinem
    // Rahmenvertrag des Kunden passt (Altbestand/Freitext).
    const ref = quote.details?.frameworkRef || '';
    const labels = (customer.frameworkContracts || []).map(frameworkContractLabel);
    setFrameworkManual(!!ref && !labels.includes(ref));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote]);

  // Katalog + Kundenpreise einmalig laden.
  useEffect(() => {
    let active = true;
    (async () => {
      const { data } = await supabase.from('service_catalog').select('*').order('lv_position');
      if (active && data) setCatalog(data.map(mapCatalogRow));
      try {
        const p = await fetchCustomerPrices(customer.id);
        if (active) setPrices(p);
      } catch {
        /* Preise optional – ohne Sonderpreise wird der Katalogpreis verwendet. */
      }
    })();
    return () => {
      active = false;
    };
  }, [customer.id]);

  // Textbausteine einmalig beim Editor-Mount laden (Admin-only, best effort).
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const tpls = await fetchTextTemplates();
        if (active) setTextTemplates(tpls);
      } catch {
        /* Bausteine optional – ohne bleibt das Vorlagen-Dropdown leer. */
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Nach Kategorie gruppiert (clientseitig) fuer die vier Picker.
  const templatesByCat = (cat: TextTemplateCategory): TextTemplate[] =>
    textTemplates.filter((t) => t.category === cat);
  const handleTemplateCreated = (t: TextTemplate) => setTextTemplates((prev) => [...prev, t]);
  const handleTemplateDeleted = (id: string) => setTextTemplates((prev) => prev.filter((t) => t.id !== id));

  // Klick ausserhalb schliesst das Katalog-Dropdown.
  useEffect(() => {
    if (!catalogOpen) return;
    const onDown = (e: MouseEvent) => {
      if (catalogWrapRef.current && !catalogWrapRef.current.contains(e.target as Node)) {
        setCatalogOpen(false);
        setCatalogQuery('');
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [catalogOpen]);

  const set = <K extends keyof Quote>(key: K, val: Quote[K]) => {
    setDraft((d) => ({ ...d, [key]: val }));
    setSaved(false);
  };

  const totals = useMemo(
    () => calculateQuoteTotals(draft.items || [], draft.discountPercent || 0, draft.taxRate || 0),
    [draft.items, draft.discountPercent, draft.taxRate]
  );

  // Detaillierte Summen (je Sektion + inkl./ohne NEP) fuer das Kalkulationsblatt.
  const detailed = useMemo(
    () => calculateQuoteTotalsDetailed(draft.items || [], draft.discountPercent || 0, draft.taxRate || 0),
    [draft.items, draft.discountPercent, draft.taxRate]
  );
  // Summen-Modus fuers PDF (Gesamtsumme / nur Einheitspreise / je Monat).
  const totalsMode = quoteTotalsMode(draft);
  const perMonthSuffix = totalsMode === 'monthly' ? ' je Monat' : '';

  // ─── Sektionen ─────────────────────────────────────────────────────────────
  // Default = Firmen-Standard (DEFAULT_QUOTE_SECTIONS) beim ersten Bearbeiten.
  const sections: QuoteSection[] = draft.details?.sections ?? DEFAULT_QUOTE_SECTIONS;

  const setDetail = <K extends keyof NonNullable<Quote['details']>>(key: K, val: NonNullable<Quote['details']>[K]) => {
    setDraft((d) => ({ ...d, details: { ...(d.details || {}), [key]: val } }));
    setSaved(false);
  };
  const setSections = (next: QuoteSection[]) => setDetail('sections', next);

  // Hoehenversatz des Anschreiben-Blocks auf Seite 1 des PDF. Dieselbe
  // Klemmung wie in generatorQuote.ts, damit der Regler nie eine Stellung
  // zeigt, die das PDF anders auslegt.
  const bodyOffsetMm = Math.max(-20, Math.min(60, Number(draft.details?.bodyOffsetMm) || 0));

  const addSection = () => setSections([...sections, { id: crypto.randomUUID(), title: 'Neue Sektion' }]);
  const renameSection = (id: string, title: string) => setSections(sections.map((s) => (s.id === id ? { ...s, title } : s)));
  const deleteSection = (id: string) => {
    // Positionen der geloeschten Sektion werden "ohne Zuordnung".
    setDraft((d) => ({
      ...d,
      details: { ...(d.details || {}), sections: sections.filter((s) => s.id !== id) },
      items: (d.items || []).map((it) => (it.sectionId === id ? { ...it, sectionId: undefined } : it)),
    }));
    setSaved(false);
  };
  const moveSection = (id: string, dir: -1 | 1) => {
    const idx = sections.findIndex((s) => s.id === id);
    const to = idx + dir;
    if (idx < 0 || to < 0 || to >= sections.length) return;
    const next = [...sections];
    [next[idx], next[to]] = [next[to], next[idx]];
    setSections(next);
  };

  // ─── Begehungs-Intervalle (Phasen) ──────────────────────────────────────────
  // Mehrere Phasen mit eigenem Zeitraum + Intervall. Bei vorhandenen Phasen
  // werden inspectionCount + intervalLabel automatisch abgeleitet; ohne Phasen
  // bleiben die manuellen Fallback-Felder (Altbestand) erhalten.
  const phases: IntervalPhase[] = draft.details?.intervalPhases ?? [];
  const phasesTotal = useMemo(() => totalInspectionCount(phases), [phases]);
  // Positionen, deren Menge aus Phasen berechnet wird (fuer die Link-Markierung).
  const linkedItemIds = useMemo(
    () => new Set(phases.map((p) => p.linkedItemId).filter(Boolean) as string[]),
    [phases],
  );

  // Zentrale, schleifenfreie Fortschreibung: die Mengen verknuepfter Positionen
  // werden SYNCHRON innerhalb der jeweiligen setDraft-Aktualisierung berechnet
  // (kein useEffect-Chain -> keine Endlosschleife). Jede Phasen- oder Positions-
  // Mutation laeuft durch applySync; details.intervalPhases samt der abgeleiteten
  // Felder (inspectionCount, intervalLabel) werden dabei mitgeschrieben.
  const applySync = (d: Quote, nextItems: QuoteItem[], nextPhases: IntervalPhase[]): Quote => ({
    ...d,
    items: syncPhaseQuantities(nextItems, nextPhases),
    details: {
      ...(d.details || {}),
      intervalPhases: nextPhases.length ? nextPhases : undefined,
      ...(nextPhases.length
        ? { inspectionCount: totalInspectionCount(nextPhases), intervalLabel: intervalSummary(nextPhases) }
        : {}),
    },
  });

  // ─── Ausschreibungs-Import (KI) ──────────────────────────────────────────────
  // Funktion 1: LV-Zeilen aus der Ausschreibung als GESPERRTE Positionen
  // uebernehmen (locked: true). Auftraggeber-Vorgabe -> LV-Nummer und Menge
  // nur nach Rueckfrage aenderbar; Einheit, Einheitspreis, Beschreibung, NEP
  // und Sektion bleiben editierbar. Preise
  // kommen NIE aus der KI (unitPrice: 0). Abschnitte werden zu Sektionen
  // (bestehende gleichen Titels werden wiederverwendet).
  // `meta.tenderDocumentId`: das Original-LV, dessen Positionen hier
  // uebernommen werden (details.activeTenderDocumentId). null = unbekannt,
  // details bleibt dann unveraendert.
  const applyTenderLv = (rows: TenderLvRow[], meta?: TenderLvApplyMeta) => {
    if (!rows.length) return;
    // Silent safety net. The import modal already disables struck rows and
    // drops them before calling this, so `accepted` normally holds every row.
    // The filter stays as a second line of defence: a struck position must
    // never be healed back into the calculation nor added a second time by a
    // re-import. The user-facing feedback lives in the modal (disabled row +
    // "Gestrichen" badge) — deliberately not duplicated here.
    const { accepted } = splitExcludedDuplicates(
      rows,
      tenderLvRowKey,
      excludedQuoteItemKeys<QuoteItem>(draft.items || [], quoteItemLvKey),
    );
    if (!accepted.length) return;
    setDraft((d) => {
      const curSections = d.details?.sections ?? DEFAULT_QUOTE_SECTIONS;
      const sectionsCopy = [...curSections];
      const titleToId = new Map<string, string>();
      sectionsCopy.forEach((s) => titleToId.set(s.title.trim().toLowerCase(), s.id));
      const resolveSectionId = (abschnitt: string): string | undefined => {
        const t = (abschnitt || '').trim();
        if (!t) return undefined;
        const key = t.toLowerCase();
        let id = titleToId.get(key);
        if (!id) {
          id = crypto.randomUUID();
          titleToId.set(key, id);
          sectionsCopy.push({ id, title: t });
        }
        return id;
      };
      const newItems: QuoteItem[] = accepted.map((r) => ({
        id: crypto.randomUUID(),
        lvPosition: r.position || '',
        title: r.bezeichnung || '',
        unit: r.einheit || '',
        quantity: Number.isFinite(r.menge) ? r.menge : 0,
        unitPrice: 0,
        discountPercent: 0,
        sectionId: resolveSectionId(r.abschnitt),
        assignment: r.assignment,
        // Bedarfs-/Eventualposition aus dem LV. Ohne die Uebernahme zaehlte
        // sie voll in die Angebotssumme — ein Geldfehler, kein Schoenheits-
        // fehler.
        nep: r.nep || undefined,
        locked: true,
      }));
      const nextItems = [...(d.items || []), ...newItems];
      return {
        ...d,
        details: {
          ...(d.details || {}),
          sections: sectionsCopy,
          ...(meta?.tenderDocumentId ? { activeTenderDocumentId: meta.tenderDocumentId } : {}),
        },
        items: syncPhaseQuantities(nextItems, d.details?.intervalPhases ?? []),
      };
    });
    setSaved(false);
  };

  // Funktion 2: ausgelesene Projekt-Rahmendaten/Anforderungen vorbefuellen.
  // Nur uebergebene (= im Modal angehakte) Felder werden gesetzt, Bestehendes
  // bleibt sonst unangetastet. Erzeugt KEINE Positionen/Preise.
  const applyTenderRequirements = (r: TenderRequirements) => {
    setDraft((d) => {
      const next: Quote = { ...d };
      if (r.title) next.title = r.title;
      const details = { ...(d.details || {}) };
      if (r.siteStreet) details.siteStreet = r.siteStreet;
      if (r.siteZip) details.siteZip = r.siteZip;
      if (r.siteCity) details.siteCity = r.siteCity;
      if (r.projectStart) details.projectStart = r.projectStart;
      if (r.projectEnd) details.projectEnd = r.projectEnd;
      if (r.intervalLabel) details.intervalLabel = r.intervalLabel;
      if (r.inspectionCount !== undefined) details.inspectionCount = r.inspectionCount;
      if (r.tenderRequirements) details.tenderRequirements = r.tenderRequirements;
      next.details = details;
      return next;
    });
    setSaved(false);
  };

  const setPhases = (next: IntervalPhase[]) => {
    setDraft((d) => applySync(d, d.items || [], next));
    setSaved(false);
  };
  const addPhase = () => {
    const prev = phases[phases.length - 1];
    // Die neue (= letzte) Phase endet automatisch am Projektende, damit die
    // Zeitraeume laut Anforderung immer mit dem Enddatum uebereinstimmen.
    setPhases([
      ...phases,
      {
        id: crypto.randomUUID(),
        // Einen Tag NACH dem Ende der vorigen Phase — dasselbe Datum liesse
        // die Zeitraeume ueberlappen und zaehlte Begehungen doppelt.
        from: (prev?.to && nextDayIso(prev.to)) || draft.details?.projectStart || '',
        to: draft.details?.projectEnd || '',
        interval: '14taeglich',
      },
    ]);
  };
  // Fallback 'Gesamtzeitraum': die Auswahl eines Intervalls legt EINE Phase ueber
  // den gesamten Projektzeitraum an; danach uebernimmt die normale Phasen-UI.
  const startFallbackPhase = (interval: IntervalKind) => {
    setPhases([
      {
        id: crypto.randomUUID(),
        from: draft.details?.projectStart || '',
        to: draft.details?.projectEnd || '',
        interval,
      },
    ]);
  };
  const updatePhase = <K extends keyof IntervalPhase>(id: string, field: K, val: IntervalPhase[K]) =>
    setPhases(phases.map((p) => (p.id === id ? { ...p, [field]: val } : p)));
  const removePhase = (id: string) => setPhases(phases.filter((p) => p.id !== id));
  // Phase <-> Position verknuepfen (leerer Wert loest die Verknuepfung).
  const setPhaseLink = (id: string, itemId: string | undefined) =>
    setPhases(phases.map((p) => (p.id === id ? { ...p, linkedItemId: itemId || undefined } : p)));
  // Neue Begehungsposition fuer eine Phase anlegen und direkt verknuepfen. Menge
  // bleibt 0 – applySync fuellt sie sofort aus den Begehungen der Phase.
  const createLinkedItemForPhase = (phaseId: string) => {
    setDraft((d) => {
      const curPhases = d.details?.intervalPhases ?? [];
      const ph = curPhases.find((p) => p.id === phaseId);
      if (!ph) return d;
      // Bauphasen-Heuristik: zweite Sektion, sonst die erste.
      const secId = sections.length >= 2 ? sections[1].id : sections[0]?.id;
      const newItem: QuoteItem = {
        id: crypto.randomUUID(),
        lvPosition: '',
        title: `Sicherheitsbegehungen (${INTERVAL_LABELS[ph.interval]})`,
        // 'Stück' als Standard, nicht 'Begehung' — die Einheit soll neutral
        // bleiben; bei Stundenfaktor weiterhin 'Std'.
        unit: ph.factor && ph.factor !== 1 ? 'Std' : 'Stück',
        quantity: 0,
        unitPrice: 0,
        sectionId: secId,
      };
      const nextPhases = curPhases.map((p) => (p.id === phaseId ? { ...p, linkedItemId: newItem.id } : p));
      return applySync(d, [...(d.items || []), newItem], nextPhases);
    });
    setSaved(false);
  };

  // Positionen mit berechneten Positionsnummern (nur Anzeige; beim Speichern persistiert).
  const itemsWithPos = useMemo(() => computePosNumbers(sections, draft.items || []), [sections, draft.items]);
  const perSectionMap = useMemo(() => {
    const m = new Map<string | null, number>();
    detailed.perSection.forEach((p) => m.set(p.sectionId, p.subtotal));
    return m;
  }, [detailed]);

  // Counters of the position table: how many positions are struck, and what the
  // current selection consists of (drives the bulk action bar).
  const excludedCount = useMemo(() => countExcludedQuoteItems(draft.items || []), [draft.items]);
  const selectionStats = useMemo(
    () => quoteSelectionStats(draft.items || [], selectedItemIds),
    [draft.items, selectedItemIds],
  );
  // Recognition keys of the struck positions — the LV import marks matching
  // rows instead of offering them for a second import.
  const excludedLvKeys = useMemo(
    () => excludedQuoteItemKeys<QuoteItem>(draft.items || [], quoteItemLvKey),
    [draft.items],
  );

  // Firmenkopf fuer die PDF-Vorschau: company-Prop + Briefkopftext aus quoteConfig
  // (falls vorhanden). userName aus dem Koordinator-Profil.
  // invoiceConfig fuer die Spalten-Fusszeile: das Angebot traegt denselben
  // Fuss wie die Rechnung, gespeist aus den Rechnungs-Stammdaten.
  const companyForPdf = buildCompanyForPdf(company, quoteConfig, invoiceConfig);
  // Ersteller-Name fuer den PDF-Grussblock: Vor-/Nachname aus dem Profil
  // (profiles.first_name/last_name). Bewusst KEIN E-Mail-Fallback — lieber
  // ohne Namenszeile als mit Adresse im Anschreiben.
  const pdfUserName: string | undefined =
    [userProfile?.first_name, userProfile?.last_name].filter(Boolean).join(' ').trim() || undefined;

  // Liegt ein Original-LV im Bucket? Laeuft beim Mount und erneut, sobald der
  // Ausschreibungs-Import geschlossen wird (dann kann gerade eines hochgeladen
  // worden sein). Reine Nebenabfrage — ein Fehler blockiert nichts.
  useEffect(() => {
    if (!draft.id || tenderKind) return;
    let alive = true;
    fetchTenderDocuments(draft.id)
      .then((docs) => { if (alive) setHasLvDoc(docs.some((d) => d.kind === 'lv')); })
      .catch((e) => {
        console.warn('Prüfung auf Original-LV fehlgeschlagen', e);
        if (alive) setHasLvDoc(null);
      });
    return () => { alive = false; };
  }, [draft.id, tenderKind]);

  // Live-Vorschau: ~600ms nach jeder Draft-Aenderung neu rendern. Die Vorschau
  // steht dauerhaft neben der Maske (wie im Rechnungseditor), deshalb laeuft der
  // Lauf immer und nicht nur bei geoeffnetem Panel.
  // Das Anhaengen des Original-LV ist async (Bucket-Download + Merge), deshalb
  // sichert `active` gegen Out-of-order-Antworten: clearTimeout stoppt nur einen
  // noch nicht gestarteten Callback — eine bereits laufende, langsame alte
  // Vorschau darf eine neuere nicht ueberschreiben (gleiches Muster wie beim
  // Katalog-/Preis-Laden oben).
  useEffect(() => {
    let active = true;
    const handle = setTimeout(() => {
      void (async () => {
        try {
          const quoteForPdf: Quote = {
            ...draft,
            items: computePosNumbers(sections, draft.items || []),
            details: { ...(draft.details || {}), sections },
            netTotal: totals.netTotal,
            grossTotal: totals.grossTotal,
          };
          const mode = lvOriginalMode(quoteForPdf);
          const doc = generateQuotePDF(
            quoteForPdf, customerForPdf, companyForPdf, pdfUserName, signature,
            // Grussformel aus derselben Pflegestelle wie bei der Rechnung.
            { skipCalculation: mode === 'priced', closingText: invoiceConfig?.closingText },
          );
          let bytes: ArrayBuffer | Uint8Array = doc.output('arraybuffer');
          if (mode !== 'none') {
            bytes = await applyOriginalLv(bytes as ArrayBuffer, quoteForPdf);
          }
          // Benannte Datei statt Data-URI: der PDF-Viewer zeigt oben links
          // den Dateinamen an — bei einer Data-URI ist das ein Zufallsname,
          // der wie eine falsche Angebotsnummer gelesen wird.
          const uri = URL.createObjectURL(
            new File([bytes as BlobPart], `${quoteForPdf.quoteNumber || 'Angebot'}.pdf`, {
              type: 'application/pdf',
            }),
          );
          if (!active) {
            URL.revokeObjectURL(uri);
            return;
          }
          // Die alte Objekt-URL erst nach dem Umschalten freigeben — sofort
          // widerrufen zeigte der eingebettete Viewer noch kurz ins Leere.
          const old = previewUrlRef.current;
          previewUrlRef.current = uri;
          setPreviewUri(uri);
          if (old) setTimeout(() => URL.revokeObjectURL(old), 10_000);
          setPreviewError(null);
          setLvFix(null);
        } catch (e) {
          if (!active) return;
          setPreviewError(e instanceof Error ? e.message : 'Vorschau konnte nicht erstellt werden.');
          setLvFix(e instanceof LvPricingError ? { items: e.unmatchedItems, candidates: e.candidates } : null);
        }
      })();
    }, 600);
    return () => { active = false; clearTimeout(handle); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, customerPrintKey]);

  // ─── Bepreisen direkt beheben: Original-Nummern neu zuordnen ───────────────
  // Kandidaten fuer EINE Zeile des Reparatur-Panels: alle im Original
  // gefundenen Nummern, abzueglich der von anderen (zuordenbaren) Positionen
  // bereits belegten und der in anderen Panel-Zeilen gewaehlten.
  const lvFixCandidatesFor = (itemId: string): string[] => {
    if (!lvFix) return [];
    const unmatchedIds = new Set(lvFix.items.map((u) => u.itemId));
    const usedByItems = new Set(
      (draft.items || [])
        .filter((i) => !unmatchedIds.has(i.id))
        .map((i) => normalizeLvPos(i.lvPosition || ''))
        .filter(Boolean),
    );
    const chosenElsewhere = new Set(
      Object.entries(lvFixSel)
        .filter(([id, v]) => id !== itemId && v)
        .map(([, v]) => v),
    );
    return lvFix.candidates.filter((c) => !usedByItems.has(c) && !chosenElsewhere.has(c));
  };

  // Schreibt die gewaehlten Nummern in die Positionen; die Draft-Aenderung
  // stoesst die Vorschau neu an — gelingt das Bepreisen jetzt, raeumt der
  // Erfolgspfad lvFix auf, sonst listet der naechste Fehler den Rest.
  const applyLvFix = () => {
    const sel = lvFixSel;
    set('items', (draft.items || []).map((it) => (sel[it.id] ? { ...it, lvPosition: sel[it.id] } : it)));
    setLvFixSel({});
  };

  const filteredCatalog = useMemo(() => {
    const q = catalogQuery.trim().toLowerCase();
    if (!q) return catalog;
    return catalog.filter(
      (c) => (c.title || '').toLowerCase().includes(q) || (c.lvPosition || '').toLowerCase().includes(q)
    );
  }, [catalog, catalogQuery]);

  // ─── Rahmenvertrag → Positionen ─────────────────────────────────────────────
  // Traegt der gewaehlte Rahmenvertrag eigene Preise (aus einem angenommenen
  // Angebot ohne Endsumme), kommen diese ins Angebot; sonst die Positionsliste
  // der verknuepften LV-Vorlage (templateId).
  const [importingFramework, setImportingFramework] = useState(false);
  const selectedFrameworkContract = useMemo(
    () => frameworkContracts.find((c) => frameworkContractLabel(c) === (draft.details?.frameworkRef || '')),
    [frameworkContracts, draft.details?.frameworkRef],
  );
  const selectedFrameworkHasItems = (selectedFrameworkContract?.items?.length ?? 0) > 0;
  const importFrameworkTemplateItems = async () => {
    const contract = selectedFrameworkContract;
    const templateId = contract?.templateId;
    if (!contract || (!selectedFrameworkHasItems && !templateId)) return;
    // Ergaenzen statt ersetzen: bestehende Positionen bleiben erhalten.
    if ((draft.items || []).length > 0 &&
        !confirm('Das Angebot enthält bereits Positionen. Bestehende Positionen behalten und ergänzen?')) {
      return;
    }
    if (selectedFrameworkHasItems) {
      // Vereinbarte Nettopreise des Vertrags — KEINE Kunden-Sonderpreise
      // darueber: der Preis ist vertraglich fest, nicht aus dem Katalog.
      const fromContract = frameworkContractToQuoteItems(contract, sections[0]?.id);
      setDraft((d) => applySync(d, [...(d.items || []), ...fromContract], d.details?.intervalPhases ?? []));
      setSaved(false);
      return;
    }
    if (!templateId) return;
    setImportingFramework(true);
    try {
      const { data } = await supabase.from('service_templates').select('items').eq('id', templateId).maybeSingle();
      const templateItems = ((data?.items as ServiceItem[]) || []);
      if (templateItems.length === 0) {
        alert('Die verknüpfte Vorlage enthält keine Positionen.');
        return;
      }
      // Katalog-Preise/Kunden-Sonderpreise anwenden, in die erste Sektion legen.
      const priced = applyCustomerPricing(templateItems, prices, customer).map((qi) => ({
        ...qi,
        id: crypto.randomUUID(),
        sectionId: sections[0]?.id,
      }));
      setDraft((d) => applySync(d, [...(d.items || []), ...priced], d.details?.intervalPhases ?? []));
      setSaved(false);
    } catch (e) {
      alert('Positionen konnten nicht übernommen werden: ' + rechtFehlerOder(e));
    } finally {
      setImportingFramework(false);
    }
  };

  // ─── Positionen ────────────────────────────────────────────────────────────
  const addCatalogItem = (item: ServiceItem, sectionId?: string) => {
    const [priced] = applyCustomerPricing([item], prices, customer);
    if (priced) set('items', [...(draft.items || []), { ...priced, id: crypto.randomUUID(), sectionId }]);
    setCatalogOpen(false);
    setCatalogTargetSection(null);
    setCatalogQuery('');
  };

  const addFreeItem = (sectionId?: string) => {
    const item: QuoteItem = {
      id: crypto.randomUUID(),
      lvPosition: '',
      title: '',
      unit: '',
      quantity: 1,
      unitPrice: 0,
      discountPercent: 0,
      sectionId,
    };
    set('items', [...(draft.items || []), item]);
  };

  const updateItem = <K extends keyof QuoteItem>(id: string, field: K, val: QuoteItem[K]) => {
    // Ueber applySync: manuelle Mengen verlinkter Positionen werden aus den
    // Phasen ueberschrieben (bewusst – Menge ist abgeleitet, siehe Link-Marker).
    setDraft((d) => {
      const nextItems = (d.items || []).map((it) => {
        if (it.id !== id) return it;
        // Klartext direkt geaendert: eine noch anhaengende Formatierung passt
        // nicht mehr zum Text (das Inline-Feld ist bei aktiver Formatierung
        // gesperrt, hier landen nur verwaiste) — weg damit.
        if (field === 'title' && it.titleRich) {
          const { titleRich: _drop, ...rest } = it;
          return { ...rest, title: val as string };
        }
        return { ...it, [field]: val };
      });
      return applySync(d, nextItems, d.details?.intervalPhases ?? []);
    });
    setSaved(false);
  };

  /**
   * PAKETPOSITION: Bestandteile und Stundenkontingent in EINEM Schritt — mit
   * der Liste faellt das Kontingent (S1, bestandteilePatch). Ein
   * `undefined`-Feld verschwindet beim Speichern (jsonb), wie bisher bei
   * `bestandteile`.
   */
  const patchItem = (id: string, patch: Partial<QuoteItem>) => {
    setDraft((d) => {
      const nextItems = (d.items || []).map((it) => (it.id === id ? { ...it, ...patch } : it));
      return applySync(d, nextItems, d.details?.intervalPhases ?? []);
    });
    setSaved(false);
  };

  /** Leistungsbeschreibung aus dem Formatier-Editor: Klartext + Formatierung in einem Schritt. */
  const updateItemRich = (id: string, blocks: QuoteRichBlock[]) => {
    const next = quoteTextFromRich(blocks);
    setDraft((d) => {
      const nextItems = (d.items || []).map((it) => {
        if (it.id !== id) return it;
        const { titleRich: _drop, ...rest } = it;
        return next.titleRich ? { ...rest, title: next.title, titleRich: next.titleRich } : { ...rest, title: next.title };
      });
      return applySync(d, nextItems, d.details?.intervalPhases ?? []);
    });
    setSaved(false);
  };

  /**
   * Position einen Platz innerhalb ihrer Sektion verschieben. Die
   * Positionsnummer haengt an der Reihenfolge — sortiert der LV-Import eine
   * Zeile falsch ein, ist das hier die Handkorrektur. Mengen/Phasen bleiben
   * unberuehrt (reine Umsortierung), applySync haelt die Verknuepfungen aktuell.
   */
  const moveItem = (id: string, dir: -1 | 1) => {
    setDraft((d) => {
      const nextItems = moveQuoteItem(d.items || [], id, dir);
      if (nextItems === (d.items || [])) return d;
      return applySync(d, nextItems, d.details?.intervalPhases ?? []);
    });
    setSaved(false);
  };

  /**
   * Die Vorschauspalte faellt weg, sobald die Vorschau abgedockt ist —
   * AUSSER im Fehlerfall: unter der Fehlermeldung steht das Panel, mit dem
   * sich ein gescheitertes Bepreisen direkt beheben laesst. Ohne diese
   * Ausnahme waere es im abgedockten Zustand unerreichbar, und das eigene
   * Fenster zeigte weiter ein veraltetes PDF ohne Hinweis darauf.
   */
  const previewPanelVisible = !previewDetached || !!previewError;

  /**
   * Positionen je Abschnitt nach der LV-Nummer des Kunden ordnen. Der LV-Import
   * uebernimmt die Reihenfolge aus einer KI-Antwort und sortiert dabei nicht
   * immer richtig ein; von Hand war das bisher nur Klick fuer Klick zu
   * korrigieren. Die Pfeile in der Zeile bleiben der Feinschliff danach — die
   * Sortierung ist ein einmaliger Lauf, keine dauerhafte Sortierordnung.
   */
  const sortItemsByLv = () => {
    setDraft((d) => {
      const nextItems = sortQuoteItemsByLvPosition(d.items || []);
      if (nextItems === (d.items || [])) return d;
      return applySync(d, nextItems, d.details?.intervalPhases ?? []);
    });
    setSaved(false);
  };

  /**
   * Strike a position (`excluded`) — it stays visible and keeps its slot, but
   * drops out of every total, out of the delivery and is printed as "entfällt".
   * Reversible, and independent of `nep`/`locked`. The rules live in
   * quoteItemSelection/quoteLogic, this is only the state plumbing.
   */
  const setItemsExcluded = (ids: ReadonlySet<string>, excluded: boolean) => {
    setDraft((d) => applySync(d, setQuoteItemsExcluded(d.items || [], ids, excluded), d.details?.intervalPhases ?? []));
    setSaved(false);
  };
  const toggleItemExcluded = (id: string, excluded: boolean) => setItemsExcluded(new Set([id]), excluded);
  // Bulk action: acts on the current selection and clears it afterwards, so the
  // action bar never keeps a stale selection around.
  const applyBulkExcluded = (excluded: boolean) => {
    setItemsExcluded(selectedItemIds, excluded);
    setSelectedItemIds(new Set<string>());
  };

  const removeItem = (id: string) => {
    setSelectedItemIds((prev) => pruneQuoteItemSelection(prev, (draft.items || []).filter((it) => it.id !== id)));
    setDraft((d) => {
      const nextItems = (d.items || []).filter((it) => it.id !== id);
      // Verwaiste Verknuepfungen loesen, wenn die verlinkte Position geloescht wird.
      const nextPhases = (d.details?.intervalPhases ?? []).map((p) =>
        p.linkedItemId === id ? { ...p, linkedItemId: undefined } : p,
      );
      return applySync(d, nextItems, nextPhases);
    });
    setSaved(false);
  };

  // Baustellen-Adresse ueber Nominatim pruefen (informativ, blockiert nichts).
  const handleVerifySite = async () => {
    setSiteChecking(true);
    setSiteCheck(null);
    try {
      const found = await verifyAddress({
        street: draft.details?.siteStreet,
        zip: draft.details?.siteZip,
        city: draft.details?.siteCity,
      });
      setSiteCheck(found ? { ok: true, text: found } : { ok: false, text: 'Nicht eindeutig gefunden' });
    } catch {
      setSiteCheck({ ok: false, text: 'Nicht eindeutig gefunden' });
    } finally {
      setSiteChecking(false);
    }
  };

  // ─── Als Vorlage speichern ─────────────────────────────────────────────────
  const handleSaveTemplate = async () => {
    const name = prompt('Name der Vorlage:');
    if (!name || !name.trim()) return;
    setSavingTemplate(true);
    try {
      await createQuoteTemplate(name.trim(), {
        title: draft.title,
        offerType: draft.details?.offerType,
        introText: draft.introText,
        outroText: draft.outroText,
        paymentTerms: draft.paymentTerms,
        sections,
        // Frische IDs, damit die Vorlage von diesem Angebot entkoppelt ist.
        // `excluded` wird bewusst ABGESTREIFT: eine Streichung ist eine
        // Entscheidung an DIESEM Angebot. Bliebe sie stehen, traegt die Vorlage
        // tote, vorgestrichene Zeilen in jedes neue Angebot. (Beim Duplizieren
        // eines Angebots bleibt sie dagegen erhalten — crmService.duplicateQuote.)
        items: (draft.items || []).map((it) => {
          const copy: QuoteItem = { ...it, id: crypto.randomUUID() };
          delete copy.excluded;
          return copy;
        }),
        discountPercent: draft.discountPercent,
        taxRate: draft.taxRate,
      });
      alert('Vorlage gespeichert.');
    } catch (e) {
      alert('Vorlage konnte nicht gespeichert werden: ' + rechtFehlerOder(e));
    } finally {
      setSavingTemplate(false);
    }
  };

  // ─── Speichern ─────────────────────────────────────────────────────────────
  // EIN Speicherweg (saveNow) fuer Autosave, Knopf, Zurueck, Unmount, pagehide
  // und die Statusaktionen. Er arbeitet auf draftRef/baseRef, damit auch ein
  // Aufruf aus einem alten Render den aktuellen Stand speichert.
  const reportProblem = (p: SaveProblem | null) => {
    problemRef.current = p;
    if (mountedRef.current) setSaveProblemState(p);
  };

  const doSave = async (sent: Quote, base: Quote): Promise<SaveResult> => {
    const quoteId = sent.id;
    // Welche Sicherungen dieses Speichern abdeckt: alles bis hierher aus
    // dieser Sitzung (Vorgaenger von `sent`) — nichts, was danach kommt.
    const cover = { sent, sessionId: backupRef.current.sessionId, seqAtStart: backupRef.current.seq };
    if (mountedRef.current) { setSaving(true); setSaved(false); }
    setQuoteEditorBusy(getQuoteEditorBusy().open, true);
    let newBase: Quote;
    let next: Quote;
    try {
      // Innerhalb des try: wirft die Aufbereitung, ist das ein gescheitertes
      // Speichern mit Hinweis — kein haengender Zustand.
      const prepared = prepareQuoteSave(sent, base);
      next = prepared.next;
      let updatedAt = base.updatedAt;
      let merged: Quote | null = null;
      if (Object.keys(prepared.updates).length > 0) {
        const res = await updateQuote(quoteId, prepared.updates, { expectedUpdatedAt: base.updatedAt, base });
        updatedAt = res.updatedAt;
        merged = res.merged;
      }
      newBase = settleSaveBase(next, updatedAt, merged);
    } catch (e) {
      // Autosave fuer diesen Stand pausieren; die lokale Sicherung haelt ihn fest.
      const stillHere = mountedRef.current && lastQuoteIdRef.current === quoteId;
      if (stillHere) {
        autosaveFailedForRef.current = sent;
        const backedUp = persistBackup(draftRef.current, baseRef.current, backupRef.current) !== null;
        if (e instanceof QuoteConflictError) reportProblem({ kind: 'conflict', server: e.server });
        else if (e instanceof QuoteNotSavedError) reportProblem({ kind: 'missing', backedUp });
        else reportProblem({ kind: 'error', message: rechtFehlerOder(e), backedUp });
      }
      // Nicht mehr hier: die Sicherung von `sent` wurde beim Verlassen bzw.
      // Wechsel geschrieben und bleibt liegen.
      if (mountedRef.current) setSaving(false);
      setQuoteEditorBusy(getQuoteEditorBusy().open, snapshotPendingRef.current > 0);
      return { ok: false, base: null, sent };
    }

    // Ab hier ist GESPEICHERT. Neuer Anker sofort — noch vor jeder
    // Nachbearbeitung, damit ein Fehler dort keinen alten Anker hinterlaesst.
    const stillHere = mountedRef.current && lastQuoteIdRef.current === quoteId;
    if (stillHere) baseRef.current = newBase;
    onChange(newBase);
    try {
      clearQuoteDraftBackupIfCovered(cover);
      if (stillHere) {
        // Auch der Ref bekommt den drei-Wege-gelegten Stand — ein direkt
        // folgendes saveNow (Flush) darf nicht den alten Entwurf gegen die neue
        // Basis diffen und fremde Positionen als geloescht schreiben.
        const settledFrom = draftRef.current;
        const settledDraft = rebaseInFlightEdits(sent, settledFrom, newBase);
        draftRef.current = settledDraft;
        autosaveFailedForRef.current = null;
        reportProblem(null);
        setGuardMessage('');
        // Eingaben, die WAEHREND des Speicherns passiert sind, nicht verwerfen —
        // drei-Wege auf den neuen Stand. Ohne solche Eingaben: exakt newBase.
        setDraft((cur) => (cur === settledFrom ? settledDraft : rebaseInFlightEdits(sent, cur, newBase)));
        setSaved(true);
      }
    } catch (e) {
      // Gespeichert ist es; nur das Nachziehen des Entwurfs ging schief. Den
      // Entwurf nicht blind weiterschreiben — sichtbar melden.
      console.error('Nachbearbeitung nach dem Speichern fehlgeschlagen', e);
      if (stillHere) {
        autosaveFailedForRef.current = draftRef.current;
        const backedUp = persistBackup(draftRef.current, baseRef.current, backupRef.current) !== null;
        reportProblem({ kind: 'error', message: 'Gespeichert, aber die Anzeige konnte nicht nachgezogen werden: ' + rechtFehlerOder(e), backedUp });
      }
    } finally {
      if (mountedRef.current) setSaving(false);
      setQuoteEditorBusy(getQuoteEditorBusy().open, snapshotPendingRef.current > 0);
    }
    return { ok: true, base: newBase, sent };
  };

  const startSave = (sent: Quote, base: Quote): Promise<SaveResult> => {
    const p = doSave(sent, base);
    inFlightRef.current = p;
    void p.finally(() => { if (inFlightRef.current === p) inFlightRef.current = null; });
    return p;
  };

  const saveNow = async (): Promise<boolean> => {
    // Ein laufendes Speichern abwarten, dann den dann aktuellen Stand nehmen.
    while (inFlightRef.current) {
      const r = await inFlightRef.current;
      if (!r.ok) return false;
    }
    const sent = draftRef.current;
    const base = baseRef.current;
    if (sent === base) return true;
    return (await startSave(sent, base)).ok;
  };

  /**
   * Einen eingefrorenen Stand speichern (Angebotswechsel). Liest keine Refs:
   * die zeigen danach schon auf das neue Angebot. Laeuft noch ein Speichern
   * DESSELBEN Angebots, wird dessen neuer Serverstand die Basis.
   */
  const saveSnapshot = async (frozenDraft: Quote, frozenBase: Quote): Promise<boolean> => {
    snapshotPendingRef.current += 1;
    try {
      return await saveSnapshotInner(frozenDraft, frozenBase);
    } finally {
      snapshotPendingRef.current -= 1;
      setQuoteEditorBusy(getQuoteEditorBusy().open, inFlightRef.current !== null || snapshotPendingRef.current > 0);
    }
  };

  const saveSnapshotInner = async (frozenDraft: Quote, frozenBase: Quote): Promise<boolean> => {
    let b = frozenBase;
    let inFlightSent: Quote | null = null;
    while (inFlightRef.current) {
      const r = await inFlightRef.current;
      if (r.ok && r.base && r.base.id === frozenDraft.id) {
        b = r.base;
        inFlightSent = r.sent;
      }
    }
    // Die eingefrorenen Aenderungen drei-Wege auf den neuen Serverstand legen,
    // gemessen an dem, was jenes Speichern TATSAECHLICH abgeschickt hat: so
    // bleiben fremde, zusammengefuehrte Positionen erhalten, und ein
    // zwischenzeitliches Zuruecknehmen geht nicht verloren.
    const sent = rebaseSnapshotOnto(inFlightSent, frozenDraft, b);
    if (sent === b) return true;
    return (await startSave(sent, b)).ok;
  };

  /**
   * Sofort speichern, falls etwas offen ist. true = nichts mehr offen.
   * Bei ungeloestem Konflikt / fehlendem Angebot kein erneuter Versuch — dort
   * entscheidet der Nutzer im Banner.
   */
  const flushPending = (): Promise<boolean> => flushUntilSettled(
    saveNow,
    () => draftRef.current === baseRef.current,
    () => {
      const p = problemRef.current;
      return !!p && p.kind !== 'error';
    },
  );

  /** Vor einer Aktion speichern; scheitert das, Aktion sichtbar abbrechen. */
  const guardAction = async (action: string): Promise<boolean> => {
    setGuardMessage('');
    const ok = await flushPending();
    if (!ok && mountedRef.current) {
      setGuardMessage(`${action} abgebrochen: Die letzten Änderungen konnten nicht gespeichert werden.`);
    }
    return ok;
  };

  const handleSave = () => {
    if (problemRef.current?.kind === 'error') reportProblem(null);
    autosaveFailedForRef.current = null;
    void saveNow();
  };

  /**
   * Angebot geloescht / keine Berechtigung: Speichern ist aussichtslos. Wer
   * geht, bekommt die Aenderungen lokal gesichert. Scheitert die Sicherung,
   * sagt der Banner das — ein zweiter Klick dort verlaesst trotzdem.
   */
  const leaveMissing = (force: boolean) => {
    const saved = draftRef.current === baseRef.current
      || persistBackup(draftRef.current, baseRef.current, backupRef.current, 'main') !== null;
    if (saved || force) {
      onBack();
      return;
    }
    setLeaveUnsaved(true);
  };

  const handleBack = async () => {
    if (leaving) return;
    if (problemRef.current?.kind === 'missing') {
      leaveMissing(false);
      return;
    }
    setLeaving(true);
    let ok = false;
    try {
      ok = await guardAction('Zurück');
    } finally {
      if (mountedRef.current) setLeaving(false);
    }
    if (ok && mountedRef.current) onBack();
  };

  // Konflikt: Serverstand uebernehmen. `keepMine` legt die eigene Fassung
  // vorher in die lokale Sicherung und bietet sie gleich zum Wiederherstellen an.
  const handleConflictReload = (keepMine: boolean) => {
    const p = problemRef.current;
    if (!p || p.kind !== 'conflict') return;
    let offer: QuoteDraftBackup | null = null;
    let memoryOnly = false;
    if (keepMine) {
      offer = persistBackup(draftRef.current, baseRef.current, backupRef.current, 'main');
      if (!offer && draftRef.current !== baseRef.current) {
        // localStorage schrieb nicht: die Fassung NICHT still verlieren,
        // sondern im Speicher anbieten — mit Warnung.
        offer = buildQuoteDraftBackup(draftRef.current, baseRef.current);
        memoryOnly = true;
      }
    } else {
      clearQuoteDraftBackup(p.server.id);
    }
    backupRef.current.suspended = offer !== null && !memoryOnly;
    setOfferMemoryOnly(memoryOnly);
    baseRef.current = p.server;
    draftRef.current = p.server;
    autosaveFailedForRef.current = null;
    reportProblem(null);
    setGuardMessage('');
    setDraft(p.server);
    onChange(p.server);
    setRestoreOffer(offer);
  };

  /**
   * Angebot entschieden: ab jetzt sichert die laufende Sitzung wieder in den
   * Hauptplatz. Der aktuelle Stand wird sofort dort gesichert, der
   * Zwischenplatz geleert.
   */
  const endOffer = (quoteId: string) => {
    backupRef.current.suspended = false;
    persistBackup(draftRef.current, baseRef.current, backupRef.current, 'main');
    clearQuoteDraftBackup(quoteId, undefined, 'pending');
    setRestoreOffer(null);
    setOfferMemoryOnly(false);
  };

  const handleRestoreBackup = () => {
    if (!restoreOffer) return;
    // Drei-Wege auf den AKTUELLEN Entwurf (nicht den Serverstand): seit dem
    // Oeffnen Getipptes und gerade Gespeichertes bleiben stehen. baseRef
    // bleibt, der naechste Speichervorgang diffed normal gegen ihn.
    const current = draftRef.current;
    const restored = restoreQuoteDraftBackup(current, restoreOffer, isLockedQuote(baseRef.current));
    if (restored !== current) {
      draftRef.current = restored;
      autosaveFailedForRef.current = null;
      setDraft(restored);
      setSaved(false);
    }
    endOffer(restoreOffer.quoteId);
  };

  const handleDiscardBackup = () => {
    if (!restoreOffer) return;
    clearQuoteDraftBackup(restoreOffer.quoteId);
    endOffer(restoreOffer.quoteId);
  };

  // Aktueller Stand fuer Unmount/pagehide (laufen ohne neuen Render).
  useEffect(() => { draftRef.current = draft; }, [draft]);
  const saveNowRef = useRef(saveNow);
  const saveSnapshotRef = useRef(saveSnapshot);
  useEffect(() => { saveNowRef.current = saveNow; saveSnapshotRef.current = saveSnapshot; });

  // ── Autosave (nur Entwuerfe): 2s nach der letzten Aenderung ────────────────
  // dirty = Draft-Referenz weicht vom Parent-Prop ab (jede Mutation erzeugt ein
  // neues Objekt; das Save-Echo stellt Referenzgleichheit wieder her). Nach
  // Fehler erst wieder versuchen, wenn sich der Draft erneut geaendert hat;
  // bei Konflikt/fehlendem Angebot gar nicht, bis im Banner entschieden ist.
  useEffect(() => {
    if (locked || saving || draft === quote) return;
    if (autosaveFailedForRef.current === draft) return;
    if (saveProblem && saveProblem.kind !== 'error') return;
    const t = setTimeout(() => { void saveNow(); }, 2000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, quote, saving, locked, saveProblem]);

  // Lokale Sicherung, solange etwas ungespeichert ist — hoechstens einmal je
  // BACKUP_INTERVAL_MS, der letzte Stand wird trotzdem immer geschrieben.
  useEffect(() => {
    if (draft === quote) return;
    const wait = Math.max(0, BACKUP_INTERVAL_MS - (Date.now() - lastBackupAtRef.current));
    const t = setTimeout(() => {
      lastBackupAtRef.current = Date.now();
      persistBackup(draftRef.current, baseRef.current, backupRef.current);
    }, wait);
    return () => clearTimeout(t);
  }, [draft, quote]);

  // Unmount (Seitenleiste, Ansichtswechsel): ein offener Stand wird NICHT
  // verworfen, sondern lokal gesichert und noch abgeschickt. doSave setzt
  // danach keinen State mehr (mountedRef).
  useEffect(() => {
    mountedRef.current = true;
    // Dasselbe (mutierte) Sitzungsobjekt ueber die ganze Lebensdauer.
    const backupSession = backupRef.current;
    return () => {
      mountedRef.current = false;
      const p = problemRef.current;
      if (draftRef.current !== baseRef.current) {
        const frozenDraft = draftRef.current;
        const frozenBase = baseRef.current;
        persistBackup(frozenDraft, frozenBase, backupSession);
        if (!p || p.kind === 'error') {
          // Reload-Sperre VOR dem Start halten: saveSnapshot wartet evtl. erst
          // auf ein laufendes Speichern — dazwischen darf kein Deploy-Reload
          // den eingefrorenen Stand verschlucken. saveSnapshot gibt sie im
          // finally selbst wieder frei.
          setQuoteEditorBusy(false, true);
          void saveSnapshotRef.current(frozenDraft, frozenBase);
          return;
        }
      }
      // Editor zu; ein noch laufender Speichervorgang blockiert den
      // Deploy-Reload weiter, bis doSave ihn im finally zuruecknimmt.
      setQuoteEditorBusy(false, inFlightRef.current !== null);
    };
  }, []);

  // Editor offen + Speicherstand an App melden (Deploy-Reload-Sperre).
  useEffect(() => { setQuoteEditorBusy(true, saving); }, [saving]);

  // Tab zu / Reload: Browser-Rueckfrage, solange etwas offen ist oder gerade
  // gespeichert wird; pagehide sichert lokal und schickt den Stand noch los.
  useEffect(() => {
    const pending = () => draftRef.current !== baseRef.current || inFlightRef.current !== null;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!pending()) return;
      persistBackup(draftRef.current, baseRef.current, backupRef.current);
      e.preventDefault();
      e.returnValue = '';
    };
    const onPageHide = () => {
      if (draftRef.current === baseRef.current) return;
      persistBackup(draftRef.current, baseRef.current, backupRef.current);
      const p = problemRef.current;
      if (!p || p.kind === 'error') void saveNowRef.current();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, []);

  // Versendetes/abgelaufenes Angebot zur Bearbeitung entsperren (Kunde hat
  // Änderungswunsch): Status zurueck auf Entwurf, danach erneut versenden.
  const handleReopen = async () => {
    if (!confirm('Angebot zurück auf „Entwurf" setzen, um Änderungen vorzunehmen? Es muss danach erneut versendet werden.')) return;
    setStatusBusy(true);
    try {
      if (!(await guardAction('Entsperren'))) return;
      const base = baseRef.current;
      const res = await updateQuote(base.id, { status: 'draft' }, { expectedUpdatedAt: base.updatedAt, base });
      if (!mountedRef.current || lastQuoteIdRef.current !== base.id) return;
      const next: Quote = res.merged
        ? { ...res.merged, status: 'draft' as Quote['status'] }
        : { ...base, status: 'draft' as Quote['status'], updatedAt: res.updatedAt };
      baseRef.current = next;
      draftRef.current = next;
      setDraft(next);
      onChange(next);
    } catch (e) {
      if (mountedRef.current) setGuardMessage('Entsperren fehlgeschlagen: ' + rechtFehlerOder(e));
    } finally {
      if (mountedRef.current) setStatusBusy(false);
    }
  };

  const handleReject = async () => {
    if (!canTransition(effStatus, 'rejected')) return;
    if (!confirm('Angebot als abgelehnt markieren?')) return;
    setRejecting(true);
    setStatusBusy(true);
    try {
      // Erst den offenen Stand speichern (Q4): sonst meldete das Ablehnen
      // ungespeicherte Eingaben als gespeichert an die Liste.
      if (!(await guardAction('Ablehnen'))) return;
      const base = baseRef.current;
      const { updatedAt } = await rejectQuote(base.id);
      if (!mountedRef.current || lastQuoteIdRef.current !== base.id) return;
      // Mit dem neuen Serveranker: die naechste Aktion dieser Sitzung setzt
      // sonst auf dem alten Stand auf und endet im Konflikt.
      const next: Quote = { ...base, status: 'rejected', rejectedAt: new Date().toISOString(), updatedAt };
      baseRef.current = next;
      draftRef.current = next;
      setDraft(next);
      onChange(next);
    } catch (e) {
      if (mountedRef.current) setGuardMessage('Ablehnen fehlgeschlagen: ' + rechtFehlerOder(e));
    } finally {
      if (mountedRef.current) { setRejecting(false); setStatusBusy(false); }
    }
  };

  // Versand erst oeffnen, wenn der offene Stand gespeichert ist (Q4).
  const handleOpenSend = async () => {
    setStatusBusy(true);
    try {
      if (await guardAction('Versand')) setShowSend(true);
    } finally {
      if (mountedRef.current) setStatusBusy(false);
    }
  };

  // Nach erfolgreichem Outlook-Versand: lokalen Status (sent/sentAt/followUpDate…)
  // uebernehmen, damit Editor + Liste ohne Reload umschalten. Basis ist der
  // gespeicherte Stand — vor dem Oeffnen des Dialogs wurde gespeichert.
  // `updated.updatedAt` ist der neue Serveranker aus markQuoteSent.
  const handleMarkedSent = (updated: Partial<Quote>) => {
    const next: Quote = { ...baseRef.current, ...updated };
    baseRef.current = next;
    draftRef.current = next;
    setDraft(next);
    onChange(next);
  };

  // ─── Auftragsannahme (Phase 5) ─────────────────────────────────────────────
  // Angenommenes Angebot -> Auftrag + automatisch angelegtes Projekt (LV aus den
  // Angebotspositionen). Danach lokalen Status uebernehmen und Erfolgsbanner mit
  // "Projekt oeffnen" zeigen.
  // Oeffnet nur den Dialog: bis zur Bestaetigung wird NICHTS geschrieben, damit
  // Abbrechen weder Status noch Auftragsnummer verbraucht. Vorher wird der
  // offene Stand gespeichert (Q4) — das Projekt entsteht aus diesem Stand.
  const handleAccept = async () => {
    // Kein Abbruch bei annahmeSperre: der Nachtragsweg im Dialog braucht kein
    // Anlage-Recht (siehe annahmeKnopfHinweis), der Projektweg bleibt dort gesperrt.
    if (!canTransition(effStatus, 'accepted') || accepting) return;
    setAcceptError('');
    setStatusBusy(true);
    try {
      if (await guardAction('Annahme')) setShowAcceptDialog(true);
    } finally {
      if (mountedRef.current) setStatusBusy(false);
    }
  };

  const handleAcceptConfirmed = async (values: AcceptQuoteValues, coordinator: UserProfile) => {
    // Projektanlage ohne Recht: vorab sagen, statt erst nach Claim und
    // Kompensation an der Datenbank zu scheitern (Nr. 154, projects_typ_recht).
    const verweigert = projektAnlegenVerweigert(rechte, values.projectType);
    if (verweigert) {
      setAcceptError(verweigert);
      return;
    }
    // Alles Folgende gehoert zu DIESEM Angebot. Wechselt der Editor waehrend
    // des Aufrufs, bekommt nur die Liste das Ergebnis — nie Refs/Draft des
    // dann gezeigten Angebots.
    const startedFor = lastQuoteIdRef.current;
    const stillMine = () => resultBelongsToEditor(startedFor, lastQuoteIdRef.current, mountedRef.current);
    setAccepting(true);
    // Maske UND Dialog gesperrt, bis die Antwort da ist: eine Eingabe
    // waehrenddessen wuerde vom Ergebnis ueberschrieben.
    setStatusBusy(true);
    setAcceptError('');
    let acceptBase: Quote | null = null;
    try {
      if (!(await flushPending())) {
        if (stillMine()) setAcceptError('Die letzten Änderungen am Angebot konnten nicht gespeichert werden — Annahme abgebrochen.');
        return;
      }
      if (!stillMine()) return;
      const base = baseRef.current;
      acceptBase = base;
      // Wer annimmt, steht am Auftragsteil (by/byName) — nicht der Koordinator.
      const actor = { id: userProfile?.id, name: pdfUserName || userProfile?.email || '' };
      const { orderNumber, projectRow, quote: acceptedQuote } = await acceptQuote(base, customer, coordinator, settings, { ...values, actor });
      // Die Zeile NACH der Annahme (Status, Auftragsnummer, Projektbezug und
      // der neue updated_at) — sonst endete jedes folgende Speichern (z. B.
      // Nachfassdatum) in einem selbst verursachten Konflikt.
      const next: Quote = acceptedQuote;
      if (!stillMine()) {
        onChange(next);
        return;
      }
      baseRef.current = next;
      draftRef.current = next;
      setDraft(next);
      onChange(next);
      setShowAcceptDialog(false);
      setAcceptResult({ kind: 'project', orderNumber, projectNumber: projectRow.project_number || '', projectRow });
    } catch (e) {
      // Dialog bleibt offen: die eingegebenen Werte gehen nicht verloren.
      // Supabase wirft Objekte, keine Error-Instanzen: ohne describeError stand
      // hier nur "[object Object]" und der eigentliche Grund (RLS, Constraint,
      // fehlende Admin-Rolle) war weg. Rohobjekt zusaetzlich in die Konsole.
      console.error('acceptQuote fehlgeschlagen', e);
      // Claim/Ruecksetzen/c5 haben schon geschrieben: Anker nachziehen, damit
      // ein erneuter Versuch bzw. das naechste Speichern nicht am eigenen
      // Schreiben scheitert — gebunden an das Angebot, fuer das die Annahme
      // lief (startedFor), nicht an das, das der Editor jetzt zeigt.
      const failed = acceptFailureState(e);
      if (failed && acceptBase) {
        const next = quoteAfterAcceptFailure(acceptBase, failed);
        if (!stillMine()) {
          onChange(next);
          return;
        }
        baseRef.current = next;
        draftRef.current = next;
        setDraft(next);
        onChange(next);
        // Dialog bleibt offen: dort steht die Fehlermeldung. Ein erneuter
        // Versuch auf ein angenommenes Angebot scheitert an canTransition.
      }
      if (!stillMine()) return;
      if (e instanceof QuoteConflictError) {
        // acceptQuote bindet die Annahme an updatedAt und bricht VOR dem
        // Anlegen des Projekts ab: kein Projekt, keine Auftragsnummer. Hinter
        // dem Dialog steht das Konflikt-Banner zum Neuladen.
        reportProblem({ kind: 'conflict', server: e.server });
        setAcceptError('Das Angebot wurde parallel geändert — es wurde kein Projekt angelegt. Bitte den Dialog schließen, im Hinweis „Neu laden“ wählen und erneut annehmen.');
      } else {
        setAcceptError(rechtFehlerOder(e));
      }
    } finally {
      if (mountedRef.current) { setAccepting(false); setStatusBusy(false); }
    }
  };

  // Annahme als Rahmenvertrag (Angebot ohne Endsumme): KEIN Projekt — die
  // Preise landen beim Kunden. Gleiches Geruest wie handleAcceptConfirmed:
  // erst speichern, dann auf dem gespeicherten Stand annehmen, Anker nachziehen.
  const handleAcceptFrameworkConfirmed = async (values: AcceptFrameworkValues) => {
    const startedFor = lastQuoteIdRef.current;
    const stillMine = () => resultBelongsToEditor(startedFor, lastQuoteIdRef.current, mountedRef.current);
    setAccepting(true);
    setStatusBusy(true);
    setAcceptError('');
    let acceptBase: Quote | null = null;
    try {
      if (!(await flushPending())) {
        if (stillMine()) setAcceptError('Die letzten Änderungen am Angebot konnten nicht gespeichert werden — Annahme abgebrochen.');
        return;
      }
      if (!stillMine()) return;
      const base = baseRef.current;
      acceptBase = base;
      // Wer annimmt, wird am Vertrag vermerkt.
      const actor = { id: userProfile?.id, name: pdfUserName || userProfile?.email || '' };
      const result = await acceptQuoteAsFrameworkContract(base, customer, { ...values, actor });
      // Der Vertrag liegt beim Kunden — die Kundenliste muss ihn kennen, auch
      // wenn der Editor inzwischen ein anderes Angebot zeigt (Projekt-Assistent
      // und neue Angebote lesen ihn dort).
      onCustomerChange?.(result.customer);
      const next: Quote = result.quote;
      if (!stillMine()) {
        onChange(next);
        return;
      }
      baseRef.current = next;
      draftRef.current = next;
      setDraft(next);
      onChange(next);
      setShowAcceptDialog(false);
      setAcceptResult({ kind: 'framework', orderNumber: result.orderNumber, contractNumber: result.contract.number });
    } catch (e) {
      // Dialog bleibt offen, Eingaben bleiben stehen (wie bei der Projektannahme).
      console.error('acceptQuoteAsFrameworkContract fehlgeschlagen', e);
      const failed = acceptFailureState(e);
      // Steht der Vertrag schon beim Kunden, muss die Kundenliste ihn kennen —
      // daran erkennt der Editor die Annahme und bietet „Bezug nachtragen" an.
      if (failed?.customer) onCustomerChange?.(failed.customer);
      if (failed && acceptBase) {
        const next = quoteAfterAcceptFailure(acceptBase, failed);
        if (!stillMine()) {
          onChange(next);
          return;
        }
        baseRef.current = next;
        draftRef.current = next;
        setDraft(next);
        onChange(next);
      }
      if (!stillMine()) return;
      if (e instanceof QuoteConflictError) {
        // Abbruch VOR jedem Schreiben: kein Vertrag, keine Auftragsnummer.
        reportProblem({ kind: 'conflict', server: e.server });
        setAcceptError('Das Angebot wurde parallel geändert — es wurde nichts hinterlegt. Bitte den Dialog schließen, im Hinweis „Neu laden“ wählen und erneut annehmen.');
      } else {
        setAcceptError(rechtFehlerOder(e));
      }
    } finally {
      if (mountedRef.current) { setAccepting(false); setStatusBusy(false); }
    }
  };

  // Annahme als NACHTRAG zu einem bestehenden Projekt: kein neues Projekt —
  // die Positionen gehen ins LV des gewaehlten Projekts. Gleiches Geruest wie
  // handleAcceptConfirmed. Steht der Auftrag am Angebot, scheiterte aber das
  // Eintragen ins Projekt, kommt `applied: false` — dann „Erneut eintragen"
  // im Beauftragungsstand.
  const handleAcceptSupplementConfirmed = async (values: SupplementFormValues) => {
    const startedFor = lastQuoteIdRef.current;
    const stillMine = () => resultBelongsToEditor(startedFor, lastQuoteIdRef.current, mountedRef.current);
    setAccepting(true);
    setStatusBusy(true);
    setAcceptError('');
    let acceptBase: Quote | null = null;
    try {
      if (!(await flushPending())) {
        if (stillMine()) setAcceptError('Die letzten Änderungen am Angebot konnten nicht gespeichert werden — Annahme abgebrochen.');
        return;
      }
      if (!stillMine()) return;
      const base = baseRef.current;
      acceptBase = base;
      // Wer annimmt, steht am Auftragsteil (by/byName).
      const actor = { id: userProfile?.id, name: pdfUserName || userProfile?.email || '' };
      const result = await acceptQuoteAsSupplement(base, { ...values, actor });
      const next: Quote = result.quote;
      if (!stillMine()) {
        onChange(next);
        return;
      }
      baseRef.current = next;
      draftRef.current = next;
      setDraft(next);
      onChange(next);
      setShowAcceptDialog(false);
      setAcceptResult({ kind: 'supplement', orderNumber: result.orderNumber, projectId: result.projectId, applied: result.applied });
      if (!result.applied) {
        setTrancheNotice({
          tone: 'warn',
          text: `Der Nachtrag ist am Angebot erfasst, steht aber noch NICHT im Projekt-LV: ${result.applyError || 'unbekannter Fehler'}. Bitte im Beauftragungsstand „Erneut eintragen“.`,
        });
      }
    } catch (e) {
      // Dialog bleibt offen, Eingaben bleiben stehen (wie bei der Projektannahme).
      console.error('acceptQuoteAsSupplement fehlgeschlagen', e);
      const failed = acceptFailureState(e);
      if (failed && acceptBase) {
        const next = quoteAfterAcceptFailure(acceptBase, failed);
        if (!stillMine()) {
          onChange(next);
          return;
        }
        baseRef.current = next;
        draftRef.current = next;
        setDraft(next);
        onChange(next);
      }
      if (!stillMine()) return;
      if (e instanceof QuoteConflictError) {
        // Abbruch VOR jedem Schreiben: nichts im Projekt, keine Auftragsnummer.
        reportProblem({ kind: 'conflict', server: e.server });
        setAcceptError('Das Angebot wurde parallel geändert — es wurde nichts eingetragen. Bitte den Dialog schließen, im Hinweis „Neu laden“ wählen und erneut annehmen.');
      } else {
        setAcceptError(rechtFehlerOder(e));
      }
    } finally {
      if (mountedRef.current) { setAccepting(false); setStatusBusy(false); }
    }
  };

  // Reparatur nach einer abgebrochenen Rahmenvertrags-Annahme: der Vertrag
  // steht beim Kunden, am Angebot fehlen Auftragsnummer/Vertragsbezug.
  const [linkingFramework, setLinkingFramework] = useState(false);
  const handleLinkFramework = async (contract: FrameworkContract) => {
    const startedFor = lastQuoteIdRef.current;
    const stillMine = () => resultBelongsToEditor(startedFor, lastQuoteIdRef.current, mountedRef.current);
    setLinkingFramework(true);
    setTrancheNotice(null);
    try {
      if (!(await flushPending())) {
        if (stillMine()) setTrancheNotice({ tone: 'warn', text: 'Die letzten Änderungen am Angebot konnten nicht gespeichert werden — abgebrochen.' });
        return;
      }
      if (!stillMine()) return;
      const next = await linkQuoteToFrameworkContract(baseRef.current.id, contract);
      if (!stillMine()) { onChange(next); return; }
      adoptServerQuote(next);
      setTrancheNotice({ tone: 'ok', text: `Bezug zu Rahmenvertrag ${contract.number} nachgetragen.` });
    } catch (e) {
      if (stillMine()) setTrancheNotice({ tone: 'warn', text: `Bezug konnte nicht nachgetragen werden: ${rechtFehlerOder(e)}` });
    } finally {
      if (mountedRef.current) setLinkingFramework(false);
    }
  };

  // ─── Teilbeauftragung: Rest beauftragen / Rest entfaellt / Erneut eintragen ─
  // Geschrieben wird in quoteOrderService (verankert auf updated_at). Danach
  // uebernimmt der Editor die Zeile vom Server als neuen Anker — sonst endete
  // das naechste Speichern (Nachfassdatum) in einem selbst verursachten Konflikt.
  const adoptServerQuote = (next: Quote) => {
    baseRef.current = next;
    draftRef.current = next;
    setDraft(next);
    onChange(next);
  };

  const handleTrancheConfirm = async (result: TrancheDialogResult) => {
    if (!trancheDialog) return;
    const startedFor = lastQuoteIdRef.current;
    const stillMine = () => resultBelongsToEditor(startedFor, lastQuoteIdRef.current, mountedRef.current);
    const actorName = pdfUserName || userProfile?.email || '';
    setTrancheBusy(true);
    setTrancheError('');
    try {
      if (!(await flushPending())) {
        if (stillMine()) setTrancheError('Die letzten Änderungen am Angebot konnten nicht gespeichert werden — abgebrochen.');
        return;
      }
      if (!stillMine()) return;
      if (trancheDialog.mode === 'retry') {
        const next = await applyTrancheToProject(baseRef.current, trancheDialog.trancheId || '', {
          actorName,
          flatDecisions: result.flatDecisions,
          endDate: result.endDate,
          orderValueCents: result.orderValueCents,
        });
        if (!stillMine()) { onChange(next); return; }
        adoptServerQuote(next);
        setTrancheDialog(null);
        setTrancheNotice({ tone: 'ok', text: 'Auftragsteil ins Projekt-LV eingetragen.' });
        return;
      }
      const res = await recordQuoteTranche(baseRef.current, {
        kind: trancheDialog.mode,
        lines: result.lines,
        orderDate: result.orderDate,
        customerRef: result.customerRef,
        note: result.note,
        actor: { id: userProfile?.id, name: actorName },
        flatDecisions: result.flatDecisions,
        endDate: result.endDate,
        orderValueCents: result.orderValueCents,
      });
      if (!stillMine()) { onChange(res.quote); return; }
      adoptServerQuote(res.quote);
      setTrancheDialog(null);
      if (res.tranche.kind === 'decline') {
        setTrancheNotice({ tone: 'ok', text: `Teil ${res.tranche.seq}: Rest entfällt — erfasst.` });
      } else if (res.applied) {
        setTrancheNotice({ tone: 'ok', text: `Teil ${res.tranche.seq} beauftragt und ins Projekt-LV eingetragen.` });
      } else {
        setTrancheNotice({
          tone: 'warn',
          text: `Teil ${res.tranche.seq} ist am Angebot erfasst, steht aber noch NICHT im Projekt-LV: ${res.applyError || 'unbekannter Fehler'}. Bitte „Erneut eintragen“.`,
        });
      }
    } catch (e) {
      console.error('Auftragsteil fehlgeschlagen', e);
      if (!stillMine()) return;
      if (e instanceof QuoteConflictError) {
        reportProblem({ kind: 'conflict', server: e.server });
        setTrancheError('Das Angebot wurde parallel geändert — es wurde nichts erfasst. Bitte den Dialog schließen, im Hinweis „Neu laden“ wählen und erneut versuchen.');
      } else {
        setTrancheError(rechtFehlerOder(e));
      }
    } finally {
      if (mountedRef.current) setTrancheBusy(false);
    }
  };

  // Als Rahmenvertrag angenommen: Nummer direkt aus dem Ergebnis, bei spaeterem
  // Oeffnen aus dem Kundenstamm (fehlt der Vertrag dort, bleibt sie weg).
  // Brach eine Annahme NACH dem Anlegen des Vertrags ab, fehlt am Angebot der
  // Bezug — der Vertrag beim Kunden nennt das Angebot aber (source.quoteId).
  // Dann gilt es trotzdem als Rahmenvertrag, und der Bezug laesst sich nachtragen.
  const contractOfQuote = frameworkContracts.find((c) => c.id === draft.details?.frameworkContractId)
    ?? (isQuoteOrdered(draft.status) ? frameworkContracts.find((c) => c.source?.quoteId === draft.id) : undefined);
  const frameworkAccepted = isFrameworkAcceptedQuote(draft) || !!contractOfQuote;
  const frameworkLinkMissing = !!contractOfQuote && !draft.details?.frameworkContractId && draft.status === 'accepted';
  const acceptedFrameworkNumber = acceptResult?.kind === 'framework'
    ? acceptResult.contractNumber
    : contractOfQuote?.number;

  // ─── Nachtragsangebot ──────────────────────────────────────────────────────
  // „Nachtrag erstellen": aus einem beauftragten Angebot mit Projekt (nicht
  // Rahmenvertrag) ein NEUES, leeres Nachtragsangebot zum selben Projekt. Die
  // Liste nimmt es auf und oeffnet es (onCreatedQuote) — ohne die Prop keine Aktion.
  const canCreateSupplement = !!onCreatedQuote && isQuoteOrdered(draft.status) && !!draft.projectId && !frameworkAccepted;
  // Teilbeauftragt: „Rest beauftragen" und „Nachtrag" werden leicht verwechselt.
  const createSupplementTitle = draft.status === 'partial'
    ? 'Rest beauftragen = schon angebotene Mengen; Nachtrag = zusätzlicher Umfang'
    : 'Neues Nachtragsangebot zu diesem Projekt anlegen';
  const handleCreateSupplement = async () => {
    if (!onCreatedQuote || !draft.projectId || creatingSupplement) return;
    setCreatingSupplement(true);
    setTrancheNotice(null);
    try {
      // Vorgaben wie beim Anlegen in der Liste (QuotesView.handleCreate).
      const validityDays = quoteConfig?.defaultValidityDays ?? 30;
      const created = await createSupplementQuote({
        projectId: draft.projectId,
        // Aus einem Nachtrag heraus sucht der Dienst das Hauptangebot selbst.
        mainQuote: isSupplementQuote(draft) ? undefined : draft,
        validUntil: computeFollowUpDate(new Date().toISOString(), validityDays),
        taxRate: quoteConfig?.defaultTaxRate ?? 19,
        paymentTerms: quoteConfig?.defaultPaymentTerms || undefined,
      });
      // Angelegt ist angelegt: die Liste muss es kennen, auch wenn der Editor
      // inzwischen ein anderes Angebot zeigt.
      onCreatedQuote(created);
    } catch (e) {
      if (mountedRef.current) setTrancheNotice({ tone: 'warn', text: `Nachtrag konnte nicht angelegt werden: ${rechtFehlerOder(e)}` });
    } finally {
      if (mountedRef.current) setCreatingSupplement(false);
    }
  };
  // „Projektposition übernehmen": Positionen mit Projektpreis und
  // targetServiceId — derselbe Weg wie eine Katalogposition (addCatalogItem),
  // aber OHNE Kundenpreise: der Projektpreis entscheidet, ob die Menge der
  // Projektposition steigen kann.
  const addProjectItems = (items: QuoteItem[]) => {
    if (items.length > 0) set('items', [...(draft.items || []), ...items]);
    setPositionPicker(null);
  };

  // Maske links, Live-Vorschau rechts — wie im Rechnungseditor. Unter lg stapeln
  // beide untereinander, die Vorschau steht dann unter dem Formular.
  return (
    <div data-glass-view="quotes" className="flex h-full flex-col">
      {/* Kopfleiste: gleiche Anordnung wie InvoiceEditor — oben, immer
          sichtbar (nicht Teil des scrollenden Bereichs unten), damit die
          Aktionen nicht erst nach Scrollen erreichbar sind. */}
      <div data-glass="kopf" className="flex flex-wrap items-center gap-3 border-b border-slate-200 bg-white px-4 py-3">
        <button
          onClick={() => { void handleBack(); }}
          disabled={leaving}
          className="text-slate-500 hover:text-slate-800 disabled:opacity-60"
          title="Zurück zur Liste — offene Änderungen werden vorher gespeichert"
        >
          {leaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowLeft className="w-5 h-5" />}
        </button>
        <div className="min-w-0">
          <h1 className="truncate font-semibold text-slate-800">{draft.quoteNumber}</h1>
          {/* Derselbe Kundenstand wie die Vorschau daneben: beim versendeten
              Angebot der Snapshot, sonst der lebende Kunde. */}
          <div className="flex items-center gap-1.5">
            <p className="truncate text-xs text-slate-500">{customerForPdf.name}</p>
            {/* Eine Abweichung, die man nicht sieht, ist eine Falle: das
                Angebot druckt einen anderen Empfaenger als der Kundenstamm. */}
            {overrideActive && (
              <span
                title={`Abweichende Kundenangaben nur auf diesem Angebot: ${customerOverrideSummary(customerOverride)}. Der Kundenstamm bleibt unverändert.`}
                className="shrink-0 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700"
              >
                Abweichend
              </span>
            )}
            {canEditCustomer && (
              <button
                type="button"
                onClick={() => setCustomerEditOpen(true)}
                title="Kundendaten bearbeiten (Anschrift, Name, Kontakt) — wahlweise im Kundenstamm oder nur für dieses Angebot"
                className="shrink-0 rounded p-0.5 text-slate-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Zustand des Angebots — bisher nur NEGATIV ablesbar (daran, welche
            Knoepfe fehlen). Status, Restlaufzeit und Speicherstand stehen
            jetzt als Text da. */}
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-bold ${QUOTE_STATUS_META[effStatus].cls}`}>
            {QUOTE_STATUS_META[effStatus].label}
          </span>
          {validity && (
            <span className={`text-xs ${
              validity.tone === 'danger' ? 'text-red-600 font-semibold'
                : validity.tone === 'warn' ? 'text-amber-600 font-semibold'
                  : 'text-slate-500'
            }`}>
              {validity.text}
            </span>
          )}
          {!locked && completeness.withoutPrice > 0 && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200"
              title="Aus einer Ausschreibung übernommene LV-Zeilen kommen ohne Preis. So viele sind noch offen."
            >
              <AlertTriangle className="w-3 h-3" />{completeness.withoutPrice} ohne Preis
            </span>
          )}
          {/* Speicherstand. Der Autosave laeuft NUR im Entwurf (siehe Effekt
              oben: bei `locked` steigt er sofort aus). Im gesperrten Angebot
              ist das einzige editierbare Feld "Nachfassen am" — dort muss der
              Nutzer sehen, dass er selbst speichern muss (Knopf rechts; Zurueck
              und die Statusaktionen speichern vorher ebenfalls). Ein
              gescheitertes Speichern steht hier als Knopf, nicht als Popup. */}
          {saveProblem ? (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700 hover:bg-red-100 disabled:opacity-60"
              title={saveProblem.kind === 'error' ? `Speichern fehlgeschlagen: ${saveProblem.message}` : 'Speichern fehlgeschlagen — Details im Hinweis unter der Kopfleiste'}
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <AlertTriangle className="w-3 h-3" />}
              {saving ? 'Speichert…' : 'Nicht gespeichert – erneut versuchen'}
            </button>
          ) : (
            <span
              className={`text-xs ${locked && dirty && !saving ? 'font-semibold text-amber-600' : saved && !dirty ? 'text-emerald-600' : 'text-slate-400'}`}
              title={locked ? 'Gesperrtes Angebot — Änderungen müssen von Hand gespeichert werden' : 'Änderungen werden automatisch gespeichert'}
            >
              {saving ? 'Speichert…'
                : dirty ? (locked ? 'Nicht gespeichert' : 'Speichert…')
                  : 'Gespeichert'}
            </span>
          )}
        </div>

        {/* Aktionen. Nur was im aktuellen Status wirklich geht — ein dauerhaft
            ausgegrauter Knopf ist kein Hinweis, sondern Ballast. Alles, was
            nicht der naechste Schritt ist, liegt im Ueberlaufmenue. */}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {(effStatus === 'sent' || effStatus === 'expired') && (
            <Button
              variant="secondary"
              onClick={handleReopen}
              icon={<Pencil className="w-4 h-4" />}
              title="Zur Bearbeitung entsperren — Status zurück auf Entwurf, danach erneut senden"
            >
              Bearbeiten
            </Button>
          )}
          {canTransition(effStatus, 'sent') && (
            <Button
              variant="primary"
              onClick={() => { void handleOpenSend(); }}
              icon={<Mail className="w-4 h-4" />}
              title="Per Outlook senden oder als PDF für das Vergabeportal ausgeben"
            >
              Versenden
            </Button>
          )}
          {locked && dirty && (
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={saving}
              icon={saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              title="Im gesperrten Angebot speichert nichts automatisch"
            >
              Speichern
            </Button>
          )}
          {/* Im Entwurf ist der Regelweg der Versand. Ein nie versendetes
              Angebot direkt anzunehmen ist der Ausnahmefall und gehoert nicht
              als zweiter, farbstaerkerer Primaerknopf daneben — ab 'sent' ist
              es umgekehrt. */}
          {canTransition(effStatus, 'accepted') && effStatus !== 'draft' && (
            <Button
              variant="primary"
              onClick={handleAccept}
              disabled={accepting}
              className="!bg-emerald-600 hover:!bg-emerald-700 border-b-2 border-emerald-700"
              icon={accepting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              title={annahmeKnopfHinweis ?? (supplementRef ? 'Angebot annehmen und als Nachtrag ins Projekt eintragen' : 'Angebot annehmen und Projekt anlegen')}
            >
              {accepting ? 'Wird angelegt…' : 'Angenommen → Auftrag'}
            </Button>
          )}

          <div className="relative">
            <button
              type="button"
              onClick={() => setMoreOpen((o) => !o)}
              className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-all"
              title="Weitere Aktionen"
            >
              <MoreHorizontal className="w-5 h-5" />
            </button>
            {moreOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMoreOpen(false)} />
                <div className="absolute right-0 mt-1 z-20 w-64 bg-white border border-slate-200 rounded-xl shadow-lg py-1">
                  {canTransition(effStatus, 'accepted') && effStatus === 'draft' && (
                    <button
                      type="button"
                      onClick={() => { setMoreOpen(false); void handleAccept(); }}
                      disabled={accepting}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-emerald-700 hover:bg-emerald-50 transition-all disabled:opacity-50"
                      title="Ohne Versand direkt annehmen und Projekt anlegen"
                    >
                      <CheckCircle2 className="w-4 h-4" />Angenommen → Auftrag
                    </button>
                  )}
                  {canTransition(effStatus, 'rejected') && (
                    <button
                      type="button"
                      onClick={() => { setMoreOpen(false); void handleReject(); }}
                      disabled={rejecting}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-all disabled:opacity-50"
                    >
                      <XCircle className="w-4 h-4" />Angebot ablehnen
                    </button>
                  )}
                  {canCreateSupplement && (
                    <button
                      type="button"
                      onClick={() => { setMoreOpen(false); void handleCreateSupplement(); }}
                      disabled={creatingSupplement}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-all disabled:opacity-50"
                      title={createSupplementTitle}
                    >
                      <FilePlus className="w-4 h-4" />{creatingSupplement ? 'Nachtrag wird angelegt…' : 'Nachtrag erstellen'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => { setMoreOpen(false); handleSaveTemplate(); }}
                    disabled={savingTemplate}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-all disabled:opacity-50"
                    title="Positionen und Texte als wiederverwendbare Vorlage sichern"
                  >
                    <Bookmark className="w-4 h-4" />Als Vorlage speichern
                  </button>
                  <button
                    type="button"
                    onClick={() => { setMoreOpen(false); handleSave(); }}
                    disabled={saving}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-all disabled:opacity-50"
                    title="Das Angebot wird ohnehin automatisch gespeichert — hier sofort"
                  >
                    <Save className="w-4 h-4" />Jetzt speichern
                  </button>
                  {/* Loeschen nur im Entwurf — dieselbe Regel wie in der Liste:
                      ein versendetes oder angenommenes Angebot ist ein Beleg
                      und verschwindet nicht mehr. Unten und abgesetzt, damit
                      es niemand auf dem Weg zu "Jetzt speichern" trifft. */}
                  {onDelete && effStatus === 'draft' && (
                    <>
                      <div className="my-1 border-t border-slate-100" />
                      <button
                        type="button"
                        onClick={() => { setMoreOpen(false); onDelete(); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-all"
                        title="Entwurf endgültig löschen — es folgt eine Rückfrage"
                      >
                        <Trash2 className="w-4 h-4" />Angebot löschen
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Speicher-Hinweise: inline statt Browser-Popup, damit nichts blockiert
          und der Stand sichtbar bleibt, bis er geklaert ist. */}
      {(restoreOffer || saveProblem || guardMessage) && (
        <div data-glass="hinweise" className="space-y-2 border-b border-slate-200 bg-white px-4 py-2">
          {guardMessage && (
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span className="flex-1 min-w-[12rem]">{guardMessage}</span>
              <button type="button" onClick={() => setGuardMessage('')} className="rounded p-0.5 text-red-500 hover:bg-red-100" title="Hinweis schließen">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {saveProblem?.kind === 'conflict' && (
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span className="flex-1 min-w-[12rem] font-semibold">Das Angebot wurde parallel geändert.</span>
              <Button variant="secondary" onClick={() => handleConflictReload(false)} title="Serverstand übernehmen — die eigenen, nicht gespeicherten Änderungen werden verworfen">
                Neu laden (meine Änderungen gehen verloren)
              </Button>
              <Button variant="primary" onClick={() => handleConflictReload(true)} title="Eigene Fassung lokal sichern, Serverstand laden und die Fassung zum Wiederherstellen anbieten">
                Meine Fassung sichern
              </Button>
            </div>
          )}
          {saveProblem?.kind === 'missing' && (
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span className="flex-1 min-w-[12rem]">
                <span className="font-semibold">Angebot existiert nicht mehr oder keine Berechtigung.</span>{' '}
                {leaveUnsaved
                  ? 'Die lokale Sicherung ist fehlgeschlagen — beim Schließen gehen die Änderungen verloren.'
                  : saveProblem.backedUp ? 'Die Änderungen sind lokal in diesem Browser gesichert.' : 'Die Änderungen konnten nicht lokal gesichert werden.'}
              </span>
              {leaveUnsaved ? (
                <Button variant="secondary" onClick={() => leaveMissing(true)}>Trotzdem schließen</Button>
              ) : (
                <Button variant="secondary" onClick={() => leaveMissing(false)}>Schließen (Änderungen lokal gesichert)</Button>
              )}
            </div>
          )}
          {saveProblem?.kind === 'error' && (
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span className="flex-1 min-w-[12rem]">
                <span className="font-semibold">Speichern fehlgeschlagen:</span> {saveProblem.message}{' '}
                {saveProblem.backedUp ? 'Die Änderungen sind lokal in diesem Browser gesichert.' : 'Eine lokale Sicherung war nicht möglich.'}
              </span>
              <Button variant="secondary" onClick={handleSave} disabled={saving} icon={saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}>
                Erneut versuchen
              </Button>
            </div>
          )}
          {restoreOffer && (backupRestoreMode(quote, restoreOffer, locked) === 'blocked' ? (
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <Lock className="w-4 h-4 shrink-0" />
              <span className="flex-1 min-w-[12rem]">
                <span className="font-semibold">Änderungen konnten wegen Sperre nicht übernommen werden</span>{' '}
                (nicht gespeichert am {fmtBackupTime(restoreOffer.savedAt)}). Zum Übernehmen das Angebot über „Bearbeiten“ entsperren.
              </span>
              <Button variant="secondary" onClick={handleDiscardBackup}>Verwerfen</Button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
              <RotateCcw className="w-4 h-4 shrink-0" />
              <span className="flex-1 min-w-[12rem]">
                Nicht gespeicherte Änderungen vom {fmtBackupTime(restoreOffer.savedAt)}
                {backupBaseIsStale(restoreOffer, quote.updatedAt) ? ' — das Angebot wurde seitdem geändert; wiederhergestellt werden nur die eigenen Änderungen' : ''}
                {offerMemoryOnly && (
                  <span className="block font-semibold text-amber-700">
                    Lokal nicht speicherbar — diese Fassung wird nur bis zum Schließen der Seite gehalten.
                  </span>
                )}
              </span>
              <Button variant="primary" onClick={handleRestoreBackup}>Wiederherstellen</Button>
              <Button variant="secondary" onClick={handleDiscardBackup}>Verwerfen</Button>
            </div>
          ))}
        </div>
      )}

    {/* Zweispaltig nur mit angedockter Vorschau. Abgedockt gehoert die
        Breite der Maske — das Positionsraster braucht sie.
        CONTAINER-Query statt `lg:`: Tailwind-Breakpoints messen das FENSTER,
        die Maske steht aber neben der App-Navigation und ist entsprechend
        schmaler. Bei 1024 px Fenster blieben je Spalte rund 480 px — zu wenig
        fuer die Eingabebloecke, ihr Ueberhang lief unter die Vorschau. Geteilt
        wird deshalb erst ab 1400 px TATSAECHLICHER Maskenbreite. Der aeussere
        Rahmen traegt `@container` und die Rollbox; ein Element kann seine
        eigene Groesse nicht abfragen.
        Spalten NICHT 50/50: die Maske bekommt bis 58rem (Positionsraster
        804 px + Kartenraender = 904 px, plus Luft), die Vorschau den Rest.
        Halbe-halbe gab der Maske in einem 1600er-Fenster neben der
        Navigation nur ~760 px — das Raster lief unter die Vorschau. */}
    {/* Waehrend eines Statuswechsels gesperrt (statusBusy): ein Tastendruck
        zwischen letztem Speichern und Statuswechsel ginge sonst verloren. */}
    <fieldset disabled={statusBusy} className="m-0 flex min-h-0 min-w-0 flex-1 flex-col border-0 p-0">
    <div className="@container flex-1 overflow-auto">
    <div className={`grid grid-cols-1 gap-4 p-4 md:p-6 ${previewPanelVisible ? '@min-[1400px]:grid-cols-[minmax(0,58rem)_minmax(0,1fr)]' : ''}`}>
    {/* overflow-x-clip als Riegel: was hier trotz allem zu breit wird, endet
        an der Spaltenkante statt unter der Vorschau. `clip` (nicht `hidden`)
        laesst die senkrechte Achse sichtbar — Aufklapper bleiben sichtbar und
        die klebende Fusszeile haengt weiter an der aeusseren Rollbox.
        `w-full` ist Pflicht: ein Grid-Item mit `mx-auto` wird NICHT auf die
        Spalte gestreckt, sondern fit-content — und fit-content ist mindestens
        der Min-Content (904 px). Ohne `w-full` war die Karte breiter als ihre
        Spalte, und weil die Vorschau darueber klebt, fehlten rechts
        Gesamtbetrag, Zeilenknoepfe und Vorlage-Knopf. Mit `w-full` ist die
        Breite definit, `max-w-5xl` deckelt, `mx-auto` zentriert nur noch. */}
    <div className="space-y-6 w-full max-w-5xl mx-auto min-w-0 overflow-x-clip animate-fade-in pb-20 [&_input]:min-w-0 [&_select]:min-w-0 [&_textarea]:min-w-0">
      {/* Sperr-Hinweis bei versendetem Angebot */}
      {effStatus === 'sent' && (
        <div className="flex flex-wrap items-center gap-3 p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          <Lock className="w-4 h-4 shrink-0" />
          <span className="flex-1 min-w-[14rem]">
            {isPortalSubmission(draft.sentTo) ? 'Im Vergabeportal abgegeben' : 'Versendet'} – Inhalt gesperrt. Nur Status und Nachfassen änderbar.
          </span>
          {/* Das Nachfassdatum ist das EINZIGE Feld, das hier noch etwas tut.
              Es lag in der Kopfdaten-Karte, die bei einem versendeten Angebot
              immer eingeklappt startet — der Hinweis nannte es also, ohne dass
              man es erreichen konnte. */}
          <label className="flex items-center gap-2 shrink-0">
            <span className="text-xs font-bold uppercase tracking-widest">Nachfassen am</span>
            <input
              type="date"
              value={draft.followUpDate || ''}
              onChange={(e) => set('followUpDate', e.target.value)}
              className="px-2.5 py-1.5 bg-white border border-amber-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-amber-400/30"
            />
          </label>
        </div>
      )}

      {/* Sperr-Hinweis bei abgelaufenem Angebot */}
      {effStatus === 'expired' && (
        <div className="flex items-start gap-3 p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          <Lock className="w-4 h-4 shrink-0 mt-0.5" />
          <span>Abgelaufen – Inhalt gesperrt. Das Angebot kann nicht mehr angenommen oder abgelehnt werden.</span>
        </div>
      )}

      {/* Nachtragsangebot: Bezug auf das bestehende Projekt; solange
          bearbeitbar, lassen sich Projektpositionen uebernehmen (deren
          Vertragsmenge steigt dann bei der Annahme). */}
      {supplementRef && (
        <div className="flex flex-wrap items-center gap-3 p-4 rounded-2xl bg-teal-50 border border-teal-200 text-teal-800 text-sm">
          <Layers className="w-4 h-4 shrink-0" />
          <span className="flex-1 min-w-[14rem] font-semibold">{supplementLabel(supplementRef)}</span>
          {!locked && (
            <Button
              variant="secondary"
              onClick={() => setPositionPicker({ sectionId: sections[0]?.id })}
              icon={<ListPlus className="w-4 h-4" />}
              className="h-9 text-xs shrink-0"
              title="Position aus dem Projekt-LV übernehmen — bei der Annahme steigt deren Vertragsmenge"
            >
              Projektposition übernehmen
            </Button>
          )}
        </div>
      )}

      {/* Auftrags-/Projekt-Hinweis bei angenommenem bzw. teilbeauftragtem Angebot */}
      {isQuoteOrdered(draft.status) && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
          <CheckCircle2 className="w-5 h-5 shrink-0" />
          <span className="flex-1">
            {frameworkAccepted
              ? `Angenommen als Rahmenvertrag – Auftrag ${(acceptResult?.orderNumber ?? draft.orderNumber) || '—'}. Die Preise sind beim Kunden hinterlegt${acceptedFrameworkNumber ? ` (Rahmenvertrag ${acceptedFrameworkNumber})` : ''}. Es wurde kein Projekt angelegt — ein Projekt (z. B. SiFa-Betreuung) entsteht über „Neues Projekt“ mit diesem Rahmenvertrag als LV-Quelle.`
              : acceptResult?.kind === 'project'
                ? `Auftrag ${acceptResult.orderNumber} – Projekt ${acceptResult.projectNumber} angelegt${draft.status === 'partial' ? ' (Teilbeauftragung)' : ''}.`
                : acceptResult?.kind === 'supplement'
                  ? `Auftrag ${acceptResult.orderNumber} – Nachtrag ${acceptResult.applied ? 'in' : 'zu'} Projekt${draft.details?.nachtragZu?.projectNumber ? ` ${draft.details.nachtragZu.projectNumber}` : ''} ${acceptResult.applied ? 'eingetragen' : 'erfasst, steht aber noch nicht im Projekt-LV'}${draft.status === 'partial' ? ' (Teilbeauftragung)' : ''}.`
                : draft.status === 'partial'
                  ? `Teilbeauftragt – Auftrag ${draft.orderNumber || '—'}. Der Rest ist offen.`
                  : `Angenommen – Auftrag ${draft.orderNumber || '—'}.`}
          </span>
          {/* Teilbeauftragt: der Rest wird nachgefasst wie ein versendetes Angebot. */}
          {draft.status === 'partial' && (
            <label className="flex items-center gap-2 shrink-0">
              <span className="text-xs font-bold uppercase tracking-widest">Rest nachfassen am</span>
              <input
                type="date"
                value={draft.followUpDate || ''}
                onChange={(e) => set('followUpDate', e.target.value)}
                className="px-2.5 py-1.5 bg-white border border-emerald-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-emerald-400/30"
              />
            </label>
          )}
          {/* Rahmenvertrag: es gibt kein Projekt, also auch nichts zu oeffnen —
              hoechstens den abgebrochenen Angebotsbezug nachzutragen. */}
          {frameworkAccepted ? (
            frameworkLinkMissing && contractOfQuote ? (
              <Button variant="primary" onClick={() => handleLinkFramework(contractOfQuote)} disabled={linkingFramework} className="h-9 text-xs shrink-0">
                {linkingFramework ? 'Wird nachgetragen…' : 'Bezug nachtragen'}
              </Button>
            ) : null
          ) : acceptResult?.kind === 'project' ? (
            // Direkt nach der Annahme liegt die volle projectRow vor -> Sprung ins Projekt.
            <Button variant="primary" onClick={() => onOpenProject?.(acceptResult.projectRow)} icon={<FolderOpen className="w-4 h-4" />} className="h-9 text-xs shrink-0">
              Projekt öffnen
            </Button>
          ) : acceptResult?.kind === 'supplement' && onOpenProject ? (
            // Nachtrag: das bestehende Projekt, frisch nach id geladen.
            <Button variant="primary" onClick={() => onOpenProject({ id: acceptResult.projectId })} icon={<FolderOpen className="w-4 h-4" />} className="h-9 text-xs shrink-0">
              Projekt öffnen
            </Button>
          ) : draft.projectId && onOpenProject ? (
            // Zuvor angenommenes Angebot: handleOpenProject laedt frisch nach id.
            <Button variant="primary" onClick={() => onOpenProject({ id: draft.projectId })} icon={<FolderOpen className="w-4 h-4" />} className="h-9 text-xs shrink-0">
              Projekt öffnen
            </Button>
          ) : null}
          {canCreateSupplement && (
            <Button
              variant="secondary"
              onClick={() => { void handleCreateSupplement(); }}
              disabled={creatingSupplement}
              icon={creatingSupplement ? <Loader2 className="w-4 h-4 animate-spin" /> : <FilePlus className="w-4 h-4" />}
              className="h-9 text-xs shrink-0"
              title={createSupplementTitle}
            >
              Nachtrag erstellen
            </Button>
          )}
        </div>
      )}

      {trancheNotice && (
        <div className={`flex items-start gap-3 p-4 rounded-2xl border text-sm ${trancheNotice.tone === 'ok' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
          {trancheNotice.tone === 'ok' ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />}
          <span className="flex-1">{trancheNotice.text}</span>
          <button type="button" onClick={() => setTrancheNotice(null)} className="text-xs font-bold opacity-70 hover:opacity-100">OK</button>
        </div>
      )}

      {/* Beauftragungsstand: Teile, Stand je Position, Rest beauftragen.
          Nicht beim Rahmenvertrag — dort gibt es keine Mengen zu beauftragen. */}
      {isQuoteOrdered(draft.status) && !frameworkAccepted && (
        <QuoteOrderPanel
          quote={draft}
          busy={trancheBusy}
          supplementHint={canCreateSupplement}
          onOrderRest={() => { setTrancheError(''); setTrancheDialog({ mode: 'order' }); }}
          onDeclineRest={() => { setTrancheError(''); setTrancheDialog({ mode: 'decline' }); }}
          onRetry={(trancheId) => { setTrancheError(''); setTrancheDialog({ mode: 'retry', trancheId }); }}
        />
      )}

      {/* Angebots- & Projektdaten — ein einklappbarer Block statt zweier
          dauerhaft aufgeschlagener Karten ueber den Positionen. */}
      <div className="space-y-6">
        <button
          type="button"
          onClick={() => setSetupOpen((o) => !o)}
          className="flex w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white px-6 py-3.5 text-left shadow-sm transition-all hover:border-slate-300"
          aria-expanded={setupOpen}
        >
          <Building2 className="w-4 h-4 shrink-0 text-slate-400" />
          <span className="text-sm font-bold text-slate-700 shrink-0">Angebots- &amp; Projektdaten</span>
          {!setupOpen && setupSummary && (
            <span className="min-w-0 flex-1 truncate text-xs text-slate-400" title={setupSummary}>{setupSummary}</span>
          )}
          <ChevronDown className={`ml-auto w-4 h-4 shrink-0 text-slate-400 transition-transform ${setupOpen ? 'rotate-180' : ''}`} />
        </button>

        {setupOpen && (<>
      {/* Kopfdaten */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8 space-y-6">
        <div className="space-y-1.5">
          <label className={labelClass}>Bezeichnung / Projekt</label>
          <input value={draft.title} onChange={(e) => set('title', e.target.value)} disabled={locked} className={`${inputClass} font-bold`} placeholder="z. B. Neubau Bürogebäude Musterstraße" />
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2 ml-1">
            <label className={labelClass}>Angebotsart</label>
            {!locked && (
              <TextTemplatePicker
                category="offer_type"
                templates={templatesByCat('offer_type')}
                currentText={draft.details?.offerType || ''}
                onInsert={(c) => setDetail('offerType', c)}
                onCreated={handleTemplateCreated}
                onDeleted={handleTemplateDeleted}
              />
            )}
          </div>
          <textarea
            value={draft.details?.offerType ?? 'SiGeKo-Leistungen nach Baustellenverordnung\nHonorarangebot'}
            onChange={(e) => setDetail('offerType', e.target.value)}
            disabled={locked}
            rows={2}
            className={`${inputClass} resize-none`}
            placeholder={'SiGeKo-Leistungen nach Baustellenverordnung\nHonorarangebot'}
          />
          <p className="text-[11px] text-slate-400">Überschrift auf Seite 1 des PDF, mehrzeilig möglich.</p>
        </div>

        {/* Hoehenversatz des Anschreiben-Blocks: schiebt Datum, Betreff
            ("Angebot Nr. ...") und alles darunter gemeinsam hoch/runter.
            Gleiche Klemmung wie im Generator (-20..+60 mm). */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2 ml-1">
            <label className={labelClass}>Abstand Textblock (Angebot Nr.)</label>
            <div className="flex items-center gap-2">
              <span className="tabular-nums text-xs font-bold text-slate-600">{bodyOffsetMm > 0 ? `+${bodyOffsetMm}` : bodyOffsetMm} mm</span>
              {!locked && bodyOffsetMm !== 0 && (
                <button
                  type="button"
                  onClick={() => setDetail('bodyOffsetMm', 0)}
                  className="text-[11px] font-semibold text-slate-400 underline hover:text-slate-600"
                >
                  zurücksetzen
                </button>
              )}
            </div>
          </div>
          <input
            type="range"
            min={-20}
            max={60}
            step={1}
            value={bodyOffsetMm}
            onChange={(e) => setDetail('bodyOffsetMm', Number(e.target.value))}
            disabled={locked}
            className="w-full accent-slate-700 disabled:opacity-50"
            aria-label="Abstand des Textblocks auf Seite 1 in Millimetern"
          />
          <p className="text-[11px] text-slate-400">Verschiebt Datum, Betreff und den gesamten Brieftext auf Seite 1 nach oben (−) oder unten (+). 0 = Standardlayout.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <label className={labelClass}>Angebotsdatum</label>
            <input type="date" value={toDateInput(draft.quoteDate)} onChange={(e) => set('quoteDate', e.target.value)} disabled={locked} className={inputClass} />
          </div>
          <div className="space-y-1.5">
            <label className={labelClass}>Gültig bis</label>
            <input type="date" value={toDateInput(draft.validUntil)} onChange={(e) => set('validUntil', e.target.value)} disabled={locked} className={inputClass} />
          </div>
          <div className="space-y-1.5">
            <label className={labelClass}>Nachfassen am</label>
            <input type="date" value={toDateInput(draft.followUpDate)} onChange={(e) => set('followUpDate', e.target.value)} className={inputClass} />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md">
          <div className="space-y-1.5">
            <label className={labelClass}>Gesamtrabatt (%)</label>
            <input type="number" min={0} max={100} step={0.5} value={Number.isFinite(draft.discountPercent) ? draft.discountPercent : 0} onChange={(e) => set('discountPercent', e.target.value === '' ? 0 : Number(e.target.value))} disabled={locked} className={inputClass} />
          </div>
          <div className="space-y-1.5">
            <label className={labelClass}>Steuersatz (%)</label>
            <input type="number" min={0} max={100} step={0.5} value={Number.isFinite(draft.taxRate) ? draft.taxRate : 0} onChange={(e) => set('taxRate', e.target.value === '' ? 0 : Number(e.target.value))} disabled={locked} className={inputClass} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <label className={labelClass}>Summen im PDF</label>
            <select
              value={totalsMode}
              onChange={(e) => setDetail('totalsMode', e.target.value as QuoteTotalsMode)}
              disabled={locked}
              className={inputClass}
            >
              {QUOTE_TOTALS_MODE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {totalsMode === 'unitPrices' && (
              <p className="ml-1 text-[11px] text-slate-500">
                Für laufende Leistungen ohne Enddatum: das PDF zeigt nur Einheit und Einheitspreis, keine Mengen und keine Summen. Mengen hier dienen nur der internen Auswertung.
              </p>
            )}
            {totalsMode === 'monthly' && (
              <p className="ml-1 text-[11px] text-slate-500">
                Mengen je Monat eintragen — Beträge und Summen stehen im PDF als „je Monat“.
              </p>
            )}
            {totalsMode !== 'total' && !draft.details?.projectEnd && draft.details?.projectStart && (
              <p className="ml-1 text-[11px] text-slate-500">Ohne Enddatum steht im PDF „Laufzeit: ab …, unbefristet“.</p>
            )}
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <label className={labelClass}>NEP-Darstellung im PDF</label>
            <select
              value={draft.details?.nepDisplay || 'auto'}
              onChange={(e) => setDetail('nepDisplay', e.target.value as 'auto' | 'both' | 'simple')}
              // Ohne Endsumme gibt es keine Summenbloecke, die sie steuern koennte.
              disabled={locked || totalsMode === 'unitPrices'}
              className={inputClass}
            >
              <option value="auto">Automatisch</option>
              <option value="both">Beide Summenblöcke</option>
              <option value="simple">Nur Endsumme</option>
            </select>
          </div>
        </div>
      </div>

      {/* Projekt-Metadaten */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8 space-y-6">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-bold text-slate-700">Projekt-Metadaten</span>
        </div>

        {/* Baustellen-Adresse */}
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <label className={labelClass}>Baustellen-Adresse</label>
            <Button
              variant="secondary"
              onClick={handleVerifySite}
              disabled={siteChecking || (!draft.details?.siteStreet && !draft.details?.siteZip && !draft.details?.siteCity)}
              icon={siteChecking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
              className="h-8 text-xs"
            >
              Adresse prüfen
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_2fr] gap-4">
            <div className="space-y-1.5">
              <label className={labelClass}>Straße & Nr.</label>
              <div className="relative">
                <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                <input value={draft.details?.siteStreet || ''} onChange={(e) => { setDetail('siteStreet', e.target.value); setSiteCheck(null); }} disabled={locked} className={`${inputClass} pl-10`} placeholder="Musterstraße 1" />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className={labelClass}>PLZ</label>
              <input value={draft.details?.siteZip || ''} onChange={(e) => { setDetail('siteZip', e.target.value); setSiteCheck(null); }} disabled={locked} className={inputClass} placeholder="12345" />
            </div>
            <div className="space-y-1.5">
              <label className={labelClass}>Ort</label>
              <input value={draft.details?.siteCity || ''} onChange={(e) => { setDetail('siteCity', e.target.value); setSiteCheck(null); }} disabled={locked} className={inputClass} placeholder="Musterstadt" />
            </div>
          </div>
          {siteCheck && (
            <div className={`flex items-start gap-2 p-3 rounded-xl border text-sm ${
              siteCheck.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-amber-50 border-amber-200 text-amber-700'
            }`}>
              {siteCheck.ok ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />}
              <span>{siteCheck.ok ? `Adresse gefunden: ${siteCheck.text}` : siteCheck.text}</span>
            </div>
          )}
        </div>

        {/* Zeitraum */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className={labelClass}>Startdatum</label>
            <input type="date" value={toDateInput(draft.details?.projectStart)} onChange={(e) => setDetail('projectStart', e.target.value)} disabled={locked} className={inputClass} />
          </div>
          <div className="space-y-1.5">
            <label className={labelClass}>Enddatum</label>
            <input type="date" value={toDateInput(draft.details?.projectEnd)} onChange={(e) => setDetail('projectEnd', e.target.value)} disabled={locked} className={inputClass} />
          </div>
        </div>

        {/* Begehungs-Intervalle (mehrphasig) */}
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <label className={labelClass}>Begehungs-Intervalle (Phasen)</label>
            {!locked && (
              <Button variant="secondary" onClick={addPhase} icon={<Plus className="w-3.5 h-3.5" />} className="h-8 text-xs">
                Phase hinzufügen
              </Button>
            )}
          </div>

          {phases.length === 0 ? (
            // Fallback: eine Zeile 'Gesamtzeitraum'. Die Auswahl eines Intervalls
            // legt eine einzelne Phase ueber den Projektzeitraum an -> danach
            // uebernimmt die normale Phasen-UI (inkl. individuell x/Woche).
            <div className="space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className={labelClass}>Gesamtzeitraum – Intervall</label>
                  <select
                    value=""
                    onChange={(e) => { if (e.target.value) startFallbackPhase(e.target.value as IntervalKind); }}
                    disabled={locked}
                    className={inputClass}
                    title="Intervall wählen – legt eine Phase über den Projektzeitraum an"
                  >
                    <option value="">Intervall wählen…</option>
                    {(Object.keys(INTERVAL_LABELS) as IntervalKind[]).map((k) => (
                      <option key={k} value={k}>{INTERVAL_LABELS[k]}</option>
                    ))}
                  </select>
                </div>
              </div>
              {/* Alt-Werte (Altbestand) nur nachrichtlich, solange keine Phase existiert. */}
              {(draft.details?.intervalLabel || draft.details?.inspectionCount != null) && (
                <p className="text-xs text-slate-400 ml-1">
                  Alt: {draft.details?.intervalLabel || '—'}
                  {draft.details?.inspectionCount != null ? ` · ${draft.details.inspectionCount} Begehungen` : ''}
                </p>
              )}
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {phases.map((p, idx) => {
                  const count = phaseInspectionCount(p);
                  return (
                    <div key={p.id} className="p-3 rounded-2xl border border-slate-100 bg-slate-50 space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-mono font-bold text-slate-400 shrink-0">Phase {idx + 1}</span>
                        <span className="flex-1" />
                        <span className="text-xs font-bold text-slate-600 shrink-0 tabular-nums">
                          {count} Begehung{count === 1 ? '' : 'en'}
                        </span>
                        {!locked && (
                          <button onClick={() => removePhase(p.id)} className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all" title="Phase entfernen">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 items-end">
                        <div className="space-y-1">
                          <label className={labelClass}>Von</label>
                          <input type="date" value={toDateInput(p.from)} onChange={(e) => updatePhase(p.id, 'from', e.target.value)} disabled={locked} className={inputClass} />
                        </div>
                        <div className="space-y-1">
                          <label className={labelClass}>Bis</label>
                          <input type="date" value={toDateInput(p.to)} onChange={(e) => updatePhase(p.id, 'to', e.target.value)} disabled={locked} className={inputClass} />
                        </div>
                        <div className="space-y-1">
                          <label className={labelClass}>Intervall</label>
                          <select value={p.interval} onChange={(e) => updatePhase(p.id, 'interval', e.target.value as IntervalKind)} disabled={locked} className={inputClass}>
                            {(Object.keys(INTERVAL_LABELS) as IntervalKind[]).map((k) => (
                              <option key={k} value={k}>{INTERVAL_LABELS[k]}</option>
                            ))}
                          </select>
                        </div>
                        {p.interval === 'individuell' && (
                          <div className="space-y-1">
                            <label className={labelClass}>x pro Woche</label>
                            <input type="number" min={0} step={1} value={p.perWeek ?? ''} onChange={(e) => updatePhase(p.id, 'perWeek', e.target.value === '' ? undefined : Number(e.target.value))} disabled={locked} className={inputClass} placeholder="z. B. 3" />
                          </div>
                        )}
                      </div>
                      {/* Menge je Begehung + Position-Zuordnung (speist die Positionsmenge). */}
                      <div className="grid grid-cols-1 sm:grid-cols-[150px_1fr] gap-2 items-end">
                        <div className="space-y-1">
                          <label className={labelClass}>Menge je Begehung</label>
                          <input
                            type="number" min={0} step={0.5}
                            value={p.factor ?? 1}
                            onChange={(e) => updatePhase(p.id, 'factor', e.target.value === '' ? undefined : Number(e.target.value))}
                            disabled={locked}
                            className={inputClass}
                            title="z. B. 2,5 Std pro Begehung"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className={labelClass}>Position</label>
                          <select
                            value={p.linkedItemId && (draft.items || []).some((it) => it.id === p.linkedItemId) ? p.linkedItemId : ''}
                            onChange={(e) => {
                              if (e.target.value === '__new__') createLinkedItemForPhase(p.id);
                              else setPhaseLink(p.id, e.target.value || undefined);
                            }}
                            disabled={locked}
                            className={inputClass}
                            title="Angebotsposition, deren Menge aus dieser Phase berechnet wird"
                          >
                            <option value="">Keine Zuordnung</option>
                            {itemsWithPos.map((it) => {
                              // A struck position carries no posNumber, so without
                              // the suffix it is indistinguishable from the rest.
                              const label = `${it.posNumber ?? ''} ${it.title}`.trim();
                              return <option key={it.id} value={it.id}>{isQuoteItemExcluded(it) ? `${label} — gestrichen` : label}</option>;
                            })}
                            <option value="__new__">+ Neue Position anlegen…</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-between gap-3 p-3 rounded-2xl bg-blue-50 border border-blue-100">
                <span className="text-sm font-bold text-blue-800">Anzahl Begehungen gesamt</span>
                <span className="text-sm font-bold text-blue-800 tabular-nums">{phasesTotal} <span className="text-xs font-medium text-blue-500">(automatisch berechnet)</span></span>
              </div>
            </>
          )}
        </div>

        {/* Rahmenvertrag */}
        <div className="space-y-1.5">
          <label className={labelClass}>Rahmenvertrag{frameworkContracts.length > 0 ? '' : '-Nr.'}</label>
          {frameworkContracts.length > 0 ? (
            <>
              <select
                value={
                  frameworkManual
                    ? '__manual__'
                    : frameworkLabels.includes(draft.details?.frameworkRef || '')
                      ? (draft.details?.frameworkRef || '')
                      : ''
                }
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '__manual__') {
                    setFrameworkManual(true);
                  } else if (v === '') {
                    setFrameworkManual(false);
                    setDetail('frameworkRef', undefined);
                  } else {
                    setFrameworkManual(false);
                    setDetail('frameworkRef', v);
                  }
                }}
                disabled={locked}
                className={inputClass}
              >
                <option value="">Ohne Rahmenvertrag</option>
                {frameworkContracts.map((c) => {
                  const lbl = frameworkContractLabel(c);
                  return <option key={c.id} value={lbl}>{lbl}</option>;
                })}
                <option value="__manual__">Manuell eingeben…</option>
              </select>
              {frameworkManual && (
                <input value={draft.details?.frameworkRef || ''} onChange={(e) => setDetail('frameworkRef', e.target.value)} disabled={locked} className={inputClass} placeholder="z. B. RV-2026-001" />
              )}
            </>
          ) : (
            <input value={draft.details?.frameworkRef || ''} onChange={(e) => setDetail('frameworkRef', e.target.value)} disabled={locked} className={inputClass} placeholder="z. B. RV-2026-001" />
          )}
          {/* Positionen aus dem Rahmenvertrag selbst (vereinbarte Preise) bzw.
              aus der mit ihm verknuepften LV-Vorlage. */}
          {!locked && (selectedFrameworkHasItems || selectedFrameworkContract?.templateId) && (
            <Button
              variant="secondary"
              onClick={importFrameworkTemplateItems}
              disabled={importingFramework}
              icon={importingFramework ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ListPlus className="w-3.5 h-3.5" />}
              className="h-9 text-xs"
            >
              Positionen aus Rahmenvertrag übernehmen
            </Button>
          )}
        </div>
      </div>
        </>)}
      </div>

      {/* Positionen (nach Sektionen gruppiert – Kalkulationsblatt) */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8 space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-slate-400" />
            <span className="text-sm font-bold text-slate-700">Positionen</span>
            <span className="text-xs text-slate-400">({(draft.items || []).length})</span>
            {excludedCount > 0 && (
              <span className="text-xs font-bold text-amber-600">· {excludedCount} gestrichen</span>
            )}
          </div>
          {!locked && (
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="ai" onClick={() => setTenderKind('lv')} icon={<Sparkles className="w-4 h-4" />} className="h-9 text-xs" title="Leistungsverzeichnis aus einer Ausschreibung als gesperrte Positionen übernehmen">
                LV aus Ausschreibung
              </Button>
              <Button variant="ai" onClick={() => setTenderKind('requirements')} icon={<Sparkles className="w-4 h-4" />} className="h-9 text-xs" title="Projekt-Rahmendaten und Anforderungen aus einer Ausschreibung auslesen">
                Anforderungen auslesen
              </Button>
              <Button
                variant="secondary"
                onClick={sortItemsByLv}
                disabled={!canSortQuoteItemsByLvPosition(draft.items || [])}
                icon={<ArrowDownAZ className="w-4 h-4" />}
                className="h-9 text-xs"
                title="Positionen je Abschnitt nach der LV-Nummer des Kunden ordnen (1.10 vor 1.20 vor 2.10, die 13 hinter der 4). Positionen ohne LV-Nummer bleiben am Ende. Danach lässt sich die Reihenfolge mit den Pfeilen an der Zeile von Hand nachziehen."
              >
                Nach LV-Nr. sortieren
              </Button>
              <Button variant="secondary" onClick={addSection} icon={<Layers className="w-4 h-4" />} className="h-9 text-xs">
                Abschnitt hinzufügen
              </Button>
            </div>
          )}
        </div>

        {/* Wie das Original-LV im Angebots-PDF erscheint. Stand bisher zwischen
            Angebotsdatum und Steuersatz — 280 Zeilen von dem Knopf entfernt,
            mit dem das Dokument ueberhaupt erst hochgeladen wird. Der Hilfetext
            dort gab das selbst zu („erst über ‚LV aus Ausschreibung‘
            hochladen"). Jetzt steht beides beieinander. */}
        {/* Ohne hinterlegtes Dokument UND ohne gesetzte Einstellung gibt es
            hier nichts zu entscheiden — dann bleibt eine graue Zeile statt
            einer eigenen Karte mit totem Auswahlfeld zwischen Werkzeugleiste
            und erstem Abschnitt. */}
        {!locked && hasLvDoc === false && (draft.details?.lvOriginalMode ?? 'none') === 'none' && (
          <p className="px-1 text-[11px] text-slate-400">
            Original-LV im Angebots-PDF: kein Ausschreibungs-Dokument gespeichert — erst über
            „LV aus Ausschreibung“ hochladen.
          </p>
        )}

        {!locked && !(hasLvDoc === false && (draft.details?.lvOriginalMode ?? 'none') === 'none') && (
          <div className="flex flex-col gap-1.5 rounded-2xl border border-slate-200 bg-slate-50 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <label className={`${labelClass} shrink-0`}>Original-LV im Angebots-PDF</label>
              <select
                value={draft.details?.lvOriginalMode ?? (draft.details?.attachOriginalLv ? 'append' : 'none')}
                onChange={(e) => setDetail('lvOriginalMode', e.target.value as 'none' | 'append' | 'priced')}
                disabled={hasLvDoc === false}
                className="flex-1 min-w-[16rem] px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-60"
              >
                <option value="none">Nicht einbinden</option>
                <option value="append">Unverändert anhängen</option>
                <option value="priced">Bepreisen — Preise ins Original, ersetzt die Kalkulation</option>
              </select>
            </div>
            {/* Ohne hinterlegtes Dokument bleibt die Einstellung sichtbar und
                erklaert sich. Sie ganz auszublenden war die schlechtere Wahl:
                ein DUPLIZIERTES Angebot erbt `lvOriginalMode`, aber nicht das
                Dokument — dann scheitern Vorschau UND Versand, und der Nutzer
                saehe nur eine Fehlermeldung ohne Bezug. */}
            {hasLvDoc === false && (draft.details?.lvOriginalMode ?? 'none') !== 'none' && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 text-amber-600" />
                <span className="text-[11px] text-amber-800">
                  Eingestellt ist „{(draft.details?.lvOriginalMode ?? 'none') === 'priced' ? 'Bepreisen' : 'Unverändert anhängen'}“,
                  aber zu diesem Angebot ist kein Ausschreibungs-Dokument gespeichert — Vorschau und Versand schlagen so fehl.
                </span>
                <Button
                  variant="secondary"
                  onClick={() => setDetail('lvOriginalMode', 'none')}
                  className="h-7 text-[11px]"
                >
                  Auf „Nicht einbinden“ setzen
                </Button>
              </div>
            )}
            <p className="text-[11px] text-slate-400">
              {hasLvDoc === false
                ? 'Kein Ausschreibungs-Dokument zu diesem Angebot gespeichert — erst oben über „LV aus Ausschreibung“ hochladen.'
                : 'Anhängen: Original unverändert hinter dem Angebot. Bepreisen: EP/GB werden in die Spalten des Originals geschrieben (braucht ein PDF mit Textebene, kein Scan) — das PDF besteht dann aus Anschreiben + bepreistem LV, die eigene Kalkulationstabelle entfällt.'}
            </p>
          </div>
        )}

        {/* Sammelaktion — erscheint nur mit Auswahl. Die Zaehler kommen aus
            quoteItemSelection, damit hier nichts nachgerechnet wird. */}
        {!locked && selectionStats.total > 0 && (
          <div className="flex flex-wrap items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-2xl">
            <span className="text-xs font-bold text-blue-700">
              {selectionStats.total} Position(en) ausgewählt
              {selectionStats.excluded > 0 && ` · davon ${selectionStats.excluded} gestrichen`}
            </span>
            <div className="flex-1" />
            {selectionStats.active > 0 && (
              <Button variant="secondary" onClick={() => applyBulkExcluded(true)} icon={<Strikethrough className="w-3.5 h-3.5" />} className="h-8 text-xs">
                {selectionStats.active} Position(en) streichen
              </Button>
            )}
            {selectionStats.excluded > 0 && (
              <Button variant="secondary" onClick={() => applyBulkExcluded(false)} icon={<RotateCcw className="w-3.5 h-3.5" />} className="h-8 text-xs">
                Streichung aufheben
              </Button>
            )}
            <Button variant="ghost" onClick={() => setSelectedItemIds(new Set<string>())} className="h-8 text-xs">
              Auswahl aufheben
            </Button>
          </div>
        )}

        {[...sections, null].map((section, sIdx) => {
          const sectionId = section?.id ?? null;
          const groupItems = itemsWithPos.filter((it) => (it.sectionId ?? null) === sectionId);
          // Catch-all-Gruppe (null) nur zeigen, wenn sie Positionen enthaelt.
          if (sectionId === null && groupItems.length === 0) return null;
          const subtotal = perSectionMap.get(sectionId) ?? 0;
          // Kopf-Checkbox: alle Positionen DIESES Abschnitts.
          const groupIds = groupItems.map((it) => it.id);
          const groupSelection = sectionSelectionState(selectedItemIds, groupIds);
          return (
            <div key={sectionId ?? '__none__'} className={`rounded-2xl border border-slate-200 ${catalogOpen && catalogTargetSection === sectionId ? 'overflow-visible relative z-20' : 'overflow-hidden'}`}>
              {/* Sektions-Kopf */}
              <div className="flex items-center gap-2 px-3 py-2.5 bg-slate-50 border-b border-slate-200">
                {!locked && groupIds.length > 0 && (
                  <input
                    type="checkbox"
                    checked={groupSelection === 'all'}
                    // Teilauswahl nur ueber das DOM darstellbar (kein Attribut).
                    ref={(el) => { if (el) el.indeterminate = groupSelection === 'some'; }}
                    onChange={() => setSelectedItemIds((prev) => toggleQuoteSectionSelection(prev, groupIds))}
                    className="w-4 h-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-400/40"
                    title="Alle Positionen dieses Abschnitts auswählen"
                    aria-label="Alle Positionen dieses Abschnitts auswählen"
                  />
                )}
                {section ? (
                  <>
                    <span className="text-[11px] font-mono font-bold text-slate-400 shrink-0">{sIdx + 1}.</span>
                    <input
                      value={section.title}
                      onChange={(e) => renameSection(section.id, e.target.value)}
                      disabled={locked}
                      className="flex-1 min-w-0 px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-60 disabled:bg-transparent disabled:border-transparent"
                      placeholder="Abschnittstitel"
                    />
                    {!locked && (
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => moveSection(section.id, -1)} disabled={sIdx === 0} className="p-1.5 text-slate-300 hover:text-slate-600 hover:bg-white rounded-lg transition-all disabled:opacity-30" title="Nach oben"><ArrowUp className="w-3.5 h-3.5" /></button>
                        <button onClick={() => moveSection(section.id, 1)} disabled={sIdx >= sections.length - 1} className="p-1.5 text-slate-300 hover:text-slate-600 hover:bg-white rounded-lg transition-all disabled:opacity-30" title="Nach unten"><ArrowDown className="w-3.5 h-3.5" /></button>
                        <button onClick={() => deleteSection(section.id)} className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all" title="Abschnitt löschen"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    )}
                  </>
                ) : (
                  <span className="flex-1 text-sm font-bold text-slate-500">Ohne Zuordnung</span>
                )}
                <span className="text-xs font-bold text-slate-500 shrink-0 tabular-nums">{fmtEur(subtotal)}</span>
              </div>

              {/* Positionen der Sektion */}
              <div className="p-3 space-y-2">
                {groupItems.length === 0 ? (
                  <div className="py-4 text-center text-xs text-slate-400">Noch keine Positionen in diesem Abschnitt.</div>
                ) : (
                  <div className="-mx-1 overflow-x-auto px-1 pb-1">
                    {/* Eigener Scrollbereich statt Ueberlauf: das Raster
                        braucht rund 800 px, und neben der angedockten
                        Vorschau ist die Spalte in einem 1600-px-Fenster
                        schmaler. Der Ueberhang lief bisher UNTER die
                        klebende Vorschau — Gesamtbetrag und Zeilenknoepfe
                        waren verdeckt. Wer die Breite ganz will, dockt die
                        Vorschau ab. */}
                    <div className="space-y-2 lg:min-w-[804px]">
                    <div className="hidden lg:grid grid-cols-[18px_24px_56px_60px_minmax(140px,1fr)_56px_52px_68px_48px_80px_96px] gap-2 px-[13px] text-[11px] uppercase font-bold text-slate-400 tracking-wider">
                      {/* Kopf exakt ueber dem FELDTEXT, nicht der Spaltenkante:
                          px-[13px] aussen = p-3 (12px) + 1px Kartenrand; die
                          pl-/pr-[11px] je Spalte = px-2.5 (10px) + 1px Rahmen
                          der Eingabefelder. Pos-Nr und Gesamt sind reine
                          Textzellen ohne Feldrahmen — sie bleiben ohne Einzug.
                          Die letzte Spur ist 96px breit: drei Knoepfe a 32px
                          (p-2 + w-4) stehen dort ohne eigenen Abstand.
                          Letzte Spalte FEST 64px (Streichen + Papierkorb),
                          nicht auto: auto misst je Grid den eigenen Inhalt —
                          im Kopf 0, in der Zeile 64px — und verschob damit alle
                          Spalten hinter der 1fr-Bezeichnung gegeneinander.
                          Erste Spur 18px = die Reihenfolge-Pfeile, zweite
                          24px = Auswahl-Checkbox (w-4 + Rand).
                          Bezeichnung minmax(140px,1fr): der Editor steht neben
                          der Vorschau, Tailwind-Breakpoints messen aber das
                          FENSTER — im ~800px-Panel greift trotzdem das
                          Desktop-Raster, und ohne Mindestbreite blieb der
                          Bezeichnung ein unlesbarer Rest. */}
                      <span></span><span></span><span>Pos-Nr</span><span className="pl-[11px]">LV-Pos</span><span className="pl-[11px]">Bezeichnung</span><span className="pl-[11px]">Einh.</span><span className="text-right pr-[11px]">Menge</span><span className="text-right pr-[11px]">E-Preis</span><span className="text-right pr-[11px]">Rab %</span><span className="text-right">Gesamt</span><span></span>
                    </div>
                    {groupItems.map((it) => {
                      const line = lineTotal({ quantity: it.quantity || 0, unitPrice: it.unitPrice || 0, discountPercent: it.discountPercent || 0 });
                      // Gestrichene Position: bleibt sichtbar, zaehlt aber
                      // nirgends mehr mit (Summen kommen aus quoteLogic).
                      const excluded = isQuoteItemExcluded(it);
                      const strike = excluded ? 'line-through decoration-slate-400' : '';
                      return (
                        <div key={it.id} className={`p-3 rounded-2xl border space-y-2 ${excluded ? 'bg-slate-100/70 border-slate-200' : it.nep ? 'bg-amber-50/40 border-amber-200' : 'bg-slate-50 border-slate-100'}`}>
                          <div className="grid grid-cols-2 lg:grid-cols-[18px_24px_56px_60px_minmax(140px,1fr)_56px_52px_68px_48px_80px_96px] gap-2 items-center">
                            {/* Reihenfolge direkt an der Zeile: die
                                Positionsnummer haengt an ihr, und sie erst
                                nach einem Aufklapper zu zeigen machte aus
                                jeder Umsortierung zwei Klicks. Unter lg
                                ausgeblendet, damit das Zwei-Spalten-Raster
                                der schmalen Ansicht unveraendert bleibt. */}
                            <div className="hidden lg:flex flex-col items-center justify-center">
                              {!locked && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => moveItem(it.id, -1)}
                                    disabled={!canMoveQuoteItem(draft.items || [], it.id, -1)}
                                    className="text-slate-300 transition-colors hover:text-blue-600 disabled:opacity-25 disabled:hover:text-slate-300"
                                    title="Position nach oben"
                                    aria-label="Position nach oben"
                                  >
                                    <ChevronUp className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => moveItem(it.id, 1)}
                                    disabled={!canMoveQuoteItem(draft.items || [], it.id, 1)}
                                    className="text-slate-300 transition-colors hover:text-blue-600 disabled:opacity-25 disabled:hover:text-slate-300"
                                    title="Position nach unten"
                                    aria-label="Position nach unten"
                                  >
                                    <ChevronDown className="w-3.5 h-3.5" />
                                  </button>
                                </>
                              )}
                            </div>
                            {!locked ? (
                              <input
                                type="checkbox"
                                checked={selectedItemIds.has(it.id)}
                                onChange={() => setSelectedItemIds((prev) => toggleQuoteItemSelection(prev, it.id))}
                                className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-400/40"
                                title="Position auswählen"
                                aria-label={`Position ${it.posNumber || it.title || ''} auswählen`}
                              />
                            ) : <span />}
                            <span className={`flex items-center gap-1 text-xs font-mono font-bold tabular-nums ${excluded ? 'text-slate-400' : 'text-slate-500'}`}>
                              {it.locked && <Lock className="w-3 h-3 text-slate-400 shrink-0" aria-label="Aus Ausschreibung — LV-Nummer und Menge per Klick entsperrbar; Beschreibung, Einheit, Preis und NEP editierbar" />}
                              {it.posNumber || '—'}
                            </span>
                            {/* LV-Nummer aus der Ausschreibung: vorgegeben, per Klick
                                (mit Rueckfrage) entsperrbar — wie die Menge. Kam die
                                Zeile OHNE Nummer, ist das Feld frei; wer dort tippt,
                                behaelt es ('lv:'-Eintrag), sonst sprang es nach dem
                                ersten Zeichen auf das Schloss um. Die Menge bleibt
                                davon unberuehrt gesperrt. */}
                            {(!!it.locked && !!it.lvPosition && !lvRowUnlocked.has(it.id) && !lvRowUnlocked.has(`lv:${it.id}`) && !locked) ? (
                              <button
                                type="button"
                                onClick={() => unlockLvRow(it.id)}
                                className={`w-full px-2.5 py-2 bg-white border border-slate-200 rounded-lg text-sm flex items-center gap-1 text-left hover:border-amber-300 hover:bg-amber-50/40 transition-all ${strike}`}
                                title="LV-Nummer ist vorgegeben (Ausschreibung) — klicken zum Entsperren"
                              >
                                <Lock className="w-3 h-3 text-slate-400 shrink-0" />
                                <span className="truncate">{it.lvPosition}</span>
                              </button>
                            ) : (
                              <input
                                value={it.lvPosition}
                                onChange={(e) => {
                                  if (it.locked && !lvRowUnlocked.has(it.id) && !lvRowUnlocked.has(`lv:${it.id}`)) {
                                    setLvRowUnlocked((prev) => new Set(prev).add(`lv:${it.id}`));
                                  }
                                  updateItem(it.id, 'lvPosition', e.target.value);
                                }}
                                disabled={locked}
                                className={`w-full px-2.5 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-60 ${strike}`} placeholder="LV" />
                            )}
                            <div className="relative col-span-2 lg:col-span-1">
                              {/* title-Attribut: der volle Text als Hover-Tooltip —
                                  die Spalte ist neben der Vorschau zu schmal fuer
                                  lange Bezeichnungen. Die Lupe oeffnet das
                                  Expand-Modal zum bequemen Schreiben. */}
                              {/* Formatierte Beschreibung: das einzeilige Feld kann
                                  Fett/Farbe/Aufzaehlung weder zeigen noch erhalten —
                                  es wird zur Anzeige, ein Klick oeffnet den Editor.
                                  Auch bei LV-Positionen (it.locked) bearbeitbar: der
                                  Abgleich mit dem Original-LV und der Duplikat-Check
                                  beim Re-Import laufen ueber die LV-Nummer, nicht
                                  ueber den Text. */}
                              {effectiveQuoteRich(it) ? (
                                <input
                                  readOnly
                                  value={it.title.replace(/\n+/g, '  ')}
                                  title={`${it.title}\n\n(formatiert — zum Bearbeiten klicken)`}
                                  onClick={() => setExpand({ label: `Leistungsbeschreibung ${it.posNumber || ''}`.trim(), key: it.id, kind: 'item' })}
                                  disabled={locked}
                                  className={`w-full px-2.5 py-2 bg-white border border-blue-200 rounded-lg text-sm cursor-pointer outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-60 disabled:cursor-default ${strike} ${linkedItemIds.has(it.id) ? 'pr-14' : 'pr-8'}`}
                                />
                              ) : (
                                <input value={it.title} title={it.title || undefined} onChange={(e) => updateItem(it.id, 'title', e.target.value)} disabled={locked} className={`w-full px-2.5 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-60 ${strike} ${linkedItemIds.has(it.id) ? 'pr-14' : 'pr-8'}`} placeholder="Leistung" />
                              )}
                              {linkedItemIds.has(it.id) && (
                                <span title="Menge wird aus Begehungs-Phasen berechnet" className="absolute right-2 top-1/2 -translate-y-1/2 text-blue-500 pointer-events-none">
                                  <Link2 className="w-3.5 h-3.5" />
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => setExpand({ label: `Leistungsbeschreibung ${it.posNumber || ''}`.trim(), key: it.id, kind: 'item' })}
                                className={`absolute top-1/2 -translate-y-1/2 p-0.5 text-slate-300 hover:text-blue-600 hover:bg-blue-50 rounded transition-all ${linkedItemIds.has(it.id) ? 'right-7' : 'right-1.5'}`}
                                title="Leistungsbeschreibung bearbeiten (Fett, Kursiv, Aufzählung, Farbe)"
                              >
                                <Maximize2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            {/* Einheit: Dropdown mit Firmen-Einheiten + 'Andere…'-Freitext-Fallback.
                                Bei gestrichener Zeile gesperrt und durchgestrichen —
                                dieselbe Behandlung wie Menge/E-Preis/Rabatt. Auch bei
                                LV-Positionen frei: der Import liest die Einheit nicht
                                immer richtig, und der Preisstempel im Original-LV
                                haengt nicht an ihr. */}
                            <div className="w-full">
                              <select
                                value={QUOTE_UNITS.some((u) => u.value === it.unit) ? it.unit : '__other__'}
                                onChange={(e) => updateItem(it.id, 'unit', e.target.value === '__other__' ? '' : e.target.value)}
                                disabled={locked || excluded}
                                className={`w-full px-2 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-60 ${strike}`}
                                title="Einheit"
                              >
                                {QUOTE_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                                <option value="__other__">Andere…</option>
                              </select>
                              {!QUOTE_UNITS.some((u) => u.value === it.unit) && (
                                <input value={it.unit} onChange={(e) => updateItem(it.id, 'unit', e.target.value)} disabled={locked || excluded} className={`mt-1 w-full px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-60 ${strike}`} placeholder="Einheit" />
                              )}
                            </div>
                            {/* Gestrichene Zeile: Menge nur noch lesbar — der
                                Entsperr-Dialog waere hier sinnlos. */}
                            {(!!it.locked && (it.quantity || 0) > 0 && !lvRowUnlocked.has(it.id) && !locked && !excluded) ? (
                              <button
                                type="button"
                                onClick={() => unlockLvRow(it.id)}
                                className="w-full px-2.5 py-2 bg-white border border-slate-200 rounded-lg text-sm flex items-center justify-end gap-1 hover:border-amber-300 hover:bg-amber-50/40 transition-all"
                                title="Menge ist vorgegeben (Ausschreibung) — klicken zum Entsperren"
                              >
                                {/* Schloss vor der Zahl: nur so steht die Menge
                                    an derselben Kante wie im entsperrten Feld. */}
                                <Lock className="w-3 h-3 text-slate-400 shrink-0" />
                                <span className="tabular-nums">{Number.isFinite(it.quantity) ? it.quantity : 0}</span>
                              </button>
                            ) : (
                              <input type="number" min={0} step={0.01} value={Number.isFinite(it.quantity) ? it.quantity : 0} onChange={(e) => updateItem(it.id, 'quantity', e.target.value === '' ? 0 : Number(e.target.value))} disabled={locked || excluded} className={`w-full px-2.5 py-2 bg-white border border-slate-200 rounded-lg text-sm text-right tabular-nums outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-60 ${strike}`} />
                            )}
                            <input type="number" min={0} step={0.01} value={Number.isFinite(it.unitPrice) ? it.unitPrice : 0} onChange={(e) => updateItem(it.id, 'unitPrice', e.target.value === '' ? 0 : Number(e.target.value))} disabled={locked || excluded} className={`w-full px-2.5 py-2 bg-white border border-slate-200 rounded-lg text-sm text-right tabular-nums outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-60 ${strike}`} />
                            <input type="number" min={0} max={100} step={0.5} value={Number.isFinite(it.discountPercent) ? it.discountPercent : 0} onChange={(e) => updateItem(it.id, 'discountPercent', e.target.value === '' ? 0 : Number(e.target.value))} disabled={locked || excluded} className={`w-full px-2.5 py-2 bg-white border border-slate-200 rounded-lg text-sm text-right tabular-nums outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-60 ${strike}`} />
                            {/* Gesamt (Zeilenbetrag) – auf allen Breakpoints sichtbar; NEP zeigt Preis + Marker,
                                die gestrichene Zeile den durchgestrichenen Betrag + 'entfällt'. */}
                            <div className="flex items-center justify-end gap-1 col-span-2 lg:col-span-1">
                              <span className={`text-sm font-bold tabular-nums ${excluded ? `text-slate-400 ${strike}` : it.nep ? 'text-amber-600' : 'text-slate-600'}`}>{fmtEur(line)}</span>
                              {excluded
                                ? <span className="text-[11px] font-bold text-amber-600 uppercase">{QUOTE_EXCLUDED_LABEL}</span>
                                : it.nep && <span className="text-[11px] font-bold text-amber-600">NEP</span>}
                            </div>
                            <div className="flex items-center justify-end col-span-2 lg:col-span-1">
                              {/* Mehr zur Zeile: NEP, Bemerkung, Reihenfolge,
                                  Abschnitt. Auch im gesperrten Angebot
                                  erreichbar — die Bemerkung will man lesen
                                  koennen. */}
                              <button
                                type="button"
                                onClick={() => setOpenItemId((cur) => (cur === it.id ? null : it.id))}
                                className={`p-2 rounded-lg transition-all ${
                                  openItemId === it.id ? 'text-blue-600 bg-blue-50' : 'text-slate-300 hover:text-slate-600 hover:bg-slate-100'
                                }`}
                                title={openItemId === it.id ? 'Details schließen' : 'Mehr: Bemerkung, NEP, Reihenfolge, Abschnitt'}
                                aria-expanded={openItemId === it.id}
                              >
                                {/* Bewusst ChevronDown und nicht MoreHorizontal:
                                    das Waagerecht-Menue in der Kopfleiste ist ein
                                    Ueberlaufmenue, das hier ein Aufklapper. Zwei
                                    Bedeutungen fuer dasselbe Zeichen auf einem
                                    Bildschirm sind eine Bedeutung zu viel. */}
                                <ChevronDown className={`w-4 h-4 transition-transform ${openItemId === it.id ? 'rotate-180' : ''}`} />
                              </button>
                              {!locked && (
                                <>
                                  {/* Streichen: Position bleibt stehen, faellt aber
                                      aus Summen, Uebernahme und Angebots-PDF. */}
                                  <button
                                    onClick={() => toggleItemExcluded(it.id, !excluded)}
                                    className={`p-2 rounded-lg transition-all ${excluded ? 'text-amber-600 bg-amber-50 hover:bg-amber-100' : 'text-slate-300 hover:text-amber-600 hover:bg-amber-50'}`}
                                    title={excluded ? 'Streichung aufheben' : 'Position streichen'}
                                    aria-label={excluded ? 'Streichung aufheben' : 'Position streichen'}
                                    aria-pressed={excluded}
                                  >
                                    {excluded ? <RotateCcw className="w-4 h-4" /> : <Strikethrough className="w-4 h-4" />}
                                  </button>
                                  <button onClick={() => removeItem(it.id)} className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all" title="Position entfernen">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                          {/* Zusammengefasste Notiz, solange die Zeile zu ist —
                              eine Bemerkung darf nicht unsichtbar werden. */}
                          {openItemId !== it.id && it.bemerkung && (
                            <p className="px-1 text-[11px] text-slate-400 truncate" title={it.bemerkung}>{it.bemerkung}</p>
                          )}
                          {/* Paketposition: auch zugeklappt sichtbar, dass sie
                              Bestandteile traegt (intern, nie im PDF). */}
                          {openItemId !== it.id && hatBestandteile(it) && (
                            <p className="px-1 text-[11px] text-slate-400">
                              Bestandteile (intern): {(it.bestandteile || []).length}
                              {gueltigesKontingent(it.stundenKontingent) !== undefined && ` · Kontingent ${formatStunden(gueltigesKontingent(it.stundenKontingent) as number)}`}
                            </p>
                          )}
                          {/* Nachtrag: aus dem Projekt uebernommen — Vorschlag fuer
                              die Zuordnung im Annahmedialog (dort aenderbar). */}
                          {supplementRef && it.targetServiceId && (
                            <p className="px-1 text-[11px] text-teal-700">→ erhöht Projektposition{it.lvPosition ? ` ${it.lvPosition}` : ''}</p>
                          )}

                          {/* NEP + Bemerkung + Abschnitts-Zuordnung */}
                          {openItemId === it.id && (
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-xl bg-slate-50 border border-slate-100 p-2">
                            {/* NEP bei gestrichener Zeile gesperrt: eine gestrichene
                                Eventualposition ist gegenstandslos. Bei LV-Positionen
                                (it.locked) bewusst frei: der Import erkennt
                                Bedarfspositionen nicht immer, die Korrektur muss
                                moeglich sein. */}
                            <label className={`flex items-center gap-1.5 shrink-0 text-xs font-bold select-none ${excluded ? `text-slate-400 ${strike}` : 'text-slate-500 cursor-pointer'}`}>
                              <input type="checkbox" checked={!!it.nep} onChange={(e) => updateItem(it.id, 'nep', e.target.checked)} disabled={locked || excluded} className="w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-amber-400/40 disabled:opacity-40" />
                              NEP (Eventualposition)
                            </label>
                            <input value={it.bemerkung || ''} onChange={(e) => updateItem(it.id, 'bemerkung', e.target.value)} disabled={locked} className="flex-1 px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-60" placeholder="Bemerkung…" />
                            {!locked && (
                              <>
                                {/* Die Reihenfolge-Pfeile standen hier, bis sie in
                                    die Zeile selbst gewandert sind — die
                                    Sektionswahl bleibt. */}
                                <select
                                  value={(it.sectionId && sections.some((s) => s.id === it.sectionId)) ? it.sectionId : ''}
                                  onChange={(e) => updateItem(it.id, 'sectionId', e.target.value || undefined)}
                                  className="shrink-0 px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-600 outline-none focus:ring-2 focus:ring-blue-500/20"
                                  title="Abschnitt zuordnen"
                                >
                                  <option value="">Ohne Zuordnung</option>
                                  {sections.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
                                </select>
                              </>
                            )}
                          </div>
                          )}

                          {/* PAKETPOSITION: Bestandteile (intern) — nie im
                              Angebots-PDF. Festlegen nur Admin (E6); andere
                              sehen vorhandene nur lesend. Bei der
                              Auftragsannahme werden sie ins Projekt kopiert,
                              aber nur bei Menge 1 (mapQuoteItemsToServiceItems). */}
                          {openItemId === it.id && (darfBestandteileFestlegen || hatBestandteile(it)) && (() => {
                            const pruef = pruefPosition({ quantityContracted: Number(it.quantity) || 0, unit: it.unit, assignment: it.assignment, is_optional: it.nep === true });
                            const sperreGrund = positionsSperreFuerBestandteile(pruef);
                            const auf = bestandteileItemId === it.id;
                            return (
                              <div className="rounded-xl bg-slate-50 border border-slate-100 p-2">
                                <button
                                  type="button"
                                  onClick={() => setBestandteileItemId((cur) => (cur === it.id ? null : it.id))}
                                  aria-expanded={auf}
                                  className="w-full flex items-center justify-between gap-2 px-1 py-0.5 text-xs font-semibold text-slate-600"
                                >
                                  <span className="flex items-center gap-1.5">
                                    <ListChecks className="w-3.5 h-3.5" />
                                    Bestandteile (intern){hatBestandteile(it) ? ` · ${(it.bestandteile || []).length}` : ''}
                                  </span>
                                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${auf ? 'rotate-180' : ''}`} />
                                </button>
                                {auf && (
                                  <div className="mt-2 px-1">
                                    {!hatBestandteile(it) && sperreGrund ? (
                                      <p className="text-[11px] text-slate-500">{sperreGrund}</p>
                                    ) : (
                                      <>
                                        <BestandteileEditor
                                          wert={it.bestandteile}
                                          // QuoteItem kennt kein null: „keine" = Feld weg —
                                          // und mit der Liste das Kontingent (S1).
                                          onChange={(neu) => patchItem(it.id, bestandteilePatch(neu))}
                                          vorlage={catalog.find((c) => c.id === it.catalogItemId)?.bestandteile}
                                          disabled={locked || !darfBestandteileFestlegen}
                                          service={pruef}
                                        />
                                        {/* STUNDENKONTINGENT (intern, nie im Angebots-PDF) —
                                            geht bei der Auftragsannahme mit den Bestandteilen
                                            ins Projekt (mapQuoteItemsToServiceItems). */}
                                        {hatBestandteile(it) && (
                                          <div className="mt-3 max-w-xs">
                                            <StundenKontingentFeld
                                              wert={it.stundenKontingent}
                                              onChange={(k) => updateItem(it.id, 'stundenKontingent', k)}
                                              deaktiviert={locked || !darfBestandteileFestlegen}
                                              sperrHinweis={locked ? undefined : 'Festlegen nur Admins.'}
                                            />
                                          </div>
                                        )}
                                        {hatBestandteile(it) && sperreGrund && (
                                          <p className="mt-1 text-[11px] text-amber-700">
                                            Bei der Auftragsannahme werden die Bestandteile{gueltigesKontingent(it.stundenKontingent) !== undefined ? ' und das Stundenkontingent' : ''} so nicht ins Projekt übernommen.
                                          </p>
                                        )}
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                    </div>
                  </div>
                )}

                {/* Position hinzufuegen (pro Sektion) */}
                {!locked && section && (
                  <div className="flex gap-2 pt-1">
                    <Button variant="ghost" onClick={() => addFreeItem(section.id)} icon={<Plus className="w-3.5 h-3.5" />} className="h-8 text-xs">
                      Position hinzufügen
                    </Button>
                    <div ref={catalogTargetSection === section.id ? catalogWrapRef : undefined} className="relative">
                      <Button variant="ghost" onClick={() => { setCatalogTargetSection(section.id); setCatalogOpen((o) => catalogTargetSection === section.id ? !o : true); setCatalogQuery(''); }} icon={<ListPlus className="w-3.5 h-3.5" />} className="h-8 text-xs">
                        Aus Katalog
                        <ChevronDown className={`w-3.5 h-3.5 ml-1 transition-transform ${catalogOpen && catalogTargetSection === section.id ? 'rotate-180' : ''}`} />
                      </Button>
                      {catalogOpen && catalogTargetSection === section.id && (
                        <div className="absolute left-0 z-50 mt-2 w-80 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-scale-in">
                          <div className="p-2.5 border-b border-slate-100">
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                              <input autoFocus value={catalogQuery} onChange={(e) => setCatalogQuery(e.target.value)} placeholder="Leistung suchen…" className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
                            </div>
                          </div>
                          <div className="max-h-64 overflow-y-auto py-1">
                            {filteredCatalog.length === 0 ? (
                              <div className="px-4 py-6 text-center text-xs text-slate-400">Kein Katalogeintrag gefunden</div>
                            ) : (
                              filteredCatalog.map((cit) => {
                                // Effektiven Kundenpreis anzeigen (Sonderpreis/Rabatt), nicht den Katalogpreis.
                                const override = prices.find((p) => p.catalogItemId === cit.id);
                                const eff = resolveUnitPrice(cit.unitPrice, override, customer.defaultDiscountPercent);
                                const hasCustomerPrice = eff !== cit.unitPrice;
                                return (
                                  <button key={cit.id} type="button" onClick={() => addCatalogItem(cit, section.id)} title={`${cit.lvPosition ? cit.lvPosition + ' – ' : ''}${cit.title}`} className="group w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-blue-50/60 transition-colors">
                                    <span className="text-[11px] font-mono text-slate-400 shrink-0 w-12">{cit.lvPosition || '—'}</span>
                                    <span className="flex-1 min-w-0 text-sm text-slate-700 truncate group-hover:whitespace-normal group-hover:break-words">{cit.title}</span>
                                    {hasCustomerPrice && (
                                      <span className="text-[11px] text-slate-400 line-through shrink-0">{fmtEur(cit.unitPrice)}</span>
                                    )}
                                    <span className={`text-xs font-bold shrink-0 ${hasCustomerPrice ? 'text-blue-600' : 'text-slate-500'}`}>{fmtEur(eff)}</span>
                                  </button>
                                );
                              })
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Nachtragsangebot: Position aus dem Projekt-LV in diesen Abschnitt. */}
                    {supplementRef && (
                      <Button variant="ghost" onClick={() => setPositionPicker({ sectionId: section.id })} icon={<FolderOpen className="w-3.5 h-3.5" />} className="h-8 text-xs">
                        Aus Projekt
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {(draft.items || []).length === 0 && (
          <div className="py-10 text-center text-slate-400 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-100">
            <Package className="w-8 h-8 opacity-20 mx-auto mb-2" />
            <p className="text-xs font-medium">Noch keine Positionen – über eine Sektion hinzufügen.</p>
          </div>
        )}
      </div>

      {/* Texte & Zahlungsbedingungen */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8 space-y-6">
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2 ml-1">
            <div className="flex items-center gap-1"><label className={labelClass}>Einleitungstext</label>{expandBtn('Einleitungstext', 'introText', 'quote')}</div>
            {!locked && (
              <TextTemplatePicker
                category="intro"
                templates={templatesByCat('intro')}
                currentText={draft.introText || ''}
                onInsert={(c) => set('introText', c)}
                onCreated={handleTemplateCreated}
                onDeleted={handleTemplateDeleted}
              />
            )}
          </div>
          <textarea value={draft.introText || ''} onChange={(e) => set('introText', e.target.value)} disabled={locked} rows={3} className={`${inputClass} resize-none`} placeholder="Einleitender Text zum Angebot…" />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2 ml-1">
            <div className="flex items-center gap-1"><label className={labelClass}>Schlusstext</label>{expandBtn('Schlusstext', 'outroText', 'quote')}</div>
            {!locked && (
              <TextTemplatePicker
                category="outro"
                templates={templatesByCat('outro')}
                currentText={draft.outroText || ''}
                onInsert={(c) => set('outroText', c)}
                onCreated={handleTemplateCreated}
                onDeleted={handleTemplateDeleted}
              />
            )}
          </div>
          <textarea value={draft.outroText || ''} onChange={(e) => set('outroText', e.target.value)} disabled={locked} rows={3} className={`${inputClass} resize-none`} placeholder="Abschließender Text…" />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2 ml-1">
            <div className="flex items-center gap-1"><label className={labelClass}>Zahlungsbedingungen</label>{expandBtn('Zahlungsbedingungen', 'paymentTerms', 'quote')}</div>
            {!locked && (
              <TextTemplatePicker
                category="payment"
                templates={templatesByCat('payment')}
                currentText={draft.paymentTerms || ''}
                onInsert={(c) => set('paymentTerms', c)}
                onCreated={handleTemplateCreated}
                onDeleted={handleTemplateDeleted}
              />
            )}
          </div>
          <textarea value={draft.paymentTerms || ''} onChange={(e) => set('paymentTerms', e.target.value)} disabled={locked} rows={2} className={`${inputClass} resize-none`} placeholder="z. B. Zahlbar innerhalb 14 Tagen netto…" />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2 ml-1">
            <div className="flex items-center gap-1"><label className={labelClass}>AGB</label>{expandBtn('AGB', 'agbText', 'detail')}</div>
            {!locked && (
              <TextTemplatePicker
                category="agb"
                templates={templatesByCat('agb')}
                currentText={draft.details?.agbText || ''}
                onInsert={(c) => setDetail('agbText', c)}
                onCreated={handleTemplateCreated}
                onDeleted={handleTemplateDeleted}
              />
            )}
          </div>
          <textarea value={draft.details?.agbText || ''} onChange={(e) => setDetail('agbText', e.target.value)} disabled={locked} rows={4} className={`${inputClass} resize-none`} placeholder="Allgemeine Geschäftsbedingungen…" />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2 ml-1">
            <div className="flex items-center gap-1"><label className={labelClass}>Datenschutzinformation</label>{expandBtn('Datenschutzinformation', 'privacyText', 'detail')}</div>
            {!locked && (
              <TextTemplatePicker
                category="privacy"
                templates={templatesByCat('privacy')}
                currentText={draft.details?.privacyText || ''}
                onInsert={(c) => setDetail('privacyText', c)}
                onCreated={handleTemplateCreated}
                onDeleted={handleTemplateDeleted}
              />
            )}
          </div>
          <textarea value={draft.details?.privacyText || ''} onChange={(e) => setDetail('privacyText', e.target.value)} disabled={locked} rows={4} className={`${inputClass} resize-none`} placeholder="Datenschutzinformation (eigene Seite im PDF)…" />
        </div>
      </div>

      {/* Unterschrift — persönlich, wird nur im eigenen Profil gespeichert */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-sm font-bold text-slate-700">Unterschrift</div>
            <p className="text-[11px] text-slate-400">Erscheint im PDF über deinem Namen. Persönliche Vorlage — nur in deinem Profil gespeichert.</p>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 px-3 py-2 bg-slate-50 hover:bg-blue-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-600 hover:text-blue-600 cursor-pointer transition-all">
              <Upload className="w-3.5 h-3.5" />
              {signature ? 'Ersetzen' : 'PNG/JPG hochladen'}
              <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={handleSignatureFile} />
            </label>
            <button onClick={() => void startQrSignature()} className="flex items-center gap-1.5 px-3 py-2 bg-slate-50 hover:bg-blue-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-600 hover:text-blue-600 transition-all" title="Auf dem Smartphone unterschreiben">
              <QrCode className="w-3.5 h-3.5" />
              Per Smartphone
            </button>
            {signature && (
              <button onClick={() => void saveSignature(undefined)} className="px-3 py-2 text-xs font-medium text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all">
                Entfernen
              </button>
            )}
          </div>
        </div>
        {sigSaving ? (
          <div className="text-xs text-slate-400">Speichert…</div>
        ) : signature ? (
          <img src={signature} alt="Unterschrift" className="h-14 object-contain" />
        ) : (
          <div className="text-xs text-slate-400">Keine Unterschrift hinterlegt — PDF zeigt nur den Namen.</div>
        )}
      </div>

      {/* Summen (Kalkulationsblatt: je Sektion + ohne/inkl. NEP) */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8">
        <div className="max-w-md ml-auto space-y-4 text-sm">
          {totalsMode === 'unitPrices' && (
            <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Das PDF zeigt nur Einheitspreise, keine Summen. Die Werte hier (Menge × Preis) dienen nur der internen Auswertung.
            </p>
          )}
          {/* Zwischensummen je Sektion */}
          {detailed.perSection.length > 0 && (
            <div className="space-y-2">
              <div className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Zwischensummen je Sektion{perMonthSuffix}</div>
              {detailed.perSection.map((p) => {
                const sec = sections.find((s) => s.id === p.sectionId);
                return (
                  <div key={p.sectionId ?? '__none__'} className="flex justify-between text-slate-600 gap-4">
                    <span className="truncate">{sec ? sec.title : 'Ohne Zuordnung'}</span>
                    <span className="font-medium shrink-0 tabular-nums">{fmtEur(p.subtotal)}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Endsumme ohne NEP – maßgeblich */}
          <div className="space-y-2.5 pt-3 border-t border-slate-100">
            <div className="flex justify-between text-slate-600">
              <span>Zwischensumme</span>
              <span className="font-medium tabular-nums">{fmtEur(detailed.totalsExclNep.positionsTotal)}</span>
            </div>
            <div className="flex justify-between text-slate-600">
              <span>Rabatt ({draft.discountPercent || 0} %)</span>
              <span className="font-medium text-red-500 tabular-nums">− {fmtEur(detailed.totalsExclNep.discountAmount)}</span>
            </div>
            <div className="flex justify-between text-slate-800 font-bold pt-2 border-t border-slate-100">
              <span>{totalsMode === 'monthly' ? 'Summe netto je Monat (ohne NEP)' : 'Gesamtsumme netto (ohne NEP)'}</span>
              <span className="tabular-nums">{fmtEur(detailed.totalsExclNep.netTotal)}</span>
            </div>
            <div className="flex justify-between text-slate-600">
              <span>USt ({draft.taxRate || 0} %)</span>
              <span className="font-medium tabular-nums">{fmtEur(detailed.totalsExclNep.taxAmount)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold text-slate-900 pt-2 border-t-2 border-slate-200">
              <span>Brutto{perMonthSuffix}</span>
              <span className="tabular-nums">{fmtEur(detailed.totalsExclNep.grossTotal)}</span>
            </div>
          </div>

          {/* Nachrichtlich: inkl. NEP */}
          <div className="space-y-1.5 pt-3 border-t border-dashed border-slate-200 text-xs text-slate-500">
            <div className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Nachrichtlich – inkl. NEP</div>
            <div className="flex justify-between">
              <span>Netto (inkl. NEP)</span>
              <span className="font-medium tabular-nums">{fmtEur(detailed.totalsInclNep.netTotal)}</span>
            </div>
            <div className="flex justify-between">
              <span>Brutto (inkl. NEP)</span>
              <span className="font-medium tabular-nums">{fmtEur(detailed.totalsInclNep.grossTotal)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Fusszeile der Maske. Die Endsummen liegen rund 470 JSX-Zeilen hinter
          dem Positionsanfang — die Preiswirkung einer Eingabe war ohne Scrollen
          unsichtbar. Hier steht sie, zusammen mit dem, was noch fehlt. */}
      <div data-glass="fusszeile" className="sticky bottom-0 -mx-1 mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-slate-200 bg-white/95 px-4 py-2.5 shadow-lg backdrop-blur">
        <span className="text-sm font-bold tabular-nums text-slate-800">{fmtEur(detailed.totalsExclNep.netTotal)}</span>
        <span className="text-xs text-slate-400">netto{perMonthSuffix}{totalsMode === 'unitPrices' ? ' · intern, PDF nur Einheitspreise' : ''}</span>
        <span className="text-xs text-slate-500 tabular-nums">{fmtEur(detailed.totalsExclNep.grossTotal)} brutto</span>
        <span className="h-4 w-px bg-slate-200" />
        <span className="text-xs text-slate-500">{completeness.active} Position{completeness.active === 1 ? '' : 'en'}</span>
        {completeness.nep > 0 && <span className="text-xs text-slate-500">{completeness.nep} Bedarfsposition{completeness.nep === 1 ? '' : 'en'}</span>}
        {completeness.excluded > 0 && <span className="text-xs text-slate-500">{completeness.excluded}× {QUOTE_EXCLUDED_LABEL}</span>}
        {/* Abgedockt fehlt die Spalte — der Weg zurueck muss sichtbar
            bleiben, auch wenn das Vorschaufenster hinter dem Browser
            liegt. */}
        {previewDetached && !previewPanelVisible && (
          <button
            type="button"
            onClick={attachPreview}
            className="ml-auto flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600 transition-all hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
            title="Vorschaufenster schließen und die Vorschau wieder neben die Maske stellen"
          >
            <PictureInPicture2 className="h-3.5 w-3.5" />
            Vorschau andocken
          </button>
        )}
        {readiness.length > 0 && !locked && (
          <span className={`flex items-center gap-1.5 text-xs font-semibold text-amber-700 ${previewDetached && !previewPanelVisible ? '' : 'ml-auto'}`}>
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
            {readiness.join(' ')}
          </span>
        )}
      </div>
    </div>

      {/* Live-PDF-Vorschau: zweite Spalte der Maske, immer sichtbar. Sie zeigt
          denselben Generator-Lauf wie der Versand — was hier steht, bekommt der
          Kunde. */}
      {/* FESTE Hoehe, nicht nur eine Maximalhoehe: `lg:self-start` macht die
          Karte so hoch wie ihr Inhalt, und ein `max-h` ist keine Bezugsgroesse.
          Das iframe darin (`flex-1`) fiel dadurch auf die Inhaltshoehe zusammen
          — rund 190 px, in denen sich kein Angebot lesen laesst. */}
      {previewPanelVisible && (
      <div data-glass="vorschau" className="rounded-lg border border-slate-200 bg-slate-100 @min-[1400px]:sticky @min-[1400px]:top-0 @min-[1400px]:self-start @min-[1400px]:flex @min-[1400px]:h-[calc(100vh-11rem)] @min-[1400px]:min-h-[32rem] @min-[1400px]:flex-col @min-[1400px]:overflow-hidden">
        <div className="flex items-center gap-2 rounded-t-lg border-b border-slate-200 bg-white px-4 py-3 shrink-0">
          <Eye className="w-4 h-4 text-blue-500" />
          <span className="text-sm font-bold text-slate-700">Angebotsvorschau</span>
          {/* Wirkung der Eingabe, ohne zu den Summen scrollen zu muessen. */}
          <span className="ml-auto text-xs font-semibold tabular-nums text-slate-600" title="Netto ohne Bedarfspositionen">
            {fmtEur(detailed.totalsExclNep.netTotal)} netto{perMonthSuffix}
          </span>
          {/* Abdocken: eigenes Fenster fuer den zweiten Monitor. Die
              Maske bekommt dadurch die ganze Breite. */}
          {previewDetached ? (
            <button
              type="button"
              onClick={attachPreview}
              className="shrink-0 rounded-lg p-1.5 text-slate-400 transition-all hover:bg-blue-50 hover:text-blue-600"
              title="Andocken — Vorschaufenster schließen und wieder neben der Maske anzeigen"
              aria-label="Vorschau wieder andocken"
            >
              <PictureInPicture2 className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={detachPreview}
              className="shrink-0 rounded-lg p-1.5 text-slate-400 transition-all hover:bg-blue-50 hover:text-blue-600"
              title={nativ
                ? 'Als PDF öffnen — das ganze Angebot in einer PDF-App ansehen, ablegen oder versenden.'
                : 'Abdocken — Vorschau in ein eigenes Fenster, z. B. auf den zweiten Monitor. Die Maske bekommt dann die ganze Breite.'}
              aria-label={nativ ? 'Als PDF öffnen' : 'Vorschau in eigenem Fenster öffnen'}
            >
              <ExternalLink className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setPreviewFullscreen(true)}
            disabled={!previewUri}
            className="shrink-0 rounded-lg p-1.5 text-slate-400 transition-all hover:bg-blue-50 hover:text-blue-600 disabled:opacity-30 disabled:hover:bg-transparent"
            title="Vollbild — das ganze Angebot am Stück lesen"
            aria-label="Vorschau im Vollbild öffnen"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>
        {/* Ohne diese Zeile sieht ein geblockter Popup aus wie ein toter
            Knopf. */}
        {detachBlocked && (
          <div className="flex shrink-0 items-start gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-[11px] text-amber-800">
            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
            <span>Der Browser hat das Vorschaufenster blockiert. Popups für diese Seite erlauben, dann erneut abdocken.</span>
          </div>
        )}
        {previewError ? (
          <div className="flex min-h-[400px] flex-1 flex-col items-center justify-center gap-2 overflow-y-auto p-6 text-center text-amber-700">
            <AlertTriangle className="w-6 h-6" />
            <p className="text-sm font-medium">Vorschau nicht verfügbar</p>
            <p className="text-xs text-slate-500">{previewError}</p>
            {/* Direkt-Behebung fuers Bepreisen: Nummern aus dem Original neu
                zuordnen oder mit einem Klick auf "unveraendert anhaengen"
                ausweichen. Nur im Entwurf — gesperrte Angebote sind read-only. */}
            {lvFix && !locked && (
              <div className="mt-3 w-full max-w-md space-y-3 rounded-xl border border-amber-200 bg-white p-4 text-left">
                {lvFix.items.length > 0 && lvFix.candidates.length > 0 && (
                  <>
                    <p className="text-xs font-bold text-slate-700">
                      Direkt beheben: passende Nummer aus dem Original-LV zuordnen
                    </p>
                    {lvFix.items.map((u) => (
                      <div key={u.itemId} className="space-y-1">
                        <p className="truncate text-xs text-slate-600" title={u.title}>
                          {(u.lvPosition ? `${u.lvPosition} – ` : '') + (u.title || 'Position ohne Bezeichnung')}
                        </p>
                        <select
                          value={lvFixSel[u.itemId] || ''}
                          onChange={(e) => setLvFixSel((s) => ({ ...s, [u.itemId]: e.target.value }))}
                          className={inputClass}
                        >
                          <option value="">Nummer im Original wählen…</option>
                          {lvFixCandidatesFor(u.itemId).map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                    <Button
                      onClick={applyLvFix}
                      disabled={!Object.values(lvFixSel).some(Boolean)}
                      icon={<Check className="w-4 h-4" />}
                      className="h-9 w-full text-xs"
                    >
                      Zuordnung übernehmen
                    </Button>
                  </>
                )}
                <Button
                  variant="secondary"
                  onClick={() => setDetail('lvOriginalMode', 'append')}
                  className="h-9 w-full text-xs"
                >
                  Stattdessen Original unverändert anhängen
                </Button>
                {/* Ohne diesen Ausweg blieb ein dupliziertes Angebot ohne
                    hinterlegtes Original dauerhaft blockiert: 'append'
                    scheitert dort genauso wie 'priced'. */}
                <Button
                  variant="ghost"
                  onClick={() => setDetail('lvOriginalMode', 'none')}
                  className="h-9 w-full text-xs"
                >
                  Original gar nicht einbinden
                </Button>
              </div>
            )}
          </div>
        ) : previewUri && nativ ? (
          <div className="flex h-full min-h-[600px] w-full flex-col lg:min-h-[24rem] lg:flex-1">
            {nativVorschau(false)}
          </div>
        ) : previewUri ? (
          <iframe title="Angebotsvorschau" className="h-full min-h-[600px] w-full lg:min-h-[24rem] lg:flex-1" src={previewUri} />
        ) : (
          <div className="flex min-h-[400px] flex-1 items-center justify-center text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        )}
      </div>
      )}
    </div>
    </div>
    </fieldset>

      {/* Vollbild-Vorschau. Eigene Ebene ueber der Maske, damit das Angebot
          am Stueck lesbar ist. Dasselbe previewUri wie in der Spalte — es wird
          nichts neu erzeugt, der Sprung ist verzoegerungsfrei. */}
      {previewFullscreen && previewUri && (
        <div className="fixed inset-0 z-[100] flex flex-col bg-slate-800/95 animate-fade-in backdrop-blur-sm">
          <div className="flex items-center gap-3 border-b border-slate-700 bg-slate-900 px-4 py-2.5 shrink-0">
            <Eye className="w-4 h-4 shrink-0 text-blue-400" />
            <span className="text-sm font-bold text-white">Angebotsvorschau</span>
            <span className="truncate text-xs text-slate-400">{draft.quoteNumber} · {customerForPdf.name}</span>
            <span className="ml-auto shrink-0 text-xs font-semibold tabular-nums text-slate-200" title="Netto ohne Bedarfspositionen">
              {fmtEur(detailed.totalsExclNep.netTotal)} netto{perMonthSuffix}
            </span>
            <button
              type="button"
              onClick={() => setPreviewFullscreen(false)}
              className="shrink-0 rounded-lg p-1.5 text-slate-400 transition-all hover:bg-slate-700 hover:text-white"
              title="Vollbild schließen (Esc)"
              aria-label="Vollbild schließen"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {nativ ? (
            <div className="flex min-h-0 w-full flex-1 flex-col bg-white">
              {nativVorschau(true)}
            </div>
          ) : (
          <iframe title="Angebotsvorschau (Vollbild)" className="min-h-0 flex-1 w-full bg-white" src={previewUri} />
          )}
        </div>
      )}

    {/* ── Modale Ebenen ─────────────────────────────────────────────────
        Bewusst AUSSERHALB der Maskenspalte und ausserhalb des Grids.

        Die linke Spalte traegt `animate-fade-in`. Eine laufende (und per
        `fill-mode: both` weiterwirkende) Opacity-Animation macht das
        Element zu einem eigenen Stacking-Context. Ein Modal darin ist mit
        seinem z-index in diesem Kontext GEFANGEN: die klebende Vorschau
        steht im DOM danach und liegt damit ueber dem Modal, obwohl das
        z-[130] und die Vorschau nur z-auto hat. Sichtbar wurde das beim
        Bearbeiten der Kundenanschrift — die Vorschau ueberdeckte den
        halben Dialog.

        Ein `position: fixed`-Element gehoert ohnehin nicht in eine
        scrollende Spalte. Hier oben gilt sein z-index gegen alles. */}
      {/* QR-Modal: Unterschrift per Smartphone */}
      {qr && (
        <div className="fixed inset-0 z-[100] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4" onClick={() => void cancelQrSignature()}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 flex flex-col items-center gap-4 text-center" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-slate-700">Mit dem Smartphone unterschreiben</h3>
            <img src={qr.qrDataUrl} alt="QR-Code" className="w-56 h-56" />
            <p className="text-xs text-slate-500">QR-Code scannen, auf dem Handy unterschreiben — die Unterschrift erscheint hier automatisch. Link ist 10 Minuten gültig.</p>
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Warte auf Unterschrift…
            </div>
            <Button variant="secondary" onClick={() => void cancelQrSignature()} className="h-9 text-xs">Abbrechen</Button>
          </div>
        </div>
      )}

      {/* Expand-Modal: grosses Bearbeitungsfeld, liest/schreibt direkt den Draft */}
      {expand && (
        <div className="fixed inset-0 z-[100] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setExpand(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl p-6 flex flex-col gap-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-700">{expand.label}</h3>
              <button onClick={() => setExpand(null)} className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all" title="Schließen">
                <X className="w-4 h-4" />
              </button>
            </div>
            {expand.kind === 'item' ? (() => {
              const it = draft.items?.find((i) => i.id === expand.key);
              if (!it) return null;
              return (
                <>
                  {/* key = Position: der Editor ist unkontrolliert und liest
                      seinen Startinhalt nur beim Einhaengen. */}
                  <QuoteRichTextEditor
                    key={it.id}
                    initial={effectiveQuoteRich(it) ?? quoteRichFromPlain(it.title)}
                    onChange={(blocks) => updateItemRich(it.id, blocks)}
                    // Auch LV-Positionen (it.locked) sind hier bearbeitbar —
                    // Freigabe des Betreibers 07.10.2026, siehe Inline-Feld.
                    disabled={locked}
                    autoFocus
                  />
                  <p className="text-[11px] text-slate-400">
                    So wie hier formatiert steht die Beschreibung im Angebots-PDF. Ohne eigene Formatierung bleibt sie wie bisher fett.
                  </p>
                </>
              );
            })() : (
              <textarea
                autoFocus
                value={
                  expand.kind === 'quote'
                    ? String((draft as any)[expand.key] || '')
                    : String((draft.details as any)?.[expand.key] || '')
                }
                onChange={(e) =>
                  expand.kind === 'quote'
                    ? set(expand.key as keyof Quote, e.target.value as never)
                    : setDetail(expand.key as keyof NonNullable<Quote['details']>, e.target.value as never)
                }
                disabled={locked}
                rows={20}
                className={`${inputClass} resize-none`}
              />
            )}
            <div className="flex justify-end">
              <Button variant="secondary" onClick={() => setExpand(null)} className="h-9 text-xs">Fertig</Button>
            </div>
          </div>
        </div>
      )}

      {showSend && (
        <QuoteSendModal
          quote={draft}
          // Der Kunde MIT der Abweichung dieses Angebots: PDF, Anrede,
          // Empfaengervorschlag und der beim Versand eingefrorene Snapshot
          // muessen dasselbe sagen wie die Vorschau daneben. Bewusst NICHT
          // customerForPdf — der waere bei einem erneut geoeffneten,
          // versendeten Angebot der alte Snapshot und schriebe ihn beim
          // Markieren unveraendert zurueck.
          customer={customerWithOverride}
          quoteConfig={quoteConfig}
          invoiceConfig={invoiceConfig}
          company={company || {}}
          userName={pdfUserName}
          userSignature={signature}
          onClose={() => setShowSend(false)}
          onMarkedSent={handleMarkedSent}
        />
      )}

      {tenderKind && (
        <TenderImportModal
          quoteId={draft.id}
          kind={tenderKind}
          // Gestrichene Positionen zaehlen weiter als vorhanden — sie stehen
          // deshalb sowohl in existingKeys (Duplikat) als auch in excludedKeys.
          existingKeys={new Set((draft.items || []).map(quoteItemLvKey))}
          excludedKeys={excludedLvKeys}
          onClose={() => setTenderKind(null)}
          onApplyLv={applyTenderLv}
          onApplyRequirements={applyTenderRequirements}
        />
      )}

      {showAcceptDialog && userProfile?.id && (
        <AcceptQuoteDialog
          quote={draft}
          customer={customer}
          busy={accepting}
          error={acceptError}
          // Waehrend der Annahme laeuft, bleibt der Dialog stehen (X ist
          // gesperrt): sonst waere die Maske frei, und das Ergebnis
          // ueberschriebe, was inzwischen getippt wurde.
          onCancel={() => { if (!accepting) setShowAcceptDialog(false); }}
          onConfirm={handleAcceptConfirmed}
          onConfirmFramework={handleAcceptFrameworkConfirmed}
          onConfirmSupplement={(v) => { void handleAcceptSupplementConfirmed(v); }}
          projectTypes={annahmeProjektTypen}
          // Fehler des einen Wegs gehoert nicht unter das Formular des anderen.
          onModeChange={() => setAcceptError('')}
        />
      )}

      {trancheDialog && (
        <QuoteTrancheDialog
          quote={draft}
          mode={trancheDialog.mode}
          tranche={trancheDialog.trancheId ? (draft.orderTranches || []).find((t) => t.id === trancheDialog.trancheId) : undefined}
          busy={trancheBusy}
          error={trancheError}
          onCancel={() => { if (!trancheBusy) setTrancheDialog(null); }}
          onConfirm={(r) => { void handleTrancheConfirm(r); }}
        />
      )}

      {positionPicker && supplementRef && !locked && (
        <ProjectPositionPicker
          projectId={supplementRef.projectId}
          quote={draft}
          sections={sections}
          initialSectionId={positionPicker.sectionId}
          onCancel={() => setPositionPicker(null)}
          onAdd={addProjectItems}
        />
      )}

      {/* Kundendaten ohne Umweg ueber die Kundenverwaltung — mit der Wahl,
          wie weit die Aenderung reicht.

          GLOBAL geht das Ergebnis an die Angebotsliste (customers), NICHT an
          das Angebot: der Entwurf liest den Kunden live, und ihn anzufassen
          setzte den gerade getippten Stand zurueck.

          ANGEBOTSBEZOGEN landet nur der Diff in details.customerOverride —
          ueber setDetail, also denselben Weg wie jedes andere Feld. */}
      {customerEditOpen && customer.id && (
        <CustomerQuickEditModal
          customerId={customer.id}
          fallback={customer}
          kind="quote"
          state={customerEditState}
          override={customerOverride}
          stammAenderbar={kundenBearbeitung.stamm}
          onSaved={(updated) => onCustomerChange?.(updated)}
          onSavedForDocument={(next) => setDetail('customerOverride', next)}
          onClose={() => setCustomerEditOpen(false)}
        />
      )}

    </div>
  );
};
